import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AircraftManager } from './aircraft.js';
import { BasemapSelector } from './basemaps.js';
import { LeftPanelManager } from './leftPanel.js';
import { MapManager } from './mapManager.js';
import { ReplaySetup } from './replay/ReplaySetup.js';
import { ReplayControls } from './replay/ReplayControls.js';
import { ReplayManager } from './replay/ReplayManager.js';
import { ReplayLoadingModal } from './replay/ReplayLoadingModal.js';
import { TaskManager } from './taskManager.js';
import { AircraftStateTracker } from './aircraftStateTracker.js';
import { PolarManager } from './polarManager.js';
import { calculateTEC, EnergyCalculator } from './energyCalculator.js';
import { calculateDistanceForTime, calculateETA, formatTimeMMSS } from './etaCalculator.js';
import { xcSpeedCalculator } from './xcSpeedCalculator.js';
import { getDefaultWingLoading, getHandicap } from './handicapData.js';
import { cookieRegistry } from './cookieRegistry.js';
import { normalizePosition } from './positionNormalizer.js';
import { positionNoiseFilter } from './positionNoiseFilter.js';
import { haversineDistance } from './distanceCalculator.js';
import {
  DEFAULT_TREND_COLOR_SCHEME,
  resolveTrendColorSchemeId
} from './trendColorScheme.js';

// Initialize polar manager and energy calculator
// Polars are loaded on-demand from binary .pol files
const polarManager = new PolarManager();
const energyCalc = new EnergyCalculator();

// Configuration
// Toggle between production and local development
const USE_PRODUCTION = true; // Change to true for production backend
const USE_LOCAL_PROXY = true; // Use Vite proxy to bypass CORS when developing locally

// Cesium Ion Token
const CESIUM_ION_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJhZTVlNDY2My0zMTE2LTQ3OTQtOGEzOS1jNjYxMTQ4NmIyNmQiLCJpZCI6Mjk0ODUyLCJpYXQiOjE3NjAxNjA2MjF9.gRAC6q_u5k1ZB2kaQ6K03DPJ2ydV8BHZkwDyCyaTngc';
// For localhost use your local Cesium Ion token dummy!

const BACKEND_CONFIG = {
  production: {
    ws: 'wss://server.condormap.com',
    http: 'https://server.condormap.com'
  },
  localProxy: {
    // Use Vite proxy - appears as map.condormap.com to server
    ws: `ws://${window.location.host}/ws-proxy`,
    http: `http://${window.location.host}` // Proxy handles /api and /health paths
  },
  local: {
    ws: 'ws://localhost:3001',
    http: 'http://localhost:3000'
  }
};

// Select config based on flags
let selectedConfig;
if (USE_LOCAL_PROXY && window.location.hostname === 'localhost') {
  selectedConfig = BACKEND_CONFIG.localProxy;
} else if (USE_PRODUCTION) {
  selectedConfig = BACKEND_CONFIG.production;
} else {
  selectedConfig = BACKEND_CONFIG.local;
}

const WS_URL = selectedConfig.ws;
const HTTP_URL = selectedConfig.http;
const RECONNECT_INTERVAL = 3000;
const AIRCRAFT_TIMEOUT = 10000; // 10 seconds

// State
let ws = null;
let reconnectTimer = null;
let shouldAutoReconnect = true; // Flag to control auto-reconnection
let aircraftManager = null;
let basemapSelector = null;
let mapManager = null; // NEW: Manages 2D/3D switching
let hasSnappedToGliders = false; // Track if we've already snapped once
let activeServers = new Map(); // Track servers and their position counts: Map<serverKey, {name, port, count}>
let selectedServers = new Set(['all']); // Track selected servers for filtering (default: 'all')
let serverAircraft = new Map(); // Track which aircraft belong to which server: Map<serverKey, Set<cookie>>
let aircraftTimestamps = new Map(); // Track last update time for each aircraft: Map<cookie, timestamp>
let latestLivePositions = new Map(); // Map<cookie, {lat, lon, serverName, portNumber, timestamp}>
let timeoutCheckInterval = null; // Interval for checking aircraft timeouts
let etfLabelShowUntilMs = Date.now() + 10000; // Control ETF title visibility
setTimeout(() => {
  etfLabelShowUntilMs = Date.now() + 10000;
  window.etfLabelShowUntilMs = etfLabelShowUntilMs;
}, 0);
window.etfLabelShowUntilMs = etfLabelShowUntilMs;

// Task state
let selectedTaskFilter = 'liveNow'; // 'liveNow' or 'byServer'
let activeTasks = []; // Array of fetched tasks
let selectedTask = null; // Currently selected task ID
let replaySelectedTask = null; // Persist replay/IGC task so fetchTasks() refreshes don't drop it
let taskOverlayLayers = []; // Array of Leaflet layers for the current task overlay
let taskManager = new TaskManager(); // Task distance calculator
let aircraftStateTracker = new AircraftStateTracker(); // Aircraft state tracker for race timing

// Phase 4: Polar and Energy state
const globalPolarManager = polarManager; // Use the test polar manager
const globalEnergyCalc = energyCalc; // Use the test energy calculator

// ETF delta tracking (stores recent ETF predictions and trend deltas per aircraft)
const etfHistory = new Map(); // cookie -> trend samples keyed by sim time
const etfDeltaByAircraft = new Map(); // cookie -> deltaSeconds
const etfDeltaByAircraftB = new Map(); // cookie -> deltaSeconds (window B)
// Sampled XC Pace: captures etaDelta at configurable sim-time intervals (game-time driven)
// Map<cookie, { value: number|null, lastCaptureSimTimeSec: number }>
const sampledEtfDeltasA = new Map();
const sampledEtfDeltasB = new Map();
const etfAltSourceByAircraft = new Map(); // cookie -> 'tec' | 'alt' — tracks altitude source to detect transitions
const etfStartActivationByAircraft = new Map(); // cookie -> startTimeMs for the currently active race run
const ETF_HISTORY_RETENTION_SEC = 6 * 3600; // keep up to 6 hours (full race/server session)
const ETF_BASELINE_MAX_DRIFT_SEC = 5; // baseline sample must be close to requested look-back target
let lastEtfComputationSignature = null;

const OVERLAY_COLUMN_OPTIONS = [
  { key: 'rank', label: 'Rank (#)' },
  { key: 'cn', label: 'CN' },
  { key: 'glider', label: 'Glider' },
  { key: 'pilot_name', label: 'Pilot Name' },
  { key: 'etf_delta', label: 'ETA gap to leader' },
  { key: 'xc_perf_a', label: 'XC Pace A' },
  { key: 'xc_perf_b', label: 'XC Pace B' },
  { key: 'positions_delta', label: 'Rank change' },
  { key: 'off_km', label: 'Official km' },
  { key: 'real_km', label: 'Real km' },
  { key: 'xc_speed', label: 'XC speed' },
  { key: 'etf_total', label: 'ETA total' },
  { key: 'etf_cruise', label: 'ETA cruise' },
  { key: 'etf_climb', label: 'ETA climb' },
  { key: 'ddh', label: 'Finish Alt Δ (DDH)' },
  { key: 'fg_speed', label: 'FG speed' }
];

const OVERLAY_COLUMN_KEYS = new Set(OVERLAY_COLUMN_OPTIONS.map(col => col.key));
const OVERLAY_DEFAULT_PERMANENT_COLUMNS = ['rank', 'cn', 'etf_delta'];
const OVERLAY_DEFAULT_ROTATING_COLUMNS = ['xc_perf_a'];
const LINKABLE_COLOR_SCHEMES = new Set([
  'aircraft',
  'altitude',
  'speed',
  'vario',
  'vario_avg',
  'vario_kalman',
  'vario_te',
  'etf_delta',
  'etf_delta_b'
]);

// Track hidden aircraft cookies for leaderboard visibility toggle
const hiddenCookies = new Set();

// Load saved settings from localStorage
function loadSettings() {
  const saved = localStorage.getItem('condormap-settings');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse saved settings:', e);
    }
  }
  return null;
}

function sanitizeOverlayColumnListAllowEmpty(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set();
  const cleaned = [];
  raw.forEach((value) => {
    const key = String(value || '').trim();
    if (!OVERLAY_COLUMN_KEYS.has(key) || seen.has(key)) {
      return;
    }
    seen.add(key);
    cleaned.push(key);
  });
  return cleaned;
}

function resolveSelectedTaskObject() {
  if ((currentMode === 'replay' || currentMode === 'igc') &&
    replaySelectedTask &&
    Array.isArray(replaySelectedTask.turnpoints) &&
    replaySelectedTask.turnpoints.length > 0) {
    return replaySelectedTask;
  }

  if (selectedTask === null || selectedTask === undefined) {
    return null;
  }

  return activeTasks.find((t) => String(t?.id) === String(selectedTask)) || null;
}

function parseBooleanSetting(value, fallback = false) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
}

// Debounced localStorage write for leaderboard to reduce I/O frequency
let pendingLeaderboardData = null;
let leaderboardWriteTimer = null;
const LEADERBOARD_WRITE_DEBOUNCE_MS = 500; // Write at most once per 500ms

function scheduleLeaderboardWrite(data) {
  pendingLeaderboardData = data;

  if (leaderboardWriteTimer === null) {
    leaderboardWriteTimer = setTimeout(() => {
      if (pendingLeaderboardData !== null) {
        try {
          localStorage.setItem('condormap-leaderboard', JSON.stringify(pendingLeaderboardData));
        } catch (e) {
          console.warn('[Leaderboard overlay] localStorage write failed:', e);
        }
        pendingLeaderboardData = null;
      }
      leaderboardWriteTimer = null;
    }, LEADERBOARD_WRITE_DEBOUNCE_MS);
  }
}

// Save settings to localStorage
function saveSettings() {
  const settings = {
    trailLength: config.trailLength,
    infoLabel: config.infoLabel,
    colors: config.colors,
    colorSensitivity: config.colorSensitivity,
    units: config.units,
    trendWindowSec: config.trendWindowSec,
    teGain: config.teGain,
    teAccelLimit: config.teAccelLimit,
    cnLabelSize: config.cnLabelSize,
    maccready: config.maccready,
    netto: config.netto,
    wingLoading: config.wingLoading,
    startTimeOverrideLocal: config.startTimeOverrideLocal,
    startTimeOverrideIso: config.startTimeOverrideIso,
    replayRefreshHz: config.replayRefreshHz,
    trendWindowBSec: config.trendWindowBSec,
    overlayXcPerfAUpdateSec: config.overlayXcPerfAUpdateSec,
    overlayXcPerfBUpdateSec: config.overlayXcPerfBUpdateSec,
    overlayPositionWindowSec: config.overlayPositionWindowSec,
    overlayPositionRefreshSec: config.overlayPositionRefreshSec,
    overlayPermanentColumns: config.overlayPermanentColumns,
    overlayRotatingColumns: config.overlayRotatingColumns,
    overlayRotationSec: config.overlayRotationSec,
    trendColorScheme: config.trendColorScheme,
    basemap: basemapSelector?.currentBasemap || 'Esri Imagery + Roads'
  };
  localStorage.setItem('condormap-settings', JSON.stringify(settings));
}

// Configuration state with defaults
const savedSettings = loadSettings();
const savedPermanentColumns = Array.isArray(savedSettings?.overlayPermanentColumns)
  ? sanitizeOverlayColumnListAllowEmpty(savedSettings.overlayPermanentColumns)
  : [...OVERLAY_DEFAULT_PERMANENT_COLUMNS];
const savedRotatingColumnsRaw = Array.isArray(savedSettings?.overlayRotatingColumns)
  ? sanitizeOverlayColumnListAllowEmpty(savedSettings.overlayRotatingColumns)
  : [...OVERLAY_DEFAULT_ROTATING_COLUMNS];
const savedRotatingColumns = savedRotatingColumnsRaw.filter(key => !savedPermanentColumns.includes(key));
const migratedLapModeA = parseBooleanSetting(savedSettings?.overlayXcPerfALapTimer, false);
const migratedLapModeB = parseBooleanSetting(savedSettings?.overlayXcPerfBLapTimer, false);
const resolvedOverlayXcPerfAUpdateSec = (() => {
  const raw = Number(savedSettings?.overlayXcPerfAUpdateSec);
  if (Number.isFinite(raw)) return Math.max(1, Math.min(600, Math.round(raw)));
  // Legacy migration: checked lap-timer mode mapped to window-sized updates.
  return migratedLapModeA
    ? Math.max(1, Math.min(600, Math.round(Number(savedSettings?.trendWindowSec) || 180)))
    : 1;
})();
const resolvedOverlayXcPerfBUpdateSec = (() => {
  const raw = Number(savedSettings?.overlayXcPerfBUpdateSec);
  if (Number.isFinite(raw)) return Math.max(1, Math.min(600, Math.round(raw)));
  // Legacy migration: checked lap-timer mode mapped to window-sized updates.
  return migratedLapModeB
    ? Math.max(1, Math.min(600, Math.round(Number(savedSettings?.trendWindowBSec) || 300)))
    : 1;
})();
const resolvedOverlayPositionWindowSec = (() => {
  const raw = Number(savedSettings?.overlayPositionWindowSec);
  return Number.isFinite(raw) ? Math.max(30, Math.min(3600, Math.round(raw))) : 300;
})();
const resolvedOverlayPositionRefreshSec = (() => {
  const raw = Number(savedSettings?.overlayPositionRefreshSec);
  return Number.isFinite(raw) ? Math.max(1, Math.min(600, Math.round(raw))) : 10;
})();
const config = {
  trailLength: savedSettings?.trailLength ?? 300,
  infoLabel: savedSettings?.infoLabel ?? 'etf_delta',
  colors: savedSettings?.colors ?? 'etf_delta',
  colorSensitivity: savedSettings?.colorSensitivity ?? 0, // -100 to +100, 0 = default (75% gradient)
  units: savedSettings?.units ?? 'metric',
  teGain: savedSettings?.teGain ?? 1.0,
  teAccelLimit: savedSettings?.teAccelLimit ?? 3.0,
  cnLabelSize: savedSettings?.cnLabelSize ?? 16,
  maccready: savedSettings?.maccready ?? 2.0, // Default 2.0 m/s for live races
  netto: savedSettings?.netto ?? 0,
  wingLoading: savedSettings?.wingLoading ?? 45,
  startTimeOverrideLocal: typeof savedSettings?.startTimeOverrideLocal === 'string' ? savedSettings.startTimeOverrideLocal : '',
  startTimeOverrideIso: typeof savedSettings?.startTimeOverrideIso === 'string' ? savedSettings.startTimeOverrideIso : null,
  trendWindowSec: Number(savedSettings?.trendWindowSec) || 60,
  trendWindowBSec: Number(savedSettings?.trendWindowBSec) || 300,
  replayRefreshHz: savedSettings?.replayRefreshHz ?? 1,
  overlayXcPerfAUpdateSec: resolvedOverlayXcPerfAUpdateSec,
  overlayXcPerfBUpdateSec: resolvedOverlayXcPerfBUpdateSec,
  overlayPositionWindowSec: resolvedOverlayPositionWindowSec,
  overlayPositionRefreshSec: resolvedOverlayPositionRefreshSec,
  overlayPermanentColumns: savedPermanentColumns,
  overlayRotatingColumns: savedRotatingColumns,
  overlayRotationSec: Math.max(1, Math.min(120, Number(savedSettings?.overlayRotationSec) || 10)),
  trendColorScheme: resolveTrendColorSchemeId(savedSettings?.trendColorScheme || DEFAULT_TREND_COLOR_SCHEME),
  // Task-specific wing loading (from task_additional_details.MaxWingLoadingKgM2)
  taskMaxWingLoading: null
};

// Session flag: wing loading dialog already shown this page load
let wingLoadingPromptedThisSession = false;
let autoLiveTaskSelectionPending = true;
let liveTaskRefreshTimer = null;
const LIVE_TASK_PROXIMITY_THRESHOLD_KM = 20;

// Cached MacCready speed/sink results (invalidated when config.maccready changes)
let _mcCache = { mc: null, results: new Map() }; // results: Map<cacheKey, {speedKmh, sinkMs}>

function getCachedMcSpeed(coeffs, mcSetting_ms) {
  if (_mcCache.mc !== mcSetting_ms) {
    _mcCache.mc = mcSetting_ms;
    _mcCache.results.clear();
  }
  const key = `${coeffs[0]},${coeffs[1]},${coeffs[2]}`;
  let cached = _mcCache.results.get(key);
  if (cached) return cached;

  const [a, b, c] = coeffs;
  let mcSpeedKmh = 150;
  let mcSinkMs = -2.0;
  const vne_kmh = 300;

  if (mcSetting_ms > 0 && a < 0) {
    const term = (c - mcSetting_ms) / a;
    if (term > 0) {
      const v_mc_ms = Math.sqrt(term);
      mcSpeedKmh = v_mc_ms * 3.6;
      mcSinkMs = a * v_mc_ms * v_mc_ms + b * v_mc_ms + c;
    } else {
      const vne_ms = vne_kmh / 3.6;
      mcSpeedKmh = vne_kmh;
      mcSinkMs = a * vne_ms * vne_ms + b * vne_ms + c;
    }
  } else if (a !== 0) {
    const v_min_ms = -b / (2 * a);
    mcSpeedKmh = v_min_ms * 3.6;
    mcSinkMs = a * v_min_ms * v_min_ms + b * v_min_ms + c;
  }

  if (mcSpeedKmh > vne_kmh) {
    mcSpeedKmh = vne_kmh;
    const vne_ms = vne_kmh / 3.6;
    mcSinkMs = a * vne_ms * vne_ms + b * vne_ms + c;
  }

  cached = { speedKmh: mcSpeedKmh, sinkMs: mcSinkMs };
  _mcCache.results.set(key, cached);
  return cached;
}

// MacCready automation state
let macCreadyManualOverride = false;
let macCreadyWinnerTaskId = null;
let macCreadyWinnerPromise = null;
let macCreadyLastCheckMs = 0;
let macCreadyLiveLeaderTaskId = null;
let macCreadyLiveLeaderCookie = null;
let macCreadyLiveLeaderLastCheckMs = 0;

function resolveFinishedDistances(task, cookie, finishInfo = null) {
  const state = aircraftStateTracker.getAllStates().get(cookie);
  const isAatTask = task?.task_type === 'AAT';
  const preferCachedFinish = !!finishInfo;

  const official_km =
    (preferCachedFinish && Number.isFinite(finishInfo?.officialDistance_km) ? finishInfo.officialDistance_km : null) ??
    (Number.isFinite(state?.officialDistance_km) ? state.officialDistance_km : null) ??
    (!preferCachedFinish && Number.isFinite(finishInfo?.officialDistance_km) ? finishInfo.officialDistance_km : null) ??
    (isAatTask
      ? null
      : (
        task?.scoringTaskLength_km ??
        task?.scoringTaskLength ??
        0
      ));

  const real_km =
    (preferCachedFinish && Number.isFinite(finishInfo?.realDistance_km) ? finishInfo.realDistance_km : null) ??
    (Number.isFinite(state?.realDistance_km) ? state.realDistance_km : null) ??
    (!preferCachedFinish && Number.isFinite(finishInfo?.realDistance_km) ? finishInfo.realDistance_km : null) ??
    (isAatTask
      ? official_km
      : (
        task?.netTaskLength_km ??
        task?.netTaskLength ??
        official_km
      ));

  return { official_km, real_km, state };
}

function buildFinishedEntriesFromCache(task) {
  const entries = [];
  const finishedCookies = aircraftStateTracker.getAllFinishedPilotCookies();
  finishedCookies.forEach(cookie => {
    const finishInfo = aircraftStateTracker.getFinishedPilotInfo(cookie);
    if (!finishInfo) return;

    const { official_km, real_km } = resolveFinishedDistances(task, cookie, finishInfo);
    let xcSpeedKmh = finishInfo.xcSpeedKmh ?? null;

    if (!Number.isFinite(xcSpeedKmh) && Number.isFinite(finishInfo.elapsedSeconds) && finishInfo.elapsedSeconds > 0) {
      const hours = finishInfo.elapsedSeconds / 3600;
      const distance_km = Number.isFinite(real_km) && real_km > 0
        ? real_km
        : (Number.isFinite(official_km) && official_km > 0 ? official_km : null);
      if (Number.isFinite(distance_km) && distance_km > 0 && hours > 0) {
        xcSpeedKmh = distance_km / hours;
      }
    }

    entries.push({
      cookie,
      cn: finishInfo.cn || cookie,
      gliderType: finishInfo.gliderType || null,
      status: 'finished',
      official_km,
      real_km,
      finishOrder: Number.isFinite(finishInfo.order) ? finishInfo.order : null,
      finishTimeMs: finishInfo.finishTimeMs ?? null,
      finishElapsedMinutes: Number.isFinite(finishInfo.elapsedSeconds) ? finishInfo.elapsedSeconds / 60 : null,
      startAltitude_m: finishInfo.startAltitude_m ?? null,
      finishDeltaMinutes: Number.isFinite(finishInfo.deltaMinutes) ? finishInfo.deltaMinutes : null,
      finishDeltaMinutesHandicap: Number.isFinite(finishInfo.deltaMinutesHandicap) ? finishInfo.deltaMinutesHandicap : null,
      predictedFinishMinutesHandicap: (() => {
        if (isHandicapTaskClass(task) && Number.isFinite(finishInfo.elapsedSeconds) && finishInfo.gliderType) {
          const handicap = getConfiguredHandicap(finishInfo.gliderType, task);
          if (handicap) {
            return ((finishInfo.elapsedSeconds / 60) / 100) * handicap;
          }
        }
        return null;
      })(),
      xcSpeedKmh
    });
  });
  return entries;
}


/**
 * Ensures a color has minimum brightness for readability on dark backgrounds.
 * Converts RGB to HSL, enforces minimum lightness, converts back to RGB.
 * @param {string} rgbColor - Color in "rgb(r, g, b)" format
 * @param {number} minLightness - Minimum lightness (0-100), default 45
 * @returns {string} Adjusted color in "rgb(r, g, b)" format
 */
function ensureMinBrightness(rgbColor, minLightness = 45) {
  if (!rgbColor || !rgbColor.startsWith('rgb')) return rgbColor;

  const match = rgbColor.match(/\d+/g);
  if (!match || match.length < 3) return rgbColor;

  let [r, g, b] = match.map(Number);
  r /= 255; g /= 255; b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  // Enforce minimum lightness
  const minL = minLightness / 100;
  if (l < minL) l = minL;

  // Convert back to RGB
  let r2, g2, b2;
  if (s === 0) {
    r2 = g2 = b2 = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1 / 3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1 / 3);
  }

  return `rgb(${Math.round(r2 * 255)}, ${Math.round(g2 * 255)}, ${Math.round(b2 * 255)})`;
}

/**
 * Normalize a task class name for comparisons.
 * @param {string|null|undefined} taskClass
 * @returns {string}
 */
function normalizeTaskClassName(taskClass) {
  return typeof taskClass === 'string' ? taskClass.trim().toLowerCase() : '';
}

function resolveTaskClass(task = null) {
  const resolvedTask = task || resolveSelectedTaskObject();
  return (
    resolvedTask?.gliderClass ||
    resolvedTask?.task_additional_details?.Class ||
    null
  );
}

function getConfiguredHandicap(gliderType, task = null) {
  const resolvedTask = task || resolveSelectedTaskObject();
  return getHandicap(gliderType, resolvedTask);
}

function isHandicapTaskClass(task = null) {
  const resolvedTask = task || resolveSelectedTaskObject();
  if (resolvedTask && typeof resolvedTask.handicapEnabled === 'boolean') {
    return resolvedTask.handicapEnabled;
  }

  const taskClass = normalizeTaskClassName(resolveTaskClass(task));
  return taskClass === 'club' || taskClass === 'all' || taskClass === 'school';
}

function usesPerGliderReplayWingLoading(task = null) {
  const taskClass = normalizeTaskClassName(resolveTaskClass(task));
  return taskClass === 'all' || taskClass === 'school';
}

function getTaskMaxWingLoadingKgM2(task = null) {
  const resolvedTask = task || resolveSelectedTaskObject();
  const candidates = [
    resolvedTask?.task_additional_details?.MaxWingLoadingKgM2,
    resolvedTask?.MaxWingLoadingKgM2,
    resolvedTask?.maxWingLoadingKgM2
  ];

  if (task == null) {
    candidates.push(config.taskMaxWingLoading);
  }

  for (const candidate of candidates) {
    const value = Number.parseFloat(candidate);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  return null;
}

function getReplayFlightMetadata(cookie) {
  if (!cookie || !Array.isArray(replayManager?.flights)) {
    return null;
  }

  for (const flight of replayManager.flights) {
    const flightCookie = flight?.positions?.[0]?.cookie;
    if (flightCookie === cookie) {
      return flight?.metadata || null;
    }
  }

  return null;
}

function getGliderMaxWingLoading(gliderType) {
  if (!gliderType) {
    return null;
  }

  if (!globalPolarManager.hasPolar(gliderType)) {
    globalPolarManager.loadPolarOnDemand(gliderType).catch(() => {});
    return null;
  }

  const polarData = globalPolarManager.getPolarData(gliderType);
  const candidates = [
    polarData?.maxWingLoading,
    polarData?.maxNoFixedWingLoading
  ];

  for (const candidate of candidates) {
    const value = Number.parseFloat(candidate);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  return null;
}

function clampWingLoadingToLimits(wingLoading, gliderType, task = null) {
  if (!Number.isFinite(wingLoading) || wingLoading <= 0) {
    return null;
  }

  const gliderMaxWingLoading = getGliderMaxWingLoading(gliderType);
  const taskMaxWingLoading = getTaskMaxWingLoadingKgM2(task);

  return clampWingLoadingToResolvedLimits(wingLoading, gliderMaxWingLoading, taskMaxWingLoading);
}

function clampWingLoadingToResolvedLimits(wingLoading, gliderMaxWingLoading = null, taskMaxWingLoading = null) {
  if (!Number.isFinite(wingLoading) || wingLoading <= 0) {
    return null;
  }

  let resolvedWingLoading = wingLoading;

  if (Number.isFinite(gliderMaxWingLoading)) {
    resolvedWingLoading = Math.min(resolvedWingLoading, gliderMaxWingLoading);
  }
  if (Number.isFinite(taskMaxWingLoading)) {
    resolvedWingLoading = Math.min(resolvedWingLoading, taskMaxWingLoading);
  }

  return resolvedWingLoading;
}

function getReplayMetadataWingLoading(gliderType, flightMetadata = null) {
  if (!gliderType || !flightMetadata?.parsed_from_igc) {
    return null;
  }

  if (!globalPolarManager.hasPolar(gliderType)) {
    globalPolarManager.loadPolarOnDemand(gliderType).catch(() => {});
    return null;
  }

  const polarData = globalPolarManager.getPolarData(gliderType);
  if (!polarData || !Number.isFinite(polarData.mass) || polarData.mass <= 0 || !Number.isFinite(polarData.wingArea) || polarData.wingArea <= 0) {
    return null;
  }

  const waterBallast = Number.parseFloat(
    flightMetadata?.waterBallast_liters ?? flightMetadata?.condorPlaneSettings?.waterBallast_liters
  );
  const fixedMass = Number.parseFloat(
    flightMetadata?.fixedMass_kg ?? flightMetadata?.condorPlaneSettings?.fixedMass_kg
  );
  const totalExtraMassKg =
    (Number.isFinite(waterBallast) ? waterBallast : 0)
    + (Number.isFinite(fixedMass) ? fixedMass : 0);

  return (polarData.mass + totalExtraMassKg) / polarData.wingArea;
}

/**
 * Get effective wing loading for a glider based on task class and replay metadata.
 * @param {string} gliderType - Glider type name
 * @param {object} task - Current task object (optional)
 * @param {object} options - Optional replay metadata lookup hints
 * @returns {number} Wing loading in kg/m²
 */
function getEffectiveWingLoading(gliderType, task = null, options = {}) {
  const resolvedTask = task || resolveSelectedTaskObject();
  const taskClass = normalizeTaskClassName(resolveTaskClass(resolvedTask));
  const flightMetadata = options.flightMetadata || getReplayFlightMetadata(options.cookie);
  const gliderMaxWingLoading = getGliderMaxWingLoading(gliderType);
  const taskMaxWingLoading = getTaskMaxWingLoadingKgM2(resolvedTask);
  const manualWingLoading = Number.parseFloat(config.wingLoading);

  if (taskClass === 'club') {
    const defaultWingLoading = getDefaultWingLoading(gliderType);
    if (Number.isFinite(defaultWingLoading) && defaultWingLoading > 0) {
      return clampWingLoadingToResolvedLimits(defaultWingLoading, gliderMaxWingLoading, taskMaxWingLoading) || defaultWingLoading;
    }
  }

  if (usesPerGliderReplayWingLoading(resolvedTask)) {
    const replayWingLoading = getReplayMetadataWingLoading(gliderType, flightMetadata);
    if (Number.isFinite(replayWingLoading) && replayWingLoading > 0) {
      return clampWingLoadingToResolvedLimits(replayWingLoading, gliderMaxWingLoading, taskMaxWingLoading) || replayWingLoading;
    }
  }

  return clampWingLoadingToResolvedLimits(manualWingLoading, gliderMaxWingLoading, taskMaxWingLoading) || manualWingLoading;
}

/**
 * Calculate wing loading from polar data
 * Helper function to compute wing loading from glider specs
 * @param {object} polarData - Polar data from polarManager
 * @param {number} ballastPercent - Ballast percentage (0-100)
 * @returns {number} Wing loading in kg/m²
 */
function calculateWingLoading(polarData, ballastPercent = 100) {
  if (!polarData || !polarData.mass || !polarData.wingArea) {
    return null;
  }

  const maxBallast = polarData.maxWaterBallast || 0;
  const actualBallast = maxBallast * (ballastPercent / 100);
  return (polarData.mass + actualBallast) / polarData.wingArea;
}

// Make it globally accessible for use in other parts of the code
window.calculateWingLoading = calculateWingLoading;

// Initialize map with dark theme
const map = L.map('map', {
  center: [47, 4],
  zoom: 6,
  zoomControl: false, // Disable zoom buttons
  preferCanvas: true // Use canvas for better performance
});

// Leaflet: recalc size when iOS changes the viewport
const invalidate = () => map.invalidateSize();

// iOS/Safari fires a mix of these when toolbars show/hide or rotate
['load', 'resize', 'orientationchange', 'pageshow', 'focus', 'visibilitychange']
  .forEach(evt => window.addEventListener(evt, () => setTimeout(invalidate, 80)));

// Extra-smooth on iOS 15+ when the visual viewport animates
if (window.visualViewport) {
  let t;
  window.visualViewport.addEventListener('resize', () => {
    clearTimeout(t);
    t = setTimeout(invalidate, 80);
  });
}

// Initialize aircraft manager
aircraftManager = new AircraftManager(map);

// Apply initial config
aircraftManager.setInfoLabel(config.infoLabel);
aircraftManager.setColorScheme(config.colors);
aircraftManager.setColorSensitivity(config.colorSensitivity);
aircraftManager.setTrendColorScheme(config.trendColorScheme);
aircraftManager.setUnits(config.units);
aircraftManager.setMaxTrailPoints(config.trailLength);

// Initialize basemap selector with saved basemap and save callback
const savedBasemap = savedSettings?.basemap ?? 'Esri Imagery + Roads';
basemapSelector = new BasemapSelector(map, savedBasemap, () => {
  saveSettings();
});

// Initialize map manager for 2D/3D switching
mapManager = new MapManager(map, aircraftManager, CESIUM_ION_TOKEN);
mapManager.updateConfig(config);

// Initialize left panel manager
const leftPanelManager = new LeftPanelManager();

// Set up left panel event handlers
leftPanelManager.addEventListener('navItemClicked', (event) => {
  const { itemId, item } = event.detail;

  // Handle different navigation items
  switch (itemId) {
    case 'waypoint':
      // TODO: Implement waypoint creation tool
      break;
    case 'route':
      // TODO: Implement route planning tool
      break;
  }
});

leftPanelManager.addEventListener('panelOpened', () => {
});

leftPanelManager.addEventListener('panelClosed', () => {
});

// ========================================
// REPLAY MODE SETUP
// ========================================

// Initialize replay components
const replaySetup = new ReplaySetup(HTTP_URL);
const replayControls = new ReplayControls();
const replayLoadingModal = new ReplayLoadingModal();
let replayManager = null; // Will be initialized when needed

// Replay state
let currentMode = 'live'; // 'live' or 'replay'
let replayData = null;

// Mode toggle button handlers - set up AFTER panel is initialized
setTimeout(() => {
  const liveModeBtn = document.getElementById('mode-live-btn');
  const replayModeBtn = document.getElementById('mode-replay-btn');
  const igcModeBtn = document.getElementById('mode-igc-btn');
  const igcModeBtnBottom = document.getElementById('mode-igc-btn-bottom');

  if (liveModeBtn) liveModeBtn.addEventListener('click', switchToLiveMode);
  if (replayModeBtn) replayModeBtn.addEventListener('click', switchToReplayMode);
  if (igcModeBtn) igcModeBtn.addEventListener('click', switchToIgcMode);
  if (igcModeBtnBottom) igcModeBtnBottom.addEventListener('click', switchToIgcMode);
}, 100);

function setLiveModeState({ syncUrl = true } = {}) {
  const wasPVR = replayManager?.isLiveMode;
  const alreadyPlainLive =
    currentMode === 'live' &&
    !wasPVR &&
    selectedTask === null &&
    replaySelectedTask === null;

  // If already in plain live mode (not PVR), nothing to do
  if (alreadyPlainLive) return;

  currentMode = 'live';
  selectedTask = null;
  replaySelectedTask = null;
  autoLiveTaskSelectionPending = true;
  resetEtfTracking();
  resetMacCreadyAutoState();

  // Update button states
  const liveModeBtn = document.getElementById('mode-live-btn');
  const replayModeBtn = document.getElementById('mode-replay-btn');
  const igcModeBtn = document.getElementById('mode-igc-btn');
  const igcModeBtnBottom = document.getElementById('mode-igc-btn-bottom');

  if (liveModeBtn) liveModeBtn.classList.add('active');
  if (replayModeBtn) replayModeBtn.classList.remove('active');
  if (igcModeBtn) igcModeBtn.classList.remove('active');
  if (igcModeBtnBottom) igcModeBtnBottom.classList.remove('active');

  // Stop and clean up replay / PVR
  if (replayManager) {
    replayManager.destroy();
    replayManager = null;
  }

  // Hide replay controls and reset live mode indicator
  replayControls.setShareEnabled(false);
  replayControls.setLiveMode(false);
  replayControls.hide();

  clearTaskOverlay();
  if (syncUrl) {
    updateURLParameter(null);
  }
  updateTasksPanel();

  // Clear replay update interval
  if (replayUpdateInterval) {
    clearInterval(replayUpdateInterval);
    replayUpdateInterval = null;
  }
  if (replaySeekSettleTimer) {
    clearTimeout(replaySeekSettleTimer);
    replaySeekSettleTimer = null;
  }
  replaySeekInProgress = false;

  // Re-enable auto-reconnect and reconnect to live WebSocket
  shouldAutoReconnect = true;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    connect();
  }

  fetchTasks();
  scheduleLiveTaskRefresh();
}

function clearRenderedAircraftAndState() {
  aircraftManager?.clearAll();
  aircraftStateTracker.clearAll();
  aircraftTimestamps.clear();

  if (mapManager?.aircraftManager3D?.clearAll) {
    mapManager.aircraftManager3D.clearAll();
  }
  if (mapManager?.trailManager3D?.clearAll) {
    mapManager.trailManager3D.clearAll();
  }
  if (mapManager?.aircraftData?.clear) {
    mapManager.aircraftData.clear();
  }
}

// Switch to Live Mode
function switchToLiveMode() {
  setLiveModeState({ syncUrl: true });
}

// Switch to Replay Mode
function switchToReplayMode() {
  if (currentMode === 'replay') {
    replaySetup.show();
    return;
  }

  // Don't change mode yet - wait for user to load replay
  // Just show the setup panel
  replaySetup.show();
}

// --- IGC share state (SoaringSpot / WeGlide) ---
let currentSoaringSpotUrl = null;    // The source URL when loaded from SoaringSpot or WeGlide
let currentSoaringSpotConfig = null; // Config snapshot for share URL generation
let currentIgcSourceType = null;     // 'soaringspot' | 'weglide' | 'condorclub' | null
const condorClubBookmarkletRequestIds = [];
const condorClubBookmarkletRequestSet = new Set();
let condorClubBookmarkletStatusHideTimer = null;

function clearCondorClubBookmarkletStatusHideTimer() {
  if (condorClubBookmarkletStatusHideTimer) {
    window.clearTimeout(condorClubBookmarkletStatusHideTimer);
    condorClubBookmarkletStatusHideTimer = null;
  }
}

function notifyCondorClubBookmarkletStarted() {
  window.dispatchEvent(new CustomEvent('condorclub-bookmarklet-import-started'));
}

function showCondorClubBookmarkletWaiting(message) {
  clearCondorClubBookmarkletStatusHideTimer();
  replayLoadingModal.show();
  replayLoadingModal.setMessage(
    message || 'Waiting for Condor.Club to finish downloading IGC files in the other tab...'
  );
}

function scheduleCondorClubBookmarkletErrorHide(delayMs = 4000) {
  clearCondorClubBookmarkletStatusHideTimer();
  condorClubBookmarkletStatusHideTimer = window.setTimeout(() => {
    replayLoadingModal.hide();
    condorClubBookmarkletStatusHideTimer = null;
  }, delayMs);
}

function clearCondorClubBookmarkletHash() {
  if (window.location.hash !== '#cc-import') {
    return;
  }
  const url = new URL(window.location.href);
  url.hash = '';
  window.history.replaceState({}, '', url);
}

function primeCondorClubBookmarkletWaitingState() {
  if (window.location.hash !== '#cc-import') {
    return;
  }
  notifyCondorClubBookmarkletStarted();
  showCondorClubBookmarkletWaiting(
    'Condor.Club import started. Keep the Condor.Club tab open while the selected IGC files download in the background...'
  );
  clearCondorClubBookmarkletHash();
}

function updateSoaringSpotURLParameters() {
  if (!currentSoaringSpotUrl || !currentSoaringSpotConfig) return;
  const url = new URL(window.location);
  url.searchParams.delete('taskid');
  url.searchParams.delete('replayTaskId');
  url.searchParams.delete('replayFormat');
  // Clear both source params, then set the correct one
  url.searchParams.delete('ssUrl');
  url.searchParams.delete('wgUrl');
  if (currentIgcSourceType === 'weglide') {
    url.searchParams.set('wgUrl', currentSoaringSpotUrl);
  } else {
    url.searchParams.set('ssUrl', currentSoaringSpotUrl);
  }
  try {
    const configB64 = btoa(JSON.stringify(currentSoaringSpotConfig));
    url.searchParams.set('ssConfig', configB64);
  } catch (e) {
    console.warn('[IGC Share] Failed to encode share config:', e);
  }
  window.history.replaceState({}, '', url);
}

// Switch to IGC Mode — show import dialog for file/folder/SoaringSpot choice
async function switchToIgcMode() {
  try {
    const { showIgcImportDialog } = await import('./igc/igcImportDialog.js');
    const choice = await showIgcImportDialog();
    if (!choice) return; // cancelled

    if (choice.source === 'files' || choice.source === 'folder') {
      // Local files — feed directly into the processing pipeline
      currentSoaringSpotUrl = null;
      currentSoaringSpotConfig = null;
      currentIgcSourceType = null;
      await processIgcFiles(choice.files);
    } else if (choice.source === 'soaringspot') {
      // SoaringSpot URL — scrape, download, then process
      replayLoadingModal.show();
      replayLoadingModal.setMessage('Fetching SoaringSpot results...');
      try {
        const { importFromSoaringSpot } = await import('./igc/soaringSpotImporter.js');
        const result = await importFromSoaringSpot(choice.url, (current, total, name) => {
          if (total > 0) {
            replayLoadingModal.setMessage(`Downloading IGC ${current}/${total}: ${name}`);
          } else {
            replayLoadingModal.setMessage(name);
          }
        });
        replayLoadingModal.hide();
        currentSoaringSpotUrl = choice.url;
        currentIgcSourceType = 'soaringspot';
        await processIgcFiles(result.files, result.metadata, result.task);
      } catch (err) {
        replayLoadingModal.hide();
        console.error('[SoaringSpot] Import failed:', err);
        alert('SoaringSpot import failed: ' + err.message);
      }
    } else if (choice.source === 'weglide') {
      // WeGlide URL — API fetch, download IGCs, then process
      replayLoadingModal.show();
      replayLoadingModal.setMessage('Fetching WeGlide data...');
      try {
        const { importFromWeGlide } = await import('./igc/weGlideImporter.js');
        const result = await importFromWeGlide(choice.url, (current, total, name) => {
          if (total > 0) {
            replayLoadingModal.setMessage(`Downloading IGC ${current}/${total}: ${name}`);
          } else {
            replayLoadingModal.setMessage(name);
          }
        });
        replayLoadingModal.hide();
        currentSoaringSpotUrl = choice.url;
        currentIgcSourceType = 'weglide';
        await processIgcFiles(result.files, result.metadata, result.task);
      } catch (err) {
        replayLoadingModal.hide();
        console.error('[WeGlide] Import failed:', err);
        alert('WeGlide import failed: ' + err.message);
      }
    }
  } catch (err) {
    console.error('[IGC] Import dialog error:', err);
  }
}

// Extracted handler so both folder and file inputs can use it
async function handleIgcFilesSelected(e) {
  if (!e.target.files || e.target.files.length === 0) return;
  try {
    await processIgcFiles(Array.from(e.target.files));
  } catch (err) {
    console.error("IGC Load Error:", err);
    replayLoadingModal.hide();
    alert("Error loading IGC files: " + err.message);
    switchToLiveMode();
  }
  // reset input
  e.target.value = '';
}

/**
 * Core IGC processing pipeline: parse files, show setup dialogs, load into replay.
 * @param {File[]} fileList - IGC/CUP/FPL file objects
 * @param {Map<string, object>|null} soaringSpotMeta - Optional metadata from SoaringSpot/WeGlide import
 * @param {object|null} soaringSpotTask - Optional task definition from SoaringSpot/WeGlide API
 * @param {object|null} sharedConfig - Optional pre-fill config from a share URL
 */
async function processIgcFiles(fileList, soaringSpotMeta = null, soaringSpotTask = null, sharedConfig = null) {
  if (!fileList || fileList.length === 0) return;

  // Import parser dynamically to avoid adding to main bundle early
  try {
    console.log(`[IGC] Starting parse for ${fileList.length} file(s)`);
    replayLoadingModal.show();
    const { IgcParser } = await import('./igc/igcParser.js');

    console.log(`[IGC] Loading parser complete. Invoking parse...`);
    const parsedData = await IgcParser.parseFiles(fileList);
    console.log(`[IGC] Parse complete. Flights:`, parsedData.flights?.length);

    // --- Apply external metadata overrides (SoaringSpot / WeGlide) ---
    if (soaringSpotMeta && parsedData.flights) {
      // External imports should always show the full Glider Setup dialog
      // (polar mapping + handicaps + "Apply to all"), never the Condor-only wing loading prompt
      parsedData.isCondor = false;

      for (const flight of parsedData.flights) {
        // Match by filename — the File.name we constructed matches the metadata map key
        const meta = soaringSpotMeta.get(flight._sourceFileName);
        if (meta) {
          if (meta.cn) flight.pilot.callsign = meta.cn;
          if (meta.gliderModel) flight.aircraft.model_name = meta.gliderModel;
          if (meta.handicap != null) flight._soaringSpotHandicap = meta.handicap;
          if (meta.pilotName) flight._soaringSpotPilotName = meta.pilotName;

          // Register WeGlide polar coefficients as fallback before polar mapping dialog
          if (meta.encodedCoeffs && meta.gliderModel) {
            globalPolarManager.registerExternalCoefficients(
              meta.gliderModel,
              meta.encodedCoeffs,
              meta.referenceMass || null,
              meta.wingArea || null
            );
          }

          console.log(`[IGC Import] Applied metadata for ${meta.cn}: ${meta.gliderModel}, HC=${meta.handicap}`);
        }
      }
    }

    // Setup replay engine for IGC
    currentMode = 'igc';
    resetEtfTracking();
    updateURLParameter(null);
    replayControls.setLiveMode(false);

    const liveModeBtn = document.getElementById('mode-live-btn');
    const replayModeBtn = document.getElementById('mode-replay-btn');
    const igcModeBtn = document.getElementById('mode-igc-btn');
    const igcModeBtnBottom = document.getElementById('mode-igc-btn-bottom');

    if (liveModeBtn) liveModeBtn.classList.remove('active');
    if (replayModeBtn) replayModeBtn.classList.remove('active');
    if (igcModeBtn) igcModeBtn.classList.add('active');
    if (igcModeBtnBottom) igcModeBtnBottom.classList.add('active');

    shouldAutoReconnect = false;
    if (ws) {
      ws.close();
      ws = null;
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    clearRenderedAircraftAndState();

    if (!replayManager) {
      replayManager = new ReplayManager(aircraftManager, mapManager, HTTP_URL, aircraftStateTracker, globalPolarManager, globalEnergyCalc);
      replayManager.setPositionUpdateCallback(updateDistanceStatsWidget);
      aircraftManager.labelRefreshMs = 1000 / Math.max(0.1, config.replayRefreshHz);
      replayManager.setSpeed(replayControls.playbackSpeed);
    }

    aircraftStateTracker.clearAll();
    const distanceStatsBody = document.getElementById('distance-stats-body');
    if (distanceStatsBody) {
      distanceStatsBody.innerHTML = '<tr><td colspan="16" class="no-data">Loading IGC data...</td></tr>';
    }

    replayLoadingModal.hide();

    // --- SoaringSpot task override ---
    // If we have a task from SoaringSpot's task page, use it as the authoritative task
    // and skip the IGC-based conflict resolution entirely.
    if (soaringSpotTask && soaringSpotTask.turnpoints && soaringSpotTask.turnpoints.length > 0) {
      parsedData.task = soaringSpotTask;
      parsedData.taskCandidates = null;
      console.log(`[SoaringSpot] Using authoritative task: ${soaringSpotTask.task_name} (${soaringSpotTask.turnpoints.length} TPs)`);
    }

    // --- Task conflict resolution ---
    if (!parsedData.task && parsedData.taskCandidates && parsedData.taskCandidates.length > 0) {
      const candidates = parsedData.taskCandidates;
      const { showTaskSelectionDialog } = await import('./igc/igcSetupDialogs.js');
      const choiceIdx = await showTaskSelectionDialog(candidates);
      const selectedCandidate =
        (choiceIdx != null && choiceIdx >= 0 && choiceIdx < candidates.length)
          ? candidates[choiceIdx]
          : candidates[0];

      parsedData.task = selectedCandidate.task;
      if (selectedCandidate.taskMetadata) {
        parsedData.taskMetadata = selectedCandidate.taskMetadata;
      }

      if (choiceIdx != null && choiceIdx >= 0 && choiceIdx < candidates.length) {
        console.log(`[Task] User selected task from ${selectedCandidate.source}`);
      } else {
        console.log(`[Task] Defaulting to task from ${selectedCandidate.source}`);
      }
    }

    if (parsedData.task && parsedData.taskMetadata && typeof IgcParser.applyTaskMetadata === 'function') {
      parsedData.task = IgcParser.applyTaskMetadata(parsedData.task, parsedData.taskMetadata);
    }

    config.taskMaxWingLoading = parsedData.task
      ? getTaskMaxWingLoadingKgM2(parsedData.task)
      : null;

    // Infer start max / finish min altitudes from flight data when not explicitly set
    if (parsedData.task && parsedData.flights) {
      IgcParser.inferHeightLimits(parsedData.task, parsedData.flights);
    }

    // --- Non-Condor setup dialogs ---
    if (!parsedData.isCondor) {
      const { showHeightSetupDialog, showPolarMappingDialog } = await import('./igc/igcSetupDialogs.js');

      // 1. Task setup dialog: height limits + wing loading (only if we have a task)
      if (parsedData.task) {
        const result = await showHeightSetupDialog(
          parsedData.task,
          sharedConfig?.wingLoading ?? config.wingLoading,
          sharedConfig?.startTimeOverrideLocal ?? config.startTimeOverrideLocal,
          parsedData.time_window?.start || parsedData.flights?.[0]?.positions?.[0]?.timestamp || null,
          sharedConfig
        );
        wingLoadingPromptedThisSession = true;
        if (result?.wingLoading != null) {
          config.wingLoading = result.wingLoading;
          const wingLoadingInput = document.getElementById('wing-loading-value');
          if (wingLoadingInput) wingLoadingInput.value = result.wingLoading;
        }
        if (result) {
          config.startTimeOverrideLocal = result.startTimeOverrideLocal || '';
          config.startTimeOverrideIso = result.startTimeOverrideIso || null;
          saveSettings();
          notifyEtfInputChange();
        }
      }

      // 2. Glider setup dialog (polar mapping + optional handicap overrides)
      const gliderSetupResult = await showPolarMappingDialog(parsedData.flights, globalPolarManager, sharedConfig, currentIgcSourceType);
      if (parsedData.task && gliderSetupResult) {
        parsedData.task.handicapOverrides = gliderSetupResult.handicapOverrides || {};
        parsedData.task.handicapEnabled = gliderSetupResult.handicapEnabled === true;
      }

      // Capture config for share URL generation
      if (currentSoaringSpotUrl && parsedData.task) {
        currentSoaringSpotConfig = {
          startMax: parsedData.task.turnpoints?.[0]?.MaxAltMeters ?? null,
          finishMin: parsedData.task.turnpoints?.[parsedData.task.turnpoints.length - 1]?.MinAltMeters ?? null,
          wingLoading: config.wingLoading,
          startTimeOverrideLocal: config.startTimeOverrideLocal || null,
          polarMappings: gliderSetupResult?.polarMappings || {},
          handicapOverrides: gliderSetupResult?.handicapOverrides || {},
          handicapEnabled: gliderSetupResult?.handicapEnabled ?? true,
        };
        updateSoaringSpotURLParameters();
      }

    } else {
      // Condor IGC: no height dialog needed, but still prompt for wing loading
      wingLoadingPromptedThisSession = true;
      const { showWingLoadingDialog } = await import('./igc/igcSetupDialogs.js');
      const detectedTaskMaxWingLoading = parsedData.task
        ? getTaskMaxWingLoadingKgM2(parsedData.task)
        : null;
      const initialWingLoading = Number.isFinite(detectedTaskMaxWingLoading) && detectedTaskMaxWingLoading > 0
        ? detectedTaskMaxWingLoading
        : config.wingLoading;
      const wingLoadingSubtitle = Number.isFinite(detectedTaskMaxWingLoading) && detectedTaskMaxWingLoading > 0
        ? `Max wing loading detected in IGC. Adjust it for XC speed calculations if needed.`
        : undefined;
      const wl = await showWingLoadingDialog(initialWingLoading, {
        subtitle: wingLoadingSubtitle
      });
      if (Number.isFinite(wl)) {
        config.wingLoading = wl;
        const wingLoadingInput = document.getElementById('wing-loading-value');
        if (wingLoadingInput) wingLoadingInput.value = wl;
        saveSettings();
        notifyEtfInputChange();
      }
    }

    replayControls.resetAlignStarts();
    const replayInfo = replayManager.loadData(parsedData);

    if (replayManager.flights && replayManager.flights.length > 0) {
      const aircraftTypes = replayManager.flights
        .map(flight => flight.aircraft?.model_name)
        .filter(type => type);
      if (aircraftTypes.length > 0) {
        window._replayAircraftTypes = aircraftTypes;
      }
    }

    replayControls.show();
    replayControls.setShareEnabled(!!currentSoaringSpotUrl);
    replayControls.setDuration(replayInfo.duration);
    replayControls.setTimeRange(replayInfo.timeRange.start, replayInfo.timeRange.end);
    replayControls.setCurrentTime(0);

    if (parsedData.task) {
      const t = parsedData.task;
      const processedTask = taskManager?.processTask(t);
      const finalTask = processedTask || t;

      const existingTask = activeTasks.find(x => x.id === finalTask.id);
      if (!existingTask) {
        activeTasks.unshift(finalTask);
      }
      selectedTask = finalTask.id;
      replaySelectedTask = finalTask;

      if (finalTask.turnpoints && finalTask.turnpoints.length > 0) {
        replayManager.setSelectedTask(finalTask);
        updateAlignStartsVisibility();
        drawTaskOnMap(finalTask.id);
        updateTasksPanel();

        // Apply winner MC immediately from precomputed finish data (no need to
        // wait for the replay clock to reach finish time).
        maybeApplyWinnerMacCready(finalTask, null);
      } else {
        console.warn("[IGC] Parsed task lacks turnpoints and cannot be drawn.");
      }

      if (window._replayAircraftTypes && taskManager) {
        taskManager.updateTaskClassFromAircraft(t.id, window._replayAircraftTypes);
        updateTasksPanel();
      }
    }

    // Auto-seek to regatta start time so the user doesn't have to FF through
    // hours of pre-race thermalling.  Seek to 30s before the gate so the user
    // sees pilots approaching the start line.
    const regattaAutoSeekMs = aircraftStateTracker.getRegattaReferenceStartTime();
    if (Number.isFinite(regattaAutoSeekMs) && replayManager.minTime) {
      const offsetMs = regattaAutoSeekMs - replayManager.minTime.getTime();
      if (offsetMs > 30000) { // only seek if gate is >30s into the replay
        const seekToSec = Math.max(0, (offsetMs / 1000) - 30);
        console.log(`[Regatta] Auto-seeking to ${seekToSec.toFixed(0)}s (gate at ${new Date(regattaAutoSeekMs).toISOString()})`);
        replayManager.seekTo(new Date(regattaAutoSeekMs - 30000));
        replayControls.setCurrentTime(seekToSec);
      }
    }

    // Pre-load polars for all aircraft types before starting playback.
    // Without this, loadPolarOnDemand fires async during animation, causing
    // early ETF samples to use altitude_m while later ones use tec_m (which
    // includes kinetic energy).  The mixed history creates intermittent
    // inflated XC Pace deltas that vary by reload timing.
    if (replayManager.flights && replayManager.flights.length > 0) {
      const uniqueTypes = new Set();
      for (const flight of replayManager.flights) {
        const type = flight.aircraft?.model_name
          || flight.positions?.[0]?.aircraft_type
          || flight.positions?.[0]?.aircraft;
        if (type) uniqueTypes.add(type);
      }
      if (uniqueTypes.size > 0) {
        await Promise.all(
          [...uniqueTypes].map(type => globalPolarManager.loadPolarOnDemand(type).catch(() => {}))
        );
      }
    }

    replayControls.togglePlayPause();

  } catch (err) {
    console.error("IGC Load Error:", err);
    replayLoadingModal.hide();
    alert("Error loading IGC files: " + err.message);
    switchToLiveMode();
  }
}

function rememberCondorClubBookmarkletRequest(requestId) {
  if (!requestId || condorClubBookmarkletRequestSet.has(requestId)) {
    return;
  }
  condorClubBookmarkletRequestSet.add(requestId);
  condorClubBookmarkletRequestIds.push(requestId);
  while (condorClubBookmarkletRequestIds.length > 40) {
    const oldest = condorClubBookmarkletRequestIds.shift();
    if (oldest) {
      condorClubBookmarkletRequestSet.delete(oldest);
    }
  }
}

function isCondorClubOrigin(origin) {
  try {
    const hostname = new URL(origin).hostname.toLowerCase();
    return hostname === 'condor.club' || hostname.endsWith('.condor.club');
  } catch {
    return false;
  }
}

function acknowledgeCondorClubBookmarkletImport(event, requestId) {
  try {
    if (!event?.source || !requestId) return;
    event.source.postMessage({
      type: 'condorclub-import-ack',
      requestId
    }, event.origin);
  } catch (err) {
    console.warn('[Condor.Club] Failed to acknowledge bookmarklet import:', err);
  }
}

function sanitizeCondorClubImportedFileName(rawName, index) {
  let fileName = String(rawName || '').trim();
  if (!fileName) {
    fileName = `condorclub_${index}.igc`;
  }
  fileName = fileName.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim();
  if (!fileName.toLowerCase().endsWith('.igc')) {
    fileName += '.igc';
  }
  return fileName;
}

function buildCondorClubImportedFiles(payloadFiles) {
  if (!Array.isArray(payloadFiles)) {
    return [];
  }
  return payloadFiles
    .map((entry, index) => {
      if (!entry || typeof entry.text !== 'string' || !entry.text.trim()) {
        return null;
      }
      return new File(
        [entry.text],
        sanitizeCondorClubImportedFileName(entry.name, index + 1),
        { type: 'application/octet-stream' }
      );
    })
    .filter(Boolean);
}

function handleCondorClubBookmarkletStatusMessage(event) {
  const data = event?.data;
  if (!data || data.type !== 'condorclub-import-status' || !isCondorClubOrigin(event.origin)) {
    return;
  }

  notifyCondorClubBookmarkletStarted();

  const phase = String(data.phase || 'progress').toLowerCase();
  const message = String(data.message || '').trim();

  if (phase === 'error') {
    replayLoadingModal.show();
    replayLoadingModal.setMessage(
      message || 'Condor.Club import failed in the source tab. Check the Condor.Club window for details.'
    );
    scheduleCondorClubBookmarkletErrorHide();
    return;
  }

  showCondorClubBookmarkletWaiting(
    message || 'Condor.Club is downloading IGC files in the background...'
  );
}

async function handleCondorClubBookmarkletMessage(event) {
  const data = event?.data;
  if (!data || data.type !== 'condorclub-import' || !isCondorClubOrigin(event.origin)) {
    return;
  }

  const requestId = String(data.requestId || '').trim();
  if (!requestId) {
    console.warn('[Condor.Club] Ignoring bookmarklet import without requestId');
    return;
  }

  if (condorClubBookmarkletRequestSet.has(requestId)) {
    acknowledgeCondorClubBookmarkletImport(event, requestId);
    return;
  }

  rememberCondorClubBookmarkletRequest(requestId);
  acknowledgeCondorClubBookmarkletImport(event, requestId);

  const importedFiles = buildCondorClubImportedFiles(data.files);
  if (importedFiles.length === 0) {
    alert('Condor.Club import failed: no IGC files were received.');
    return;
  }

  try {
    notifyCondorClubBookmarkletStarted();
    clearCondorClubBookmarkletStatusHideTimer();
    currentSoaringSpotUrl = null;
    currentSoaringSpotConfig = null;
    currentIgcSourceType = 'condorclub';
    replayLoadingModal.show();
    replayLoadingModal.setMessage(`Received ${importedFiles.length} Condor.Club IGC file${importedFiles.length === 1 ? '' : 's'}. Parsing replay data...`);
    await processIgcFiles(importedFiles);
  } catch (err) {
    console.error('[Condor.Club] Bookmarklet import failed:', err);
    alert('Condor.Club import failed: ' + err.message);
  }
}

window.addEventListener('message', (event) => {
  handleCondorClubBookmarkletStatusMessage(event);
  void handleCondorClubBookmarkletMessage(event);
});
window.addEventListener('hashchange', primeCondorClubBookmarkletWaitingState);
primeCondorClubBookmarkletWaitingState();

// Handle IGC file selection (initial listener setup)
const igcFileInput = document.getElementById('igc-file-input');
if (igcFileInput) {
  igcFileInput.addEventListener('change', handleIgcFilesSelected);
}

async function loadServerReplay(taskId, options = {}) {
  const {
    format = 'json',
    timeRange = null,
    syncUrl = true,
    startSeconds = null,
    alignStarts = false
  } = options;

  try {
    currentMode = 'replay';
    resetEtfTracking();
    replayData = { task: taskId, timeRange, format };
    selectedTask = null;
    replaySelectedTask = null;

    // Update button states
    const liveModeBtn = document.getElementById('mode-live-btn');
    const replayModeBtn = document.getElementById('mode-replay-btn');
    const igcModeBtn = document.getElementById('mode-igc-btn');
    const igcModeBtnBottom = document.getElementById('mode-igc-btn-bottom');

    if (liveModeBtn) liveModeBtn.classList.remove('active');
    if (replayModeBtn) replayModeBtn.classList.add('active');
    if (igcModeBtn) igcModeBtn.classList.remove('active');
    if (igcModeBtnBottom) igcModeBtnBottom.classList.remove('active');

    // Disconnect WebSocket for live mode
    shouldAutoReconnect = false;
    if (ws) {
      ws.close();
      ws = null;
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    clearRenderedAircraftAndState();

    if (!replayManager) {
      replayManager = new ReplayManager(aircraftManager, mapManager, HTTP_URL, aircraftStateTracker, globalPolarManager, globalEnergyCalc);
      replayManager.setPositionUpdateCallback(updateDistanceStatsWidget);
      aircraftManager.labelRefreshMs = 1000 / Math.max(0.1, config.replayRefreshHz);
    } else if (replayManager.isLiveMode) {
      replayManager.exitLiveMode();
    }
    replayManager.setSpeed(replayControls.playbackSpeed);

    replayControls.setShareEnabled(true);
    replayControls.setLiveMode(false);
    clearTaskOverlay();
    updateTasksPanel();
    replayLoadingModal.show();

    // Replay setup currently loads server-backed replay by task ID.
    replayControls.resetAlignStarts();
    const replayInfo = await replayManager.load(taskId, null, format);

    aircraftStateTracker.clearAll();
    const distanceStatsBody = document.getElementById('distance-stats-body');
    if (distanceStatsBody) {
      distanceStatsBody.innerHTML = '<tr><td colspan="16" class="no-data">Loading replay data...</td></tr>';
    }

    if (replayManager.flights && replayManager.flights.length > 0) {
      const aircraftTypes = replayManager.flights
        .map(flight => flight.aircraft?.model_name)
        .filter(type => type);

      if (aircraftTypes.length > 0) {
        window._replayAircraftTypes = aircraftTypes;
      }
    }

    replayLoadingModal.hide();
    replayControls.show();
    replayControls.setDuration(replayInfo.duration);
    replayControls.setTimeRange(replayInfo.timeRange.start, replayInfo.timeRange.end);
    replayControls.setCurrentTime(0);

    if (syncUrl) {
      updateReplayURLParameters({ taskId, format });
    }

    try {
      const taskData = await fetchTaskById(taskId);
      if (taskData) {
        const existingTask = activeTasks.find(t => t.id === taskId);
        if (!existingTask) {
          activeTasks.unshift(taskData);
        }

        selectedTask = taskId;
        replaySelectedTask = taskData;
        drawTaskOnMap(taskId);
        updateTasksPanel();

        if (replayManager) {
          replayManager.setSelectedTask(taskData);
          updateAlignStartsVisibility();
        }

        if (window._replayAircraftTypes && taskManager) {
          taskManager.updateTaskClassFromAircraft(taskId, window._replayAircraftTypes);
          updateTasksPanel();
          delete window._replayAircraftTypes;
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not auto-load task for replay:', error);
    }

    if (alignStarts && replayControls && !replayControls.alignStartsEnabled) {
      replayControls.toggleAlignStarts();
    }

    if (Number.isFinite(startSeconds)) {
      seekReplayToSeconds(startSeconds);
    }

    setReplayPlaybackRunning(true);
    return true;
  } catch (error) {
    console.error('❌ Failed to load replay:', error);
    replayLoadingModal.hide();
    alert('Failed to load replay data. Please try again.');
    setLiveModeState({ syncUrl });
    return false;
  }
}

// Handle replay setup load
replaySetup.addEventListener('load', async (event) => {
  const { task, timeRange, format } = event.detail;
  await loadServerReplay(task, { format, timeRange, syncUrl: true });
});

// Handle replay setup hidden (cancelled)
replaySetup.addEventListener('hidden', () => {
  // If modal was cancelled (not in replay mode yet), ensure Live button is active
  if (currentMode !== 'replay') {
    const liveModeBtn = document.getElementById('mode-live-btn');
    const replayModeBtn = document.getElementById('mode-replay-btn');

    if (liveModeBtn && replayModeBtn) {
      liveModeBtn.classList.add('active');
      replayModeBtn.classList.remove('active');
    }
  }
});

// Handle replay exit
replayControls.addEventListener('exit', () => {
  switchToLiveMode();
});

// Replay update interval
let replayUpdateInterval = null;
let replaySeekInProgress = false;
let lastSeekRealTime = 0; // performance.now() of last seek; used to bypass sampled capture interval
let replaySeekSettleTimer = null;
const REPLAY_SEEK_SETTLE_MS = 180;
const REPLAY_TREND_BACKFILL_WINDOW_MS = 2500;

function markReplaySeekInProgress() {
  replaySeekInProgress = true;
  lastSeekRealTime = performance.now();

  // Invalidate sampled XC pace caches and per-aircraft delta maps so stale
  // pre-seek display values disappear.  Do NOT clear etfHistory — it contains
  // real recorded baselines from previous playback that let calculateTrendDelta
  // produce correct results instantly on the same frame as the seek.
  // (recordTrendSample already self-manages history: backward scrubs filter out
  // future samples; forward scrubs keep history for the stale guard + backfill.)
  sampledEtfDeltasA.clear();
  sampledEtfDeltasB.clear();
  etfDeltaByAircraft.clear();
  etfDeltaByAircraftB.clear();

  if (replaySeekSettleTimer) {
    clearTimeout(replaySeekSettleTimer);
  }
  replaySeekSettleTimer = setTimeout(() => {
    replaySeekInProgress = false;
    // Force a full widget pass once seek settles so retrospective XC windows update immediately.
    updateDistanceStatsWidget._lastRefresh = 0;
    updateDistanceStatsWidget();
  }, REPLAY_SEEK_SETTLE_MS);
}

// Handle replay playback events
replayControls.addEventListener('playPauseToggled', (event) => {
  const { isPlaying } = event.detail;

  if (!replayManager) return;

  if (isPlaying) {
    replayManager.play();

    // Start updating the timeline
    if (!replayUpdateInterval) {
      replayUpdateInterval = setInterval(() => {
        if (replayManager && replayManager.isPlaying) {
          replayManager.ensurePlaybackLoop?.();
          const currentTime = replayManager.getCurrentTimeSeconds();
          replayControls.setCurrentTime(currentTime);
        }
      }, 100); // Update every 100ms
    }
  } else {
    replayManager.pause();

    // Stop updating the timeline
    if (replayUpdateInterval) {
      clearInterval(replayUpdateInterval);
      replayUpdateInterval = null;
    }
  }
});

replayControls.addEventListener('timeChanged', (event) => {
  const { currentTime } = event.detail;

  if (!replayManager) return;
  markReplaySeekInProgress();

  // Show ETF title briefly after scrubs
  etfLabelShowUntilMs = Date.now() + 5000;
  window.etfLabelShowUntilMs = etfLabelShowUntilMs;

  // Clear all 3D trails on seek to prevent warp lines
  if (mapManager.getMode() === '3d') {
    mapManager.clearAllTrails();
  }

  // Seek to the new time (use effective min for aligned mode)
  const startTime = replayManager.getEffectiveMinTime();
  const seekTime = new Date(startTime.getTime() + currentTime * 1000);
  replayManager.seekTo(seekTime);
  replayControls.setAtLiveEdge(replayManager.isAtLiveEdge());
});

replayControls.addEventListener('alignStartsToggled', (event) => {
  const { enabled } = event.detail;
  if (!replayManager) return;

  replayManager.setAlignStarts(enabled);

  // Update timeline bounds and position in controls
  const newDuration = replayManager.getDurationSeconds();
  replayControls.setDuration(newDuration);
  replayControls.setTimeRange(replayManager.getEffectiveMinTime(), replayManager.getEffectiveMaxTime());
  replayControls.setReferenceStartTime(enabled ? replayManager.referenceStartTime : null);
  replayControls.setCurrentTime(replayManager.getCurrentTimeSeconds());
});

replayControls.addEventListener('speedChanged', (event) => {
  const { speed } = event.detail;

  if (!replayManager) return;

  const effectiveSpeed = replayControls.getEffectivePlaybackSpeed?.() ?? speed;
  replayManager.setSpeed(effectiveSpeed);
});

replayControls.addEventListener('liveEdgeStateChanged', (event) => {
  if (!replayManager) return;
  const { effectiveSpeed } = event.detail || {};
  if (Number.isFinite(effectiveSpeed) && effectiveSpeed > 0) {
    replayManager.setSpeed(effectiveSpeed);
  }
});

// Jump to Live button in PVR mode
replayControls.addEventListener('jumpToLive', () => {
  if (replayManager?.isLiveMode) {
    markReplaySeekInProgress();
    replayManager.jumpToLiveEdge();
    replayControls.setCurrentTime(replayManager.getCurrentTimeSeconds());
    replayControls.setAtLiveEdge(replayManager.isAtLiveEdge());
  }
});

function setReplayPlaybackRunning(shouldPlay) {
  if (!replayManager) return false;

  const wantsPlay = !!shouldPlay;
  if (wantsPlay && !replayControls.isPlaying) {
    replayControls.togglePlayPause();
  } else if (!wantsPlay && replayControls.isPlaying) {
    replayControls.togglePlayPause();
  }
  return true;
}

function seekReplayToSeconds(seconds) {
  if (!replayManager || !replayManager.minTime || !replayManager.maxTime) {
    return false;
  }
  markReplaySeekInProgress();
  const effMin = replayManager.getEffectiveMinTime();
  const effMax = replayManager.getEffectiveMaxTime();
  const durationSec = Math.max(0, (effMax.getTime() - effMin.getTime()) / 1000);
  const safeSeconds = Math.max(0, Math.min(durationSec, Number(seconds) || 0));

  // Match normal scrub behavior
  etfLabelShowUntilMs = Date.now() + 2000;
  window.etfLabelShowUntilMs = etfLabelShowUntilMs;
  if (mapManager.getMode() === '3d') {
    mapManager.clearAllTrails();
  }

  const seekTime = new Date(effMin.getTime() + safeSeconds * 1000);
  replayManager.seekTo(seekTime);
  replayControls.setCurrentTime(safeSeconds);

  // Force leaderboard/localStorage publish even when replay refresh throttling is active.
  updateDistanceStatsWidget._lastRefresh = 0;
  updateDistanceStatsWidget();
  return true;
}

function updateAlignStartsVisibility() {
  const isLive = replayManager?.isLiveMode === true;
  const isReplayLike = currentMode === 'replay' || currentMode === 'igc';
  replayControls.setAlignStartsVisible(isReplayLike && !isLive);
}

window.__overlayRenderApi = {
  version: '1.0.0',
  isReady: () => !!(replayManager && replayManager.minTime && replayManager.maxTime && (currentMode === 'replay' || currentMode === 'igc')),
  isTaskReady: () => !!resolveSelectedTaskObject(),
  getMode: () => currentMode,
  getDurationSec: () => {
    if (!replayManager || !replayManager.minTime || !replayManager.maxTime) return null;
    return Math.max(0, (replayManager.maxTime.getTime() - replayManager.minTime.getTime()) / 1000);
  },
  getCurrentSec: () => {
    if (!replayManager || !replayManager.currentTime || !replayManager.minTime) return null;
    return Math.max(0, (replayManager.currentTime.getTime() - replayManager.minTime.getTime()) / 1000);
  },
  pause: () => setReplayPlaybackRunning(false),
  play: () => setReplayPlaybackRunning(true),
  seekToSec: (seconds) => seekReplayToSeconds(seconds),
  setReplayRefreshHz: (hz) => {
    const numeric = Number(hz);
    if (!Number.isFinite(numeric) || numeric <= 0) return false;
    config.replayRefreshHz = numeric;
    return true;
  },
  forceOverlayRefresh: () => {
    updateDistanceStatsWidget._lastRefresh = 0;
    updateDistanceStatsWidget();
    return true;
  },
  getLeaderboardSnapshot: () => {
    try {
      const raw = localStorage.getItem('condormap-leaderboard');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }
};

// ========================================
// END REPLAY MODE SETUP
// ========================================

// Task filter button handlers (left panel) - set up AFTER panel is initialized
setTimeout(() => {
  const filterBtns = document.querySelectorAll('.task-filter-btn');
  filterBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const filter = btn.dataset.filter;
      handleTaskFilterChange(filter);
    });
  });

  // Inject left-panel help slot for XC Pace info label
  const leftColorsContainer = document.querySelector('#dropdown-colors')?.parentElement;
  const containerForHelp = leftColorsContainer || document.querySelector('#dropdown-info-label')?.parentElement;
  // Fallback to info label parent if colors container missing
  const targetContainer = containerForHelp;
  if (targetContainer) {
    let help = document.getElementById('left-etf-help');
    if (!help) {
      help = document.createElement('div');
      help.id = 'left-etf-help';
      help.style.display = 'none';
      help.className = 'dropdown-help';
      targetContainer.appendChild(help);
    }
  }
  updateEtfHelpText();
}, 100);

// Sync status indicator between main panel and left panel
function updateLeftPanelStatus(status, text) {
  const leftIndicator = document.getElementById('left-status-indicator');
  const leftText = document.getElementById('left-status-text');

  if (leftIndicator && leftText) {
    leftIndicator.className = `status-indicator ${status}`;
    leftText.textContent = text;
  }
}

// Set up compact basemap selector in left panel
const basemapSelectCompact = document.getElementById('basemap-select-compact');
if (basemapSelectCompact) {
  // Set initial value to match current basemap
  basemapSelectCompact.addEventListener('change', (e) => {
    const selectedBasemap = e.target.value;

    // Change the basemap
    if (basemapSelector && typeof basemapSelector.setBasemap === 'function') {
      basemapSelector.setBasemap(selectedBasemap);
    }
  });
}

// Make performance section collapsible
const performanceSection = document.getElementById('performance-section');
const performanceHeader = document.getElementById('performance-header');
if (performanceHeader && performanceSection) {
  performanceHeader.addEventListener('click', () => {
    performanceSection.classList.toggle('expanded');
  });
  // Start collapsed by default (removed: performanceSection.classList.add('expanded');)
}

// Make basemap section collapsible
const basemapSection = document.getElementById('basemap-section');
const basemapHeader = document.getElementById('basemap-header');
if (basemapHeader && basemapSection) {
  basemapHeader.addEventListener('click', () => {
    basemapSection.classList.toggle('expanded');
  });
}

// Make polar tester section collapsible
const polarTesterSection = document.getElementById('polar-tester-section');
const polarTesterHeader = document.getElementById('polar-tester-header');
if (polarTesterHeader && polarTesterSection) {
  polarTesterHeader.addEventListener('click', () => {
    polarTesterSection.classList.toggle('expanded');
  });
}

// Show Polar Tester button handler
const showPolarTesterBtn = document.getElementById('show-polar-tester-btn');
if (showPolarTesterBtn && polarTesterSection) {
  showPolarTesterBtn.addEventListener('click', () => {
    polarTesterSection.style.display = 'block';
    polarTesterSection.classList.add('expanded');
    // Scroll to polar tester section
    setTimeout(() => {
      polarTesterSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  });
}

// Setup follow functionality
const followPanel = document.getElementById('follow-panel');
const followCallsign = document.getElementById('follow-callsign');
const followClose = document.getElementById('follow-close');

aircraftManager.setFollowCallback((callsign) => {
  if (callsign) {
    followCallsign.textContent = callsign;
    followPanel.style.display = 'flex';
  } else {
    followPanel.style.display = 'none';
  }
});

followClose.addEventListener('click', () => {
  aircraftManager.unfollowAircraft();
});

// Setup leaderboard widget
const distanceStatsWidget = document.getElementById('distance-stats-widget');
const distanceStatsBody = document.getElementById('distance-stats-body');

// CN / Pilot Name toggle state
let leaderboardShowNames = false;
const cnNameToggleBtn = document.getElementById('cn-name-toggle');
if (cnNameToggleBtn) {
  cnNameToggleBtn.addEventListener('click', () => {
    leaderboardShowNames = !leaderboardShowNames;
    cnNameToggleBtn.textContent = leaderboardShowNames ? 'Name' : 'CN';
    cnNameToggleBtn.title = leaderboardShowNames
      ? 'Showing pilot names – click to switch to CN'
      : 'Showing competition numbers – click to switch to pilot names';
    // Force immediate refresh
    updateDistanceStatsWidget._lastRefresh = 0;
    updateDistanceStatsWidget();
  });
}
const leaderboardToggle = document.getElementById('leaderboard-toggle');
const leaderboardExpand = document.getElementById('leaderboard-expand');
const leaderboardCollapse = document.getElementById('leaderboard-collapse');

// Helper to save leaderboard state to localStorage
function saveLeaderboardState() {
  if (distanceStatsWidget.classList.contains('collapsed')) {
    localStorage.setItem('leaderboardState', 'collapsed');
  } else if (distanceStatsWidget.classList.contains('expanded')) {
    localStorage.setItem('leaderboardState', 'expanded');
  } else {
    localStorage.setItem('leaderboardState', 'minimal');
  }
}

// Restore leaderboard state from localStorage (default: minimal)
const savedLeaderboardState = localStorage.getItem('leaderboardState');
if (savedLeaderboardState === 'collapsed') {
  distanceStatsWidget.classList.add('collapsed');
} else if (savedLeaderboardState === 'expanded') {
  distanceStatsWidget.classList.add('expanded');
}
// If 'minimal' or no saved state, leave as-is (no class = minimal)

// Leaderboard states: collapsed (button only), minimal (basic table), expanded (all columns)
// Toggle button opens minimal view
if (leaderboardToggle) {
  leaderboardToggle.addEventListener('click', () => {
    distanceStatsWidget.classList.remove('collapsed');
    saveLeaderboardState();
  });
}

// Expand button toggles between minimal and expanded
if (leaderboardExpand) {
  leaderboardExpand.addEventListener('click', () => {
    distanceStatsWidget.classList.toggle('expanded');
    saveLeaderboardState();
  });
}

// Collapse button returns to collapsed state
if (leaderboardCollapse) {
  leaderboardCollapse.addEventListener('click', () => {
    distanceStatsWidget.classList.remove('expanded');
    distanceStatsWidget.classList.add('collapsed');
    saveLeaderboardState();
  });
}

// Parse HH:MM:SS strings (Condor task time) into seconds
function parseHHMMSSToSeconds(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') {
    return 0;
  }

  const parts = timeStr.trim().split(':').map(part => Number.parseInt(part, 10));
  if (parts.some(Number.isNaN)) {
    return 0;
  }

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }

  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  }

  return 0;
}

// Extract a position timestamp in seconds (supports multiple field names)
function resolvePositionTimeSeconds(position) {
  if (!position) {
    return null;
  }

  const candidates = [
    position.timestamp,
    position.time_ms,
    position.time_epoch_ms,
    position.timeEpochMs,
    position.time
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) {
      continue;
    }
    if (candidate instanceof Date) {
      return candidate.getTime() / 1000;
    }
    if (typeof candidate === 'number') {
      return candidate > 1e12 ? candidate / 1000 : candidate;
    }
    if (typeof candidate === 'string') {
      // Try ISO date string first (e.g. "2026-03-01T20:57:00.000Z")
      // Must check before parseFloat because parseFloat("2026-03-01T...") returns 2026 (the year)
      const dateMs = Date.parse(candidate);
      if (Number.isFinite(dateMs) && dateMs > 1e12) {
        return dateMs / 1000;
      }
      const numeric = Number.parseFloat(candidate);
      if (Number.isFinite(numeric)) {
        return numeric > 1e12 ? numeric / 1000 : numeric;
      }
      const hhmmss = parseHHMMSSToSeconds(candidate);
      if (hhmmss > 0) {
        return hhmmss;
      }
    }
  }

  // Fallback to wall-clock time in live mode if no timestamp is provided
  if (currentMode === 'live') {
    return Date.now() / 1000;
  }

  return null;
}

// Clear ETF tracking state (used when switching tasks/modes)
function resetEtfTracking() {
  etfHistory.clear();
  etfDeltaByAircraft.clear();
  etfDeltaByAircraftB.clear();
  sampledEtfDeltasA.clear();
  sampledEtfDeltasB.clear();
  etfAltSourceByAircraft.clear();
  etfStartActivationByAircraft.clear();
  lastEtfComputationSignature = null;
  cookieRegistry.clear();
  positionNoiseFilter.clear();
}

function clearEtfTrackingForCookie(cookie) {
  if (!cookie) {
    return;
  }
  etfHistory.delete(cookie);
  etfDeltaByAircraft.delete(cookie);
  etfDeltaByAircraftB.delete(cookie);
  sampledEtfDeltasA.delete(cookie);
  sampledEtfDeltasB.delete(cookie);
  etfAltSourceByAircraft.delete(cookie);
}

function clearEtfDerivedState(options = {}) {
  const { clearStartActivation = false } = options;
  etfHistory.clear();
  etfDeltaByAircraft.clear();
  etfDeltaByAircraftB.clear();
  sampledEtfDeltasA.clear();
  sampledEtfDeltasB.clear();
  etfAltSourceByAircraft.clear();
  if (clearStartActivation) {
    etfStartActivationByAircraft.clear();
  }
}

function buildEtfComputationSignature(task) {
  const normalizeNumber = (value, digits = 2) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Number(numeric.toFixed(digits)) : null;
  };

  return JSON.stringify({
    taskId: task?.id ?? task?.ID ?? task?.task_id ?? task?.task_name ?? null,
    taskType: task?.task_type ?? null,
    finishAlt_m: normalizeNumber(getFinishAltitudeMeters(task), 1),
    aatTimeHours: normalizeNumber(task?.aat_time_hours, 3),
    maccready: normalizeNumber(config.maccready, 2),
    netto: normalizeNumber(config.netto, 2),
    wingLoading: normalizeNumber(config.wingLoading, 2),
    startOverrideIso: config.startTimeOverrideIso || null,
    trendWindowSec: Math.round(Number(config.trendWindowSec) || 0),
    trendWindowBSec: Math.round(Number(config.trendWindowBSec) || 0),
    overlayXcPerfAUpdateSec: Math.round(Number(config.overlayXcPerfAUpdateSec) || 0),
    overlayXcPerfBUpdateSec: Math.round(Number(config.overlayXcPerfBUpdateSec) || 0)
  });
}

function syncEtfComputationState(task, options = {}) {
  const {
    clearStartActivation = false,
    triggerReplayBackfill = false,
    triggerImmediateRefresh = false
  } = options;
  const signature = buildEtfComputationSignature(task);

  if (signature === lastEtfComputationSignature) {
    return false;
  }

  lastEtfComputationSignature = signature;
  clearEtfDerivedState({ clearStartActivation });

  if (triggerReplayBackfill && (currentMode === 'replay' || currentMode === 'igc' || replayManager?.isLiveMode)) {
    lastSeekRealTime = performance.now();
  }

  if (triggerImmediateRefresh) {
    queueMicrotask(() => {
      updateDistanceStatsWidget._lastRefresh = 0;
      updateDistanceStatsWidget();
    });
  }

  return true;
}

function notifyEtfInputChange(options = {}) {
  const task = resolveSelectedTaskObject();
  syncEtfComputationState(task, {
    clearStartActivation: false,
    triggerReplayBackfill: true,
    triggerImmediateRefresh: true,
    ...options
  });
}

function estimateReplayTrendSampleAtTime(cookie, targetTimeSeconds, task, finishAlt_m) {
  if (!cookie || !Number.isFinite(targetTimeSeconds) || !task || !replayManager?.flights?.length) {
    return null;
  }

  const flight = replayManager.flights.find(f => f?.positions?.[0]?.cookie === cookie);
  if (!flight || !Array.isArray(flight.timestamps) || flight.timestamps.length === 0) {
    return null;
  }

  const targetMs = targetTimeSeconds * 1000;
  const flightStartMs = flight.timestamps[0].getTime();
  const flightEndMs = flight.timestamps[flight.timestamps.length - 1].getTime();
  if (targetMs < flightStartMs || targetMs > flightEndMs) {
    return null;
  }

  // Use ReplayManager interpolation without mutating the live flight cursor.
  const probeFlight = {
    timestamps: flight.timestamps,
    positions: flight.positions,
    currentIndex: 0
  };
  const targetDate = new Date(targetMs);
  const targetPosition = replayManager.getPositionAtTime(probeFlight, targetDate);
  if (!targetPosition) {
    return null;
  }

  const states = aircraftStateTracker.getAllStates();
  const savedState = states.get(cookie) || null;

  try {
    if (aircraftStateTracker.precomputedEvents?.has(cookie)) {
      aircraftStateTracker.reconstructStateFromEvents(cookie, targetDate, task, targetPosition);
    } else {
      const maxStartAltitude_m = task?.max_start_altitude_m || 999999;
      aircraftStateTracker.rebuildState(
        cookie,
        flight.positions,
        targetDate,
        task,
        maxStartAltitude_m
      );
    }

    const replayState = states.get(cookie);
    if (!replayState || replayState.raceStatus !== 'racing') {
      return null;
    }

    // Require minimum elapsed time after start before backfilling baselines.
    // ETA/distance projections are unreliable in the first seconds after crossing
    // the start line, and using them as baselines creates stale-looking deltas.
    const MIN_ELAPSED_FOR_BACKFILL = 30; // seconds
    if (!Number.isFinite(replayState.elapsedTime_s) || replayState.elapsedTime_s < MIN_ELAPSED_FOR_BACKFILL) {
      return null;
    }

    const gliderType = targetPosition.aircraft_type || targetPosition.aircraft || targetPosition.id_model || null;
    if (!gliderType || !globalPolarManager.hasPolar(gliderType)) {
      return null;
    }

    // Compute TEC altitude inline — the underlying flight positions don't have
    // tec_m because it's only set on ephemeral interpolated positions during
    // animate().  Without this, the backfilled baseline uses raw altitude while
    // the forward path uses TEC, creating a systematic bias in XC Pace deltas.
    const rawAlt = parseFloat(targetPosition.altitude_m) || 0;
    const speedKmh = parseFloat(targetPosition.speed_mps) * 3.6;
    const currentAlt_m = (Number.isFinite(rawAlt) && Number.isFinite(speedKmh) && speedKmh > 0)
      ? calculateTEC(rawAlt, speedKmh)
      : rawAlt;
    const netTaskLength_km = task?.netTaskLength;
    const realDistance_km = replayState.realDistance_km ?? 0;
    let realRemainingDistance_km =
      Number.isFinite(replayState.realRemainingDistance_km)
        ? Math.max(0, replayState.realRemainingDistance_km)
        : typeof netTaskLength_km === 'number' && netTaskLength_km > 0
          ? Math.max(0, netTaskLength_km - realDistance_km)
          : null;
    const taskLength_km = task?.scoringTaskLength || 0;
    let officialRemainingDistance_km = Number.isFinite(replayState.officialRemainingDistance_km)
      ? Math.max(0, replayState.officialRemainingDistance_km)
      : Math.max(0, taskLength_km - (replayState.officialDistance_km || 0));

    if (task?.task_type === 'AAT') {
      const aatBounds = aircraftStateTracker.getAatRemainingDistanceBounds(targetPosition, task);
      const minimumRemainingDistance_km = Number.isFinite(aatBounds?.minimumRemainingDistance_km)
        ? Math.max(0, aatBounds.minimumRemainingDistance_km)
        : null;
      const maximumRemainingDistance_km = Number.isFinite(aatBounds?.maximumRemainingDistance_km)
        ? Math.max(minimumRemainingDistance_km ?? 0, aatBounds.maximumRemainingDistance_km)
        : null;

      if (Number.isFinite(minimumRemainingDistance_km)) {
        realRemainingDistance_km = minimumRemainingDistance_km;
        officialRemainingDistance_km = minimumRemainingDistance_km;
      }

      if (
        Number.isFinite(task?.aat_time_hours) &&
        Number.isFinite(replayState.elapsedTime_s) &&
        gliderType &&
        globalPolarManager.hasPolar(gliderType)
      ) {
        const aatTimeRemaining_min = (task.aat_time_hours * 60) - (replayState.elapsedTime_s / 60);

        if (aatTimeRemaining_min > 0.01) {
          const effectiveWingLoading = getEffectiveWingLoading(gliderType, task, { cookie });
          const aatRemainingResult = calculateDistanceForTime(
            currentAlt_m,
            finishAlt_m,
            aatTimeRemaining_min,
            config.maccready,
            config.netto,
            globalPolarManager,
            gliderType,
            effectiveWingLoading,
            300
          );

          if (Number.isFinite(aatRemainingResult?.remainingDistance_km)) {
            let projectedRemainingDistance_km = Math.max(0, aatRemainingResult.remainingDistance_km);

            if (Number.isFinite(minimumRemainingDistance_km)) {
              projectedRemainingDistance_km = Math.max(
                minimumRemainingDistance_km,
                projectedRemainingDistance_km
              );
            }

            if (Number.isFinite(maximumRemainingDistance_km)) {
              projectedRemainingDistance_km = Math.min(
                projectedRemainingDistance_km,
                Math.max(minimumRemainingDistance_km ?? 0, maximumRemainingDistance_km)
              );
            }

            realRemainingDistance_km = projectedRemainingDistance_km;
            officialRemainingDistance_km = projectedRemainingDistance_km;
          }
        }
      }
    }

    const remainingDistance_km = realRemainingDistance_km ?? officialRemainingDistance_km;
    if (!(remainingDistance_km >= 0)) {
      return null;
    }

    const NEAR_FINISH_DISTANCE_KM = 0.05;
    let etaRemainingMinutes = null;

    if (remainingDistance_km > NEAR_FINISH_DISTANCE_KM) {
      const effectiveWingLoading = getEffectiveWingLoading(gliderType, task, { cookie });
      const etaResult = calculateETA(
        currentAlt_m,
        finishAlt_m,
        remainingDistance_km,
        config.maccready,
        config.netto,
        globalPolarManager,
        gliderType,
        effectiveWingLoading,
        300,
        null
      );
      etaRemainingMinutes = Number.isFinite(etaResult?.etfTotal_min) ? etaResult.etfTotal_min : null;
    } else {
      etaRemainingMinutes = 0;
    }

    const projectedAatMetrics = task?.task_type === 'AAT'
      ? calculateProjectedAatMetrics({
        distanceFlown_km: replayState.realDistance_km ?? replayState.officialDistance_km ?? 0,
        remainingDistance_km,
        elapsedMinutes: Number.isFinite(replayState.elapsedTime_s) ? replayState.elapsedTime_s / 60 : null,
        etaRemainingMinutes,
        aatTimeHours: task.aat_time_hours
      })
      : null;

    return buildTrendHistorySample(task, etaRemainingMinutes, projectedAatMetrics);
  } finally {
    if (savedState) {
      states.set(cookie, savedState);
    } else {
      states.delete(cookie);
    }
  }
}

// Incremental backfill work queue to spread expensive operations across frames
const replayBackfillQueue = [];
let isProcessingBackfillQueue = false;
const BACKFILL_WORK_ITEMS_PER_FRAME = 2; // Process 2 aircraft per frame to avoid lag spikes

function queueReplayBackfillWork(cookie, positionTimeSeconds, trendWindowSec, task, finishAlt_m) {
  // Deduplicate: check if this exact work is already queued
  const isDuplicate = replayBackfillQueue.some(
    item => item.cookie === cookie &&
            item.positionTimeSeconds === positionTimeSeconds &&
            item.trendWindowSec === trendWindowSec
  );

  if (!isDuplicate) {
    replayBackfillQueue.push({ cookie, positionTimeSeconds, trendWindowSec, task, finishAlt_m });
  }

  // Start processing queue if not already running
  if (!isProcessingBackfillQueue) {
    processReplayBackfillQueue();
  }
}

function processReplayBackfillQueue() {
  if (replayBackfillQueue.length === 0) {
    isProcessingBackfillQueue = false;
    return;
  }

  isProcessingBackfillQueue = true;

  // Process a small batch per frame
  const batchSize = Math.min(BACKFILL_WORK_ITEMS_PER_FRAME, replayBackfillQueue.length);
  for (let i = 0; i < batchSize; i++) {
    const work = replayBackfillQueue.shift();
    if (work) {
      backfillReplayEtfSample(
        work.cookie,
        work.positionTimeSeconds,
        work.trendWindowSec,
        work.task,
        work.finishAlt_m
      );
    }
  }

  // Schedule next batch
  requestAnimationFrame(processReplayBackfillQueue);
}

function backfillReplayEtfSample(cookie, positionTimeSeconds, trendWindowSec, task, finishAlt_m) {
  if (
    !cookie ||
    !Number.isFinite(positionTimeSeconds) ||
    !Number.isFinite(trendWindowSec) ||
    !task ||
    !(currentMode === 'replay' || currentMode === 'igc' || replayManager?.isLiveMode)
  ) {
    return;
  }

  const targetTime = positionTimeSeconds - trendWindowSec;
  if (!Number.isFinite(targetTime)) {
    return;
  }

  const history = etfHistory.get(cookie);
  let baseline = null;
  if (history && history.length > 0) {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].timeSec <= targetTime) {
        baseline = history[i];
        break;
      }
    }
  }

  if (baseline && (targetTime - baseline.timeSec) <= ETF_BASELINE_MAX_DRIFT_SEC) {
    return;
  }

  const replayTrendSample = estimateReplayTrendSampleAtTime(cookie, targetTime, task, finishAlt_m);
  if (replayTrendSample) {
    insertHistoricalTrendSample(cookie, replayTrendSample, targetTime);
  }
}

function insertHistoricalTrendSample(cookie, historySample, positionTimeSeconds) {
  if (!cookie || !historySample || !Number.isFinite(positionTimeSeconds)) {
    return;
  }
  let history = etfHistory.get(cookie);
  if (!history) {
    history = [];
    etfHistory.set(cookie, history);
  }

  // Keep history sorted while allowing older replay-derived baselines to be inserted.
  let insertIdx = history.length;
  while (insertIdx > 0 && history[insertIdx - 1].timeSec > positionTimeSeconds) {
    insertIdx--;
  }

  const EPSILON_SEC = 0.25;
  if (insertIdx > 0 && Math.abs(history[insertIdx - 1].timeSec - positionTimeSeconds) <= EPSILON_SEC) {
    history[insertIdx - 1] = { timeSec: positionTimeSeconds, ...historySample };
  } else if (insertIdx < history.length && Math.abs(history[insertIdx].timeSec - positionTimeSeconds) <= EPSILON_SEC) {
    history[insertIdx] = { timeSec: positionTimeSeconds, ...historySample };
  } else {
    history.splice(insertIdx, 0, { timeSec: positionTimeSeconds, ...historySample });
  }

  const newestTimeSec = history[history.length - 1]?.timeSec ?? positionTimeSeconds;
  const cutoff = newestTimeSec - ETF_HISTORY_RETENTION_SEC;
  while (history.length && history[0].timeSec < cutoff) {
    history.shift();
  }
}

// Record a trend sample for an aircraft
function recordTrendSample(cookie, historySample, positionTimeSeconds) {
  if (!cookie || !historySample || !Number.isFinite(positionTimeSeconds)) {
    return;
  }
  let history = etfHistory.get(cookie);

  if (!history) {
    history = [];
    etfHistory.set(cookie, history);
  }

  const last = history[history.length - 1];
  if (last) {
    const gap = positionTimeSeconds - last.timeSec;
    if (gap < -1) {
      // Scrubbed backward - drop future samples, keep prior buffer
      history = history.filter(sample => sample.timeSec <= positionTimeSeconds);
      etfHistory.set(cookie, history);
    } else if (gap > 10 && currentMode === 'live' && !replayManager?.isLiveMode) {
      // In direct live mode, a large timestamp jump usually means stale/invalid continuity
      // (clock/source discontinuity), so reset.
      // In replay/PVR, keep history so XC Pace can be computed instantly after scrubs/seeks.
      history.length = 0;
    }
  }

  history.push({ timeSec: positionTimeSeconds, ...historySample });

  // Trim old samples beyond retention window
  const cutoff = positionTimeSeconds - ETF_HISTORY_RETENTION_SEC;
  while (history.length && history[0].timeSec < cutoff) {
    history.shift();
  }
}

// Calculate trend delta for an aircraft using the configured trend window
function calculateTrendDelta(cookie, currentHistorySample, positionTimeSeconds, trendWindowSec) {
  if (!cookie || !currentHistorySample || !Number.isFinite(positionTimeSeconds) || !Number.isFinite(trendWindowSec)) {
    return null;
  }

  const history = etfHistory.get(cookie);
  if (!history || history.length === 0) {
    return null;
  }

  const targetTime = positionTimeSeconds - trendWindowSec;
  let baseline = null;

  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].timeSec <= targetTime) {
      baseline = history[i];
      break;
    }
  }

  if (!baseline && history.length > 0) {
    // Not enough history to cover the full trend window; use earliest sample.
    // But require a minimum elapsed time so that tiny windows (first few seconds
    // of playback) don't amplify normal ETF noise into wild deltas.
    baseline = history[0];
    const earlyElapsed = positionTimeSeconds - baseline.timeSec;
    const MIN_ELAPSED_FOR_EARLY_DELTA = 30; // seconds
    if (!Number.isFinite(earlyElapsed) || earlyElapsed < MIN_ELAPSED_FOR_EARLY_DELTA) {
      return null;
    }
  }

  if (!baseline) {
    return null;
  }

  const elapsedSeconds = positionTimeSeconds - baseline.timeSec;
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds < 0) {
    return null;
  }
  if (elapsedSeconds > trendWindowSec + ETF_BASELINE_MAX_DRIFT_SEC) {
    // Baseline is stale (common after replay seeks); reject instead of showing huge bogus values.
    return null;
  }

  if (currentHistorySample.sampleType === 'aat' && baseline.sampleType === 'aat') {
    return calculateAatTrendDeltaSeconds(currentHistorySample, baseline);
  }

  if (currentHistorySample.sampleType !== 'racing' || baseline.sampleType !== 'racing') {
    return null;
  }

  if (!Number.isFinite(currentHistorySample.etfSeconds) || !Number.isFinite(baseline.etfSeconds)) {
    return null;
  }

  return elapsedSeconds + currentHistorySample.etfSeconds - baseline.etfSeconds;
}

function formatDeltaMinutes(deltaMinutes) {
  if (!Number.isFinite(deltaMinutes)) {
    return '--:--';
  }

  if (Math.abs(deltaMinutes) < 0.01) {
    return 'Leader';
  }

  const sign = deltaMinutes > 0 ? '+' : '-';
  return `${sign}${formatTimeMMSS(Math.abs(deltaMinutes))}`;
}

function formatSignedSeconds(deltaSeconds) {
  if (!Number.isFinite(deltaSeconds)) {
    return '--';
  }
  const rounded = Math.round(deltaSeconds);
  return `${rounded > 0 ? '+' : ''}${rounded}s`;
}

function formatWindowMinutesLabel(seconds) {
  const totalMinutes = Number(seconds) / 60;
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return '0min';
  }
  const roundedToTenth = Math.round(totalMinutes * 10) / 10;
  const hasDecimal = Math.abs(roundedToTenth - Math.round(roundedToTenth)) > 1e-6;
  const formatted = hasDecimal ? roundedToTenth.toFixed(1) : roundedToTenth.toFixed(0);
  return `${formatted}min`;
}

function formatFinishAltDelta(valueMeters, units = 'metric') {
  if (!Number.isFinite(valueMeters)) {
    return '--';
  }

  const scaledValue = units === 'imperial'
    ? Math.round(valueMeters * 3.28084)
    : Math.round(valueMeters);
  const unit = units === 'imperial' ? 'ft' : 'm';
  const sign = scaledValue > 0 ? '+' : '';
  return `${sign}${scaledValue}${unit}`;
}

function getLinkedColorSchemeForInfoLabel(infoLabel) {
  const normalized = String(infoLabel || '').trim();
  if (normalized === 'id') {
    return 'aircraft';
  }
  return LINKABLE_COLOR_SCHEMES.has(normalized) ? normalized : 'aircraft';
}

function getLinkedInfoLabelForColorScheme(colorScheme) {
  const normalized = String(colorScheme || '').trim();
  return normalized === 'aircraft' ? 'id' : normalized;
}

function formatDebugFixed(value, digits = 2, fallback = 'n/a') {
  return Number.isFinite(value) ? value.toFixed(digits) : fallback;
}

function calculateProjectedAatMetrics({
  distanceFlown_km,
  remainingDistance_km,
  elapsedMinutes,
  etaRemainingMinutes,
  aatTimeHours
}) {
  if (!Number.isFinite(distanceFlown_km) || !Number.isFinite(elapsedMinutes) || elapsedMinutes <= 0) {
    return null;
  }

  const sanitizedRemainingDistance_km =
    Number.isFinite(remainingDistance_km) && remainingDistance_km > 0
      ? remainingDistance_km
      : 0;
  const aatMinimumRemainingMinutes =
    Number.isFinite(aatTimeHours) && aatTimeHours > 0
      ? Math.max(0, (aatTimeHours * 60) - elapsedMinutes)
      : 0;
  const extraMinutes =
    Number.isFinite(etaRemainingMinutes) && etaRemainingMinutes > aatMinimumRemainingMinutes
      ? etaRemainingMinutes - aatMinimumRemainingMinutes
      : 0;
  const projectedElapsedMinutes = elapsedMinutes + aatMinimumRemainingMinutes + extraMinutes;
  const projectedDistance_km = Math.max(0, distanceFlown_km + sanitizedRemainingDistance_km);

  if (!Number.isFinite(projectedElapsedMinutes) || projectedElapsedMinutes <= 0 || projectedDistance_km <= 0) {
    return null;
  }

  const projectedSpeedKmh = projectedDistance_km / (projectedElapsedMinutes / 60);

  // Unclamped projection: use raw ETA remaining without min-time floor.
  // This reflects actual flying performance even when min-time caps the result.
  const unclampedRemainingMinutes =
    Number.isFinite(etaRemainingMinutes) && etaRemainingMinutes > 0
      ? etaRemainingMinutes
      : 0;
  const unclampedElapsedMinutes = elapsedMinutes + unclampedRemainingMinutes;
  const unclampedSpeedKmh =
    unclampedElapsedMinutes > 0
      ? projectedDistance_km / (unclampedElapsedMinutes / 60)
      : null;

  return {
    projectedFinalDistance_km: projectedDistance_km,
    projectedFinalElapsedMinutes: projectedElapsedMinutes,
    projectedFinalSpeedKmh: projectedSpeedKmh,
    projectedAatMinimumRemainingMinutes: aatMinimumRemainingMinutes,
    projectedAatExtraMinutes: extraMinutes,
    unclampedFinalElapsedMinutes: unclampedElapsedMinutes,
    unclampedFinalSpeedKmh: unclampedSpeedKmh
  };
}

function buildTrendHistorySample(task, etaRemainingMinutes, projectedAatMetrics = null) {
  if (task?.task_type === 'AAT') {
    if (!Number.isFinite(etaRemainingMinutes)) {
      return null;
    }

    const projectedDistance_km = projectedAatMetrics?.projectedFinalDistance_km;
    // Use unclamped speed for XC Pace trends so performance changes remain
    // visible even when min-time caps the scored result.
    const projectedSpeedKmh = projectedAatMetrics?.unclampedFinalSpeedKmh
      ?? projectedAatMetrics?.projectedFinalSpeedKmh;

    if (
      !Number.isFinite(projectedDistance_km) ||
      projectedDistance_km <= 0 ||
      !Number.isFinite(projectedSpeedKmh) ||
      projectedSpeedKmh <= 0
    ) {
      return null;
    }

    return {
      sampleType: 'aat',
      projectedDistance_km,
      projectedSpeedKmh
    };
  }

  if (!Number.isFinite(etaRemainingMinutes)) {
    return null;
  }

  return {
    sampleType: 'racing',
    etfSeconds: etaRemainingMinutes * 60
  };
}

function calculateAatTrendDeltaSeconds(currentSample, baselineSample) {
  const currentDistance_km = currentSample?.projectedDistance_km;
  const currentSpeedKmh = currentSample?.projectedSpeedKmh;
  const baselineSpeedKmh = baselineSample?.projectedSpeedKmh;

  if (
    !Number.isFinite(currentDistance_km) ||
    currentDistance_km <= 0 ||
    !Number.isFinite(currentSpeedKmh) ||
    currentSpeedKmh <= 0 ||
    !Number.isFinite(baselineSpeedKmh) ||
    baselineSpeedKmh <= 0
  ) {
    return null;
  }

  // Convert the baseline and current projected average speeds onto the
  // CURRENT projected task distance so AAT pace reflects result-speed changes,
  // not raw ETA/min-time plateaus.
  const currentEquivalentSeconds = (currentDistance_km / currentSpeedKmh) * 3600;
  const baselineEquivalentSeconds = (currentDistance_km / baselineSpeedKmh) * 3600;

  return currentEquivalentSeconds - baselineEquivalentSeconds;
}

function applyAatProjectedSpeedDeltas(entries, task, persistHandicapDelta = null, shouldLogDebug = false) {
  const useHandicapDistance = isHandicapTaskClass(task);
  const getComparisonDistance = (entry) => {
    const baseDistance = entry?.projectedFinalDistance_km;
    if (!Number.isFinite(baseDistance) || baseDistance <= 0) {
      return null;
    }

    if (!useHandicapDistance) {
      return baseDistance;
    }

    const handicap = getConfiguredHandicap(entry?.gliderType, task);
    if (!Number.isFinite(handicap) || handicap <= 0) {
      return null;
    }

    // Apply handicap only to the internal AAT delta normalization distance.
    return baseDistance * (100 / handicap);
  };

  const getComparisonSpeed = (entry) => {
    const comparisonDistance = getComparisonDistance(entry);
    const projectedElapsedMinutes = entry?.projectedFinalElapsedMinutes;

    if (
      !Number.isFinite(comparisonDistance) ||
      comparisonDistance <= 0 ||
      !Number.isFinite(projectedElapsedMinutes) ||
      projectedElapsedMinutes <= 0
    ) {
      return null;
    }

    return comparisonDistance / (projectedElapsedMinutes / 60);
  };

  const getUnclampedComparisonSpeed = (entry) => {
    const comparisonDistance = getComparisonDistance(entry);
    const unclampedElapsedMinutes = entry?.unclampedFinalElapsedMinutes;

    if (
      !Number.isFinite(comparisonDistance) ||
      comparisonDistance <= 0 ||
      !Number.isFinite(unclampedElapsedMinutes) ||
      unclampedElapsedMinutes <= 0
    ) {
      return null;
    }

    return comparisonDistance / (unclampedElapsedMinutes / 60);
  };

  const eligibleEntries = entries.filter(entry => {
    if (entry?.isDnf) {
      return false;
    }

    const speed = getComparisonSpeed(entry);
    const comparisonDistance = getComparisonDistance(entry);
    return (
      Number.isFinite(speed) &&
      Number.isFinite(comparisonDistance) &&
      comparisonDistance > 0
    );
  });

  if (eligibleEntries.length === 0) {
    return;
  }

  let leaderEntry = null;
  let leaderSpeedKmh = null;

  eligibleEntries.forEach(entry => {
    const speed = getComparisonSpeed(entry);
    if (!Number.isFinite(speed)) {
      return;
    }

    if (!leaderEntry || speed > leaderSpeedKmh + 1e-6) {
      leaderEntry = entry;
      leaderSpeedKmh = speed;
    }
  });

  if (!leaderEntry || !Number.isFinite(leaderSpeedKmh) || leaderSpeedKmh <= 0) {
    return;
  }

  // Normalize deltas to AAT min time instead of leader's projected distance.
  // This gives stable, meaningful gaps: "how many seconds behind over min-time?"
  const aatMinTimeHours = Number.isFinite(task?.aat_time_hours) && task.aat_time_hours > 0
    ? task.aat_time_hours
    : null;

  if (!aatMinTimeHours) {
    return;
  }

  // Reference distance = what the leader would cover in min-time at their speed
  const referenceDistance_km = leaderSpeedKmh * aatMinTimeHours;
  const leaderReferenceTimeMinutes = aatMinTimeHours * 60;

  // Find unclamped leader (fastest without min-time floor)
  let unclampedLeaderSpeedKmh = null;
  eligibleEntries.forEach(entry => {
    const speed = getUnclampedComparisonSpeed(entry);
    if (Number.isFinite(speed) && (unclampedLeaderSpeedKmh === null || speed > unclampedLeaderSpeedKmh + 1e-6)) {
      unclampedLeaderSpeedKmh = speed;
    }
  });

  eligibleEntries.forEach(entry => {
    const comparisonSpeedKmh = getComparisonSpeed(entry);

    if (!Number.isFinite(comparisonSpeedKmh) || comparisonSpeedKmh <= 0) {
      return;
    }

    // Time this pilot would need to cover the reference distance at their speed
    const entryReferenceTimeMinutes = (referenceDistance_km / comparisonSpeedKmh) * 60;
    const deltaMinutes = Math.max(0, entryReferenceTimeMinutes - leaderReferenceTimeMinutes);

    entry.deltaToLeaderMinutes = deltaMinutes;
    entry.deltaToLeaderStr = deltaMinutes < 1e-6 ? 'Leader' : formatDeltaMinutes(deltaMinutes);

    // Unclamped delta (no AAT min-time enforcement)
    const unclampedSpeedKmh = getUnclampedComparisonSpeed(entry);
    if (
      Number.isFinite(unclampedSpeedKmh) && unclampedSpeedKmh > 0 &&
      Number.isFinite(unclampedLeaderSpeedKmh) && unclampedLeaderSpeedKmh > 0
    ) {
      const unclampedRefDist = unclampedLeaderSpeedKmh * aatMinTimeHours;
      const unclampedEntryTime = (unclampedRefDist / unclampedSpeedKmh) * 60;
      const unclampedLeaderTime = aatMinTimeHours * 60;
      const unclampedDelta = Math.max(0, unclampedEntryTime - unclampedLeaderTime);
      entry.deltaNoMinMinutes = unclampedDelta;
      entry.deltaNoMinStr = unclampedDelta < 1e-6 ? 'Leader' : formatDeltaMinutes(unclampedDelta);
    }

    if (entry.isFinished) {
      entry.hasFrozenDelta = true;
    }

    if (useHandicapDistance) {
      entry.deltaToLeaderMinutesHandicap = deltaMinutes;
      if (persistHandicapDelta && entry.cookie) {
        persistHandicapDelta(entry.cookie, deltaMinutes);
      }
    }

    if (entry._debugEntry) {
      entry._debugEntry.projectedAatSpeed = entry.projectedFinalSpeedKmh?.toFixed(1) ?? 'n/a';
      entry._debugEntry.projectedAatRefDistance = referenceDistance_km.toFixed(2);
      entry._debugEntry.deltaLeader = entry.deltaToLeaderStr;
    }
  });

  if (shouldLogDebug) {
    const leaderLabel = useHandicapDistance
      ? `${leaderEntry.cn} @ ${leaderSpeedKmh.toFixed(1)} km/h (ref ${referenceDistance_km.toFixed(1)} km over ${aatMinTimeHours}h)`
      : `${leaderEntry.cn} @ ${leaderSpeedKmh.toFixed(1)} km/h (ref ${referenceDistance_km.toFixed(1)} km over ${aatMinTimeHours}h)`;
    console.log(`AAT projected speed leader: ${leaderLabel}`);
  }
}

function parseAltitudeValue(value, unit = 'm') {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const num = Number.parseFloat(value);
  if (!Number.isFinite(num)) {
    return null;
  }
  if (unit === 'ft') {
    return num * 0.3048;
  }
  return num;
}

function getFinishAltitudeMeters(task) {
  if (!task?.turnpoints?.length) {
    return 0;
  }

  const finishTP = task.turnpoints[task.turnpoints.length - 1];

  // Candidates for the finish altitude — order matters (most specific first).
  // IMPORTANT: MaxAltMeters is the OZ cylinder ceiling (often 10000m default),
  // NOT the finish altitude.  Do NOT include it here.
  const candidates = [
    { value: finishTP?.finish_altitude_m, unit: 'm' },
    { value: finishTP?.finishAltitude_m, unit: 'm' },
    { value: finishTP?.finish_altitude_ft, unit: 'ft' },
    { value: finishTP?.FinishAltitude_m, unit: 'm' },
    { value: finishTP?.FinishAltitude_ft, unit: 'ft' },
    { value: task?.finish_altitude_m, unit: 'm' },
    { value: task?.min_finish_altitude_m, unit: 'm' },
    { value: task?.max_finish_altitude_m, unit: 'm' },
    { value: task?.finish_altitude_ft, unit: 'ft' },
    { value: finishTP?.MinAltMeters, unit: 'm' },
    // MaxAltMeters intentionally excluded — it's the OZ ceiling, not finish altitude
    { value: finishTP?.altitude_m, unit: 'm' },
    { value: finishTP?.AltitudeMeters, unit: 'm' },
    { value: finishTP?.Altitude, unit: 'm' }
    // finishTP?.altitude with unit 'ft' removed — ambiguous and likely meters
  ];

  for (const candidate of candidates) {
    const meters = parseAltitudeValue(candidate.value, candidate.unit);
    if (meters !== null && meters > 0.1) {
      return meters;
    }
  }

  return 0;
}


// Function to update distance stats widget
function updateDistanceStatsWidget() {
  if (!distanceStatsBody || !distanceStatsWidget) {
    return;
  }

  const isReplayLikeMode = currentMode === 'replay' || currentMode === 'igc' || replayManager?.isLiveMode;
  let shouldRenderReplayUi = true;
  const task = resolveSelectedTaskObject();
  if (!task) {
    resetEtfTracking();
    aircraftStateTracker.getAllStates().forEach((state) => {
      if (state?.lastPosition) {
        state.lastPosition.etfDeltaSeconds = null;
      }
    });
    distanceStatsWidget.classList.remove('aat-task');
    distanceStatsWidget.classList.remove('show-glider-minimal');
    try { localStorage.setItem('condormap-leaderboard', JSON.stringify({ timestamp: Date.now(), entries: [] })); } catch (_) { }
    return;
  }

  const useHandicapDelta = isHandicapTaskClass(task);
  distanceStatsWidget.classList.toggle('aat-task', task?.task_type === 'AAT');
  distanceStatsWidget.classList.toggle('show-glider-minimal', useHandicapDelta);

  if (isReplayLikeMode) {
    const refreshIntervalMs = 1000 / Math.max(0.1, config.replayRefreshHz);
    const replayUiNow = performance.now();
    shouldRenderReplayUi =
      replayUiNow - (updateDistanceStatsWidget._lastRefresh || 0) >= refreshIntervalMs;
    if (shouldRenderReplayUi) {
      updateDistanceStatsWidget._lastRefresh = replayUiNow;
    }
  } else {
    // Throttle live-mode stats to 1 Hz to avoid excessive calculation per WebSocket message
    const liveUiNow = performance.now();
    const liveElapsed = liveUiNow - (updateDistanceStatsWidget._lastRefresh || 0);
    if (liveElapsed < 1000) {
      return; // Skip — less than 1 second since last live stats update
    }
    updateDistanceStatsWidget._lastRefresh = liveUiNow;
  }

  syncEtfComputationState(task, {
    clearStartActivation: false,
    triggerReplayBackfill: false,
    triggerImmediateRefresh: false
  });

  const trendWindowSeconds = Math.max(30, Math.min(600, Number(config.trendWindowSec) || 180));
  const trendWindowSecondsB = Math.max(30, Math.min(3600, Number(config.trendWindowBSec) || 300));

  // Update XC column headers
  const windowAHeader = document.getElementById('frozen-xc-header');
  if (windowAHeader) {
    const headerText = `XC A ${trendWindowSeconds}s`;
    if (windowAHeader.textContent !== headerText) windowAHeader.textContent = headerText;
  }
  const windowBHeader = document.getElementById('window-b-xc-header');
  if (windowBHeader) {
    const headerText = `XC B ${formatWindowMinutesLabel(trendWindowSecondsB)}`;
    if (windowBHeader.textContent !== headerText) windowBHeader.textContent = headerText;
  }

  const replayTimeMs =
    isReplayLikeMode && replayManager?.currentTime
      ? replayManager.currentTime.getTime()
      : null;
  const skipReplayEtfBackfill =
    replaySeekInProgress && isReplayLikeMode;
  const allowReplayTrendBackfill =
    isReplayLikeMode &&
    lastSeekRealTime > 0 &&
    (performance.now() - lastSeekRealTime) < REPLAY_TREND_BACKFILL_WINDOW_MS;

  // Update leaderboard header based on task class (visibility, rank, CN, glider, ETA Δ)
  const etfDeltaHeader = document.querySelector('#distance-stats-widget th.col-delta-header');
  if (etfDeltaHeader) {
    // Only update the text node, not the entire content (preserve the expand button)
    const textNode = Array.from(etfDeltaHeader.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
    if (textNode) {
      textNode.textContent = useHandicapDelta ? '\n              ETA Δ H\n              ' : '\n              ETA Δ\n              ';
    }
  }

  // Get all aircraft with state data
  const aircraftWithDistance = [];
  let latestSimTimeSec = null;

  // Throttle debug logging (only log every 10 seconds)
  const now = Date.now();
  const shouldLogDebug = !window.lastEtaDebugTime || (now - window.lastEtaDebugTime) > 10000;
  if (shouldLogDebug) {
    window.lastEtaDebugTime = now;
  }

  if (
    currentMode === 'live' &&
    task &&
    task.regatta_start !== true &&
    typeof aircraftStateTracker?.shouldTreatTaskAsRegattaFromStates === 'function' &&
    aircraftStateTracker.shouldTreatTaskAsRegattaFromStates(task)
  ) {
    task.regatta_start = true;
    updateTasksPanel();
  }

  // Get finish altitude from task (meters, with multiple field fallbacks)
  const finishAlt_m = getFinishAltitudeMeters(task);
  const isRegattaTask = task?.regatta_start === true;
  let regattaReferenceStartMs = null;
  if (isRegattaTask && typeof aircraftStateTracker.getRegattaReferenceStartTime === 'function') {
    regattaReferenceStartMs = aircraftStateTracker.getRegattaReferenceStartTime();
    if (!Number.isFinite(regattaReferenceStartMs) && typeof aircraftStateTracker.ensureRegattaReferenceFromStates === 'function') {
      regattaReferenceStartMs = aircraftStateTracker.ensureRegattaReferenceFromStates(task);
    }
  }

  const etaDebugData = [];
  const parseNumberOrNull = (value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const num = Number.parseFloat(value);
    return Number.isFinite(num) ? num : null;
  };
  const formatDebugClock = (seconds) => {
    if (!Number.isFinite(seconds)) return '--';
    const totalSeconds = Math.max(0, Math.round(seconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    return [hours, minutes, secs].map((part) => String(part).padStart(2, '0')).join(':');
  };
  const formatDebugDateTime = (timeMs) => {
    if (!Number.isFinite(timeMs)) return '--';
    const dt = new Date(timeMs);
    if (Number.isNaN(dt.getTime())) return '--';
    return dt.toISOString().replace('T', ' ').substring(0, 19);
  };

  const resolveXcSpeed = (position) => {
    if (!position) return null;
    const candidates = [
      position.achieved_xc_speed,
      position.achieved_xc_speed_kmh,
      position.xc_speed,
      position.xcSpeed,
      position.xc_speed_kmh,
      position.current_xc_speed,
      position.instant_xc_speed,
      position.xc_speed_avg
    ];
    for (const candidate of candidates) {
      const numeric = parseNumberOrNull(candidate);
      if (numeric !== null) {
        return numeric;
      }
    }
    return null;
  };

  const getEntryStartTimeMs = (entry) => {
    if (entry?.startTimeMs && Number.isFinite(entry.startTimeMs)) {
      return entry.startTimeMs;
    }
    if (Number.isFinite(entry?.finishTimeMs) && Number.isFinite(entry?.finishElapsedMinutes)) {
      return entry.finishTimeMs - entry.finishElapsedMinutes * 60000;
    }
    return null;
  };

  const getRegattaStartOffsetMinutes = (entry) => {
    if (!isRegattaTask || !Number.isFinite(regattaReferenceStartMs)) {
      return 0;
    }
    const entryStartMs = getEntryStartTimeMs(entry);
    if (!Number.isFinite(entryStartMs)) {
      return 0;
    }
    return Math.max(0, (entryStartMs - regattaReferenceStartMs) / 60000);
  };

  const getAbsoluteFinishMinutes = (entry) => {
    if (!Number.isFinite(entry?.finishElapsedMinutes)) {
      return null;
    }
    return entry.finishElapsedMinutes + getRegattaStartOffsetMinutes(entry);
  };

  const getAbsolutePredictedFinishMinutes = (entry) => {
    let baseMinutes = null;
    if (entry.status === 'finished') {
      baseMinutes = entry.finishElapsedMinutes ?? entry.predictedFinishMinutes;
    } else {
      baseMinutes = entry.predictedFinishMinutes;
    }

    if (!Number.isFinite(baseMinutes)) {
      return null;
    }

    if (!isRegattaTask || !Number.isFinite(regattaReferenceStartMs)) {
      return baseMinutes;
    }

    return baseMinutes + getRegattaStartOffsetMinutes(entry);
  };

  const persistHandicapDelta = (cookie, deltaMinutesHandicap) => {
    if (!cookie || !Number.isFinite(deltaMinutesHandicap)) {
      return;
    }

    const stateRef = aircraftStateTracker.getAllStates().get(cookie);
    if (stateRef) {
      stateRef.finishDeltaMinutesHandicap = deltaMinutesHandicap;
    }

    const finishInfo = aircraftStateTracker.getFinishedPilotInfo(cookie);
    if (finishInfo) {
      finishInfo.deltaMinutesHandicap = deltaMinutesHandicap;
    }
  };

  const resolveElapsedSeconds = (state, position) => {
    if (Number.isFinite(state.elapsedTime_s) && state.elapsedTime_s >= 0) {
      return state.elapsedTime_s;
    }
    if (typeof position?.time === 'string') {
      const parsed = parseHHMMSSToSeconds(position.time);
      return parsed > 0 ? parsed : null;
    }
    return null;
  };

  const replayPilotMetaByCookie = new Map();
  if (Array.isArray(replayManager?.flights)) {
    replayManager.flights.forEach((flight) => {
      const cookie = flight?.positions?.[0]?.cookie;
      if (!cookie || replayPilotMetaByCookie.has(cookie)) {
        return;
      }
      replayPilotMetaByCookie.set(cookie, {
        cn:
          flight?.pilot?.callsign ||
          flight?.positions?.[0]?.id_cn ||
          flight?.positions?.[0]?.callsign ||
          null,
        pilotName:
          flight?._soaringSpotPilotName ||
          flight?.pilot?.name ||
          [flight?.pilot?.first_name, flight?.pilot?.last_name].filter(Boolean).join(' ').trim() ||
          null,
        gliderType:
          flight?.aircraft?.model_name ||
          flight?.positions?.[0]?.aircraft_type ||
          flight?.positions?.[0]?.aircraft ||
          null,
        metadata: flight?.metadata || null
      });
    });
  }

  const resolvePilotNameFromPosition = (position) => {
    if (!position) {
      return null;
    }

    return (
      [position.id_fname, position.id_lname].filter(Boolean).join(' ').trim() ||
      position.pilotName ||
      position.pilot_name ||
      position.metadata?.pilotName ||
      null
    );
  };

  const resolveEffectiveRaceStatus = (state) => {
    const startTimeMs = state?.startTimeMs ?? (state?.startTime ? Date.parse(state.startTime) : null);
    const finishTimeMs = state?.finishTimeMs ?? (state?.finishTime ? Date.parse(state.finishTime) : null);
    const hasKnownRegattaStart = isRegattaTask && Number.isFinite(regattaReferenceStartMs);
    const effectiveStartTimeMs =
      hasKnownRegattaStart && Number.isFinite(startTimeMs) && startTimeMs < regattaReferenceStartMs
        ? null
        : startTimeMs;

    if (isReplayLikeMode && replayTimeMs !== null && !replayManager?.alignStartsEnabled) {
      if (hasKnownRegattaStart && replayTimeMs < regattaReferenceStartMs) {
        return 'pre_start';
      }
      if (Number.isFinite(effectiveStartTimeMs) && replayTimeMs < effectiveStartTimeMs) {
        return 'pre_start';
      }
      if (Number.isFinite(finishTimeMs)) {
        if (replayTimeMs < finishTimeMs) {
          return Number.isFinite(effectiveStartTimeMs) && replayTimeMs >= effectiveStartTimeMs
            ? 'racing'
            : 'pre_start';
        }
        return 'finished';
      }
      if (Number.isFinite(effectiveStartTimeMs) && replayTimeMs >= effectiveStartTimeMs) {
        return 'racing';
      }
      if (hasKnownRegattaStart) {
        return 'pre_start';
      }
    }

    return state?.raceStatus || 'pre_start';
  };

  const stateEntriesSnapshot = Array.from(aircraftStateTracker.getAllStates().entries());
  const leaderboardParticipants = [];
  stateEntriesSnapshot.forEach(([cookie, state]) => {
    const raceStatus = resolveEffectiveRaceStatus(state);
    const replayPilotMeta = replayPilotMetaByCookie.get(cookie);
    const finishInfo = aircraftStateTracker.getFinishedPilotInfo(cookie);
    const position = state.lastPosition;
    const gliderType =
      position?.aircraft_type ||
      position?.aircraft ||
      position?.id_model ||
      replayPilotMeta?.gliderType ||
      finishInfo?.gliderType ||
      null;
    const cn =
      state.lastPosition?.id_cn ||
      state.lastPosition?.callsign ||
      replayPilotMeta?.cn ||
      finishInfo?.cn ||
      `#${cookie}`;

    const pilotName =
      resolvePilotNameFromPosition(state.lastPosition) ||
      replayPilotMeta?.pilotName ||
      finishInfo?.pilotName ||
      null;

    // Skip entries without proper callsign (e.g., #pilot_xxx)
    if (cn.startsWith('#')) {
      return;
    }

    leaderboardParticipants.push({
      cookie,
      cn,
      pilotName,
      gliderType,
      status: raceStatus
    });

    const currentStartTimeMs = state?.startTimeMs ?? (state?.startTime ? Date.parse(state.startTime) : null);
    if (raceStatus === 'pre_start') {
      etfStartActivationByAircraft.delete(cookie);
    } else if (Number.isFinite(currentStartTimeMs)) {
      const activeStartTimeMs = etfStartActivationByAircraft.get(cookie);
      if (activeStartTimeMs !== currentStartTimeMs) {
        clearEtfTrackingForCookie(cookie);
        etfStartActivationByAircraft.set(cookie, currentStartTimeMs);
      }
    }

    if (raceStatus !== 'racing' && raceStatus !== 'finished') {
      return;
    }
    const positionTimeSeconds = resolvePositionTimeSeconds(position);
    if (Number.isFinite(positionTimeSeconds)) {
      latestSimTimeSec = latestSimTimeSec === null
        ? positionTimeSeconds
        : Math.max(latestSimTimeSec, positionTimeSeconds);
    }
    const elapsedSecondsResolved = resolveElapsedSeconds(state, position);
    const elapsedMinutesResolved = Number.isFinite(elapsedSecondsResolved)
      ? elapsedSecondsResolved / 60
      : null;

    // Determine if this pilot is offline (for grey styling)
    // In replay mode, check if position is stale (pilot left server)
    let isOffline = false;
    if (isReplayLikeMode && replayManager?.currentTime) {
      const currentReplayTimeSeconds = replayManager.currentTime.getTime() / 1000;

      if (positionTimeSeconds !== null) {
        // If position is more than 30 seconds old, mark as offline
        if ((currentReplayTimeSeconds - positionTimeSeconds) > 30) {
          isOffline = true;
        }
      } else {
        isOffline = true;
      }
    } else if (!position) {
      // No position data at all
      isOffline = true;
    }

    let etaData = null;
    let predictedFinishMinutes = null;
    let predictedFinishMinutesHandicap = null; // Handicap-corrected predicted finish
    let etfDeltaSecondsA = null;
    let etfDeltaSecondsB = null;
    let debugEntry = null;
    let xcSpeedKmh = resolveXcSpeed(position);
    let realRemainingDistance_km = null;
    let officialRemainingDistance_km = null;
    let projectedAatMetrics = null;
    let currentTrendHistorySample = null;

    // Calculate XC speed if not available from position data
    if (xcSpeedKmh === null && position && raceStatus === 'racing' && task) {
      const currentAlt_m = position.tec_m || position.altitude_m || 0;
      const elapsedSeconds = elapsedSecondsResolved ?? 0;
      const distanceCovered_km = state.realDistance_km ?? state.officialDistance_km ?? 0;

      if (gliderType && elapsedSeconds > 0 && globalPolarManager.hasPolar(gliderType)) {
        // Get polar coefficients (use default wing loading for Club class)
        const effectiveWingLoading = getEffectiveWingLoading(gliderType, task, {
          cookie,
          flightMetadata: replayPilotMeta?.metadata || null
        });
        const coeffs = globalPolarManager.getPolarCoefficients(gliderType, effectiveWingLoading);

        if (coeffs) {
          const mcSetting_ms = (Number.isFinite(config.maccready) && config.maccready > 0) ? config.maccready : 2.0;
          const mcResult = getCachedMcSpeed(coeffs, mcSetting_ms);
          const mcSpeedKmh = mcResult.speedKmh;
          const mcSinkMs = mcResult.sinkMs;

          // Set start height if not already set (use max start altitude from task)
          if (state.startTime && !xcSpeedCalculator.startHeights.has(cookie)) {
            const startTP = task.turnpoints?.[0];
            // MaxAltMeters is the field from the API (string that needs parsing)
            const maxStartAlt_m = startTP?.MaxAltMeters ? parseFloat(startTP.MaxAltMeters) : null;
            // Fallback to other possible field names
            const taskStartAlt_m = maxStartAlt_m || startTP?.Altitude_m || 0;

            xcSpeedCalculator.setStartHeight(cookie, taskStartAlt_m);
          }

          // Calculate XC speed
          xcSpeedKmh = xcSpeedCalculator.calculateXcSpeed(
            cookie,
            currentAlt_m,
            elapsedSeconds,
            distanceCovered_km,
            mcSpeedKmh,
            mcSinkMs,
            config.maccready
          );
        }
      }
    }

    // For finished pilots, use their actual finish time (frozen)
    if (raceStatus === 'finished') {
      const finishElapsedSeconds = elapsedSecondsResolved ?? 0;
      if (finishElapsedSeconds > 0) {
        predictedFinishMinutes = finishElapsedSeconds / 60;
      }
    } else if (position) {
      // Only calculate ETF for racing pilots.
      const hasTec = position.tec_m != null && Number.isFinite(position.tec_m) && position.tec_m !== 0;
      const currentAlt_m = hasTec ? position.tec_m : (position.altitude_m || 0);

      // Detect altitude source transition (altitude_m → tec_m).  If a polar
      // loads mid-playback, early history used raw altitude while new samples
      // use TEC (altitude + kinetic energy, ~60-110m higher).  The mixed
      // history produces inflated deltas, so clear it on transition.
      const altSource = hasTec ? 'tec' : 'alt';
      const prevAltSource = etfAltSourceByAircraft.get(cookie);
      if (prevAltSource === 'alt' && altSource === 'tec') {
        etfHistory.delete(cookie);
        etfDeltaByAircraft.delete(cookie);
        etfDeltaByAircraftB.delete(cookie);
        sampledEtfDeltasA.delete(cookie);
        sampledEtfDeltasB.delete(cookie);
      }
      etfAltSourceByAircraft.set(cookie, altSource);

      const netTaskLength_km = task?.netTaskLength;
      const realDistance_km = state.realDistance_km ?? 0;
      const stateRealRemainingDistance_km = state.realRemainingDistance_km;
      const stateOfficialRemainingDistance_km = state.officialRemainingDistance_km;
      realRemainingDistance_km =
        Number.isFinite(stateRealRemainingDistance_km)
          ? Math.max(0, stateRealRemainingDistance_km)
          : typeof netTaskLength_km === 'number' && netTaskLength_km > 0
          ? Math.max(0, netTaskLength_km - realDistance_km)
          : null;

      const taskLength_km = task?.scoringTaskLength || 0;
      officialRemainingDistance_km = Number.isFinite(stateOfficialRemainingDistance_km)
        ? Math.max(0, stateOfficialRemainingDistance_km)
        : Math.max(0, taskLength_km - (state.officialDistance_km || 0));

      let aatMinimumRemainingDistance_km = null;
      let aatMaximumRemainingDistance_km = null;
      let aatTimeDistanceEstimate_km = null;

      if (task?.task_type === 'AAT') {
        const aatBounds = aircraftStateTracker.getAatRemainingDistanceBounds(position, task);
        if (aatBounds) {
          aatMinimumRemainingDistance_km = Number.isFinite(aatBounds.minimumRemainingDistance_km)
            ? Math.max(0, aatBounds.minimumRemainingDistance_km)
            : null;
          aatMaximumRemainingDistance_km = Number.isFinite(aatBounds.maximumRemainingDistance_km)
            ? Math.max(
              aatMinimumRemainingDistance_km ?? 0,
              aatBounds.maximumRemainingDistance_km
            )
            : null;

          if (Number.isFinite(aatMinimumRemainingDistance_km)) {
            realRemainingDistance_km = aatMinimumRemainingDistance_km;
            officialRemainingDistance_km = aatMinimumRemainingDistance_km;
          }
        }
      }

      if (
        task?.task_type === 'AAT' &&
        Number.isFinite(task?.aat_time_hours) &&
        elapsedMinutesResolved !== null &&
        gliderType &&
        globalPolarManager.hasPolar(gliderType)
      ) {
        const aatTimeRemaining_min = (task.aat_time_hours * 60) - elapsedMinutesResolved;

        if (aatTimeRemaining_min > 0.01) {
          const effectiveWingLoading = getEffectiveWingLoading(gliderType, task);
          const aatRemainingResult = calculateDistanceForTime(
            currentAlt_m,
            finishAlt_m,
            aatTimeRemaining_min,
            config.maccready,
            config.netto,
            globalPolarManager,
            gliderType,
            effectiveWingLoading,
            300
          );

          if (aatRemainingResult && Number.isFinite(aatRemainingResult.remainingDistance_km)) {
            aatTimeDistanceEstimate_km = Math.max(0, aatRemainingResult.remainingDistance_km);

            let projectedAatRemainingDistance_km = aatTimeDistanceEstimate_km;
            if (Number.isFinite(aatMinimumRemainingDistance_km)) {
              projectedAatRemainingDistance_km = Math.max(
                aatMinimumRemainingDistance_km,
                projectedAatRemainingDistance_km
              );
            }

            if (Number.isFinite(aatMaximumRemainingDistance_km)) {
              projectedAatRemainingDistance_km = Math.min(
                projectedAatRemainingDistance_km,
                Math.max(aatMinimumRemainingDistance_km ?? 0, aatMaximumRemainingDistance_km)
              );
            }

            realRemainingDistance_km = projectedAatRemainingDistance_km;
            officialRemainingDistance_km = projectedAatRemainingDistance_km;
          }
        }
      }

      const remainingDistance_km = realRemainingDistance_km ?? officialRemainingDistance_km;

      if (gliderType && globalPolarManager.hasPolar(gliderType)) {
        const NEAR_FINISH_DISTANCE_KM = 0.05; // Keep ETF data alive when within 50m of finish
        const isNearFinish = remainingDistance_km <= NEAR_FINISH_DISTANCE_KM;

        if (isNearFinish && elapsedMinutesResolved !== null) {
          predictedFinishMinutes = elapsedMinutesResolved;
          const finalGlideSpeed = parseNumberOrNull(
            position?.groundspeed_kmh ??
            position?.tas_kmh ??
            position?.ias_kmh ??
            position?.speed_kmh ??
            (position?.tas ? position.tas * 3.6 : null)
          ) || 0;

          etaData = etaData || {
            etfTotal_min: 0,
            etfCruise_min: 0,
            etfClimb_min: 0,
            finalGlideSpeed_kmh: finalGlideSpeed
          };

          if (isHandicapTaskClass(task) && gliderType) {
              const handicap = getConfiguredHandicap(gliderType, task);
            if (handicap) {
              predictedFinishMinutesHandicap = (predictedFinishMinutes / 100) * handicap;
            }
          }
        } else if (remainingDistance_km > 0) {
          // Enable detailed ETF debug logging for specific aircraft
          const debugAircraftId = (cookie === 'HG1') ? cookie : null;

          // Use default wing loading for Club class tasks
          const effectiveWingLoading = getEffectiveWingLoading(gliderType, task, {
            cookie,
            flightMetadata: replayPilotMeta?.metadata || null
          });

          const etaResult = calculateETA(
            currentAlt_m,
            finishAlt_m,
            remainingDistance_km,
            config.maccready,
            config.netto,
            globalPolarManager,
            gliderType,
            effectiveWingLoading,
            300, // VNE in km/h
            debugAircraftId // Pass aircraft ID for debug logging
          );

          if (etaResult) {
            etaData = etaResult;

            if (Number.isFinite(etaData.etfTotal_min) && elapsedMinutesResolved !== null) {
              predictedFinishMinutes = elapsedMinutesResolved + etaData.etfTotal_min;

              // Calculate handicap-corrected predicted finish for Club/All class
              if (isHandicapTaskClass(task) && gliderType) {
                const handicap = getConfiguredHandicap(gliderType, task);
                if (handicap) {
                  // Apply handicap correction: (predictedFinish / 100) * handicap
                  predictedFinishMinutesHandicap = (predictedFinishMinutes / 100) * handicap;
                }
              }
            }

            if (shouldLogDebug) {
              const startTimeMsResolved = Number.isFinite(state?.startTime?.getTime?.())
                ? state.startTime.getTime()
                : null;
              debugEntry = {
                cn,
                raceStatus,
                regatta: isRegattaTask ? 'yes' : 'no',
                regattaRefStart: formatDebugDateTime(regattaReferenceStartMs),
                gliderType,
                simTime: formatDebugClock(positionTimeSeconds),
                simTimeSec: Number.isFinite(positionTimeSeconds) ? positionTimeSeconds.toFixed(1) : '--',
                startTime: formatDebugDateTime(startTimeMsResolved),
                elapsed: formatDebugClock(elapsedSecondsResolved),
                elapsedSec: Number.isFinite(elapsedSecondsResolved) ? elapsedSecondsResolved.toFixed(0) : '--',
                currentAlt_m: formatDebugFixed(currentAlt_m, 0),
                finishAlt_m,
                remainingReal_km: formatDebugFixed(remainingDistance_km, 2),
                remainingOfficial_km: formatDebugFixed(officialRemainingDistance_km, 2),
                officialDistance: formatDebugFixed(state.officialDistance_km, 2),
                realDistance: formatDebugFixed(realDistance_km, 2),
                etfTotal: formatDebugFixed(etaData.etfTotal_min, 1),
                etfCruise: formatDebugFixed(etaData.etfCruise_min, 1),
                etfClimb: formatDebugFixed(etaData.etfClimb_min, 1),
                ddhMc_m: formatDebugFixed(etaData.finishAltDelta_m, 0, '--'),
                fgSpeed: formatDebugFixed(etaData.finalGlideSpeed_kmh, 0),
                xcSpeed: formatDebugFixed(xcSpeedKmh, 1, '--'),
                predictedFinish: formatDebugFixed(predictedFinishMinutes, 2)
              };
              if (task?.task_type === 'AAT') {
                debugEntry.aatGeomMin_km = formatDebugFixed(aatMinimumRemainingDistance_km, 2);
                debugEntry.aatGeomMax_km = formatDebugFixed(aatMaximumRemainingDistance_km, 2);
                debugEntry.aatTimeEstimate_km = formatDebugFixed(aatTimeDistanceEstimate_km, 2);
                debugEntry.aatProjectedRemaining_km = formatDebugFixed(realRemainingDistance_km, 2);
              }
              etaDebugData.push(debugEntry);
            }
          }
        }
      }

      if (task?.task_type === 'AAT' && Number.isFinite(etaData?.etfTotal_min)) {
        projectedAatMetrics = calculateProjectedAatMetrics({
          distanceFlown_km: state.realDistance_km ?? state.officialDistance_km ?? 0,
          remainingDistance_km,
          elapsedMinutes: elapsedMinutesResolved,
          etaRemainingMinutes: etaData?.etfTotal_min,
          aatTimeHours: task.aat_time_hours
        });
      }
    }

    currentTrendHistorySample = buildTrendHistorySample(task, etaData?.etfTotal_min, projectedAatMetrics);

    // Record trend history and compute both delta trends (window A + B)
    if (currentTrendHistorySample && positionTimeSeconds !== null) {
      // In replay/IGC/PVR, seed retrospective window baselines immediately after seeks.
      // Process largest window first so historical inserts remain chronological.
      if (!skipReplayEtfBackfill && allowReplayTrendBackfill) {
        const replayWindowsDesc = [trendWindowSeconds, trendWindowSecondsB]
          .filter(Number.isFinite)
          .sort((a, b) => b - a);

        // Queue backfill work instead of processing synchronously
        for (const windowSec of replayWindowsDesc) {
          queueReplayBackfillWork(cookie, positionTimeSeconds, windowSec, task, finishAlt_m);
        }
      }

      recordTrendSample(cookie, currentTrendHistorySample, positionTimeSeconds);
      const deltaSecondsA = calculateTrendDelta(cookie, currentTrendHistorySample, positionTimeSeconds, trendWindowSeconds);
      const deltaSecondsB = calculateTrendDelta(cookie, currentTrendHistorySample, positionTimeSeconds, trendWindowSecondsB);

      if (deltaSecondsA !== null) {
        etfDeltaSecondsA = deltaSecondsA;
        etfDeltaByAircraft.set(cookie, deltaSecondsA);
      } else if (isReplayLikeMode && etfDeltaByAircraft.has(cookie)) {
        etfDeltaSecondsA = etfDeltaByAircraft.get(cookie);
      } else {
        etfDeltaByAircraft.delete(cookie);
      }

      if (deltaSecondsB !== null) {
        etfDeltaSecondsB = deltaSecondsB;
        etfDeltaByAircraftB.set(cookie, deltaSecondsB);
      } else if (isReplayLikeMode && etfDeltaByAircraftB.has(cookie)) {
        etfDeltaSecondsB = etfDeltaByAircraftB.get(cookie);
      } else {
        etfDeltaByAircraftB.delete(cookie);
      }
    } else if (raceStatus !== 'finished') {
      if (isReplayLikeMode) {
        if (etfDeltaByAircraft.has(cookie)) {
          etfDeltaSecondsA = etfDeltaByAircraft.get(cookie);
        }
        if (etfDeltaByAircraftB.has(cookie)) {
          etfDeltaSecondsB = etfDeltaByAircraftB.get(cookie);
        }
      } else {
        etfDeltaByAircraft.delete(cookie);
        etfDeltaByAircraftB.delete(cookie);
      }
    } else {
      if (etfDeltaByAircraft.has(cookie)) {
        etfDeltaSecondsA = etfDeltaByAircraft.get(cookie);
      }
      if (etfDeltaByAircraftB.has(cookie)) {
        etfDeltaSecondsB = etfDeltaByAircraftB.get(cookie);
      }
    }

    if (position) {
      position.etfDeltaSeconds = Number.isFinite(etfDeltaSecondsA) ? etfDeltaSecondsA : null;
      position.etfTrendSec = trendWindowSeconds;
      position.etfDeltaSecondsB = Number.isFinite(etfDeltaSecondsB) ? etfDeltaSecondsB : null;
      position.etfTrendSecB = trendWindowSecondsB;
      position.finishAltDeltaM = Number.isFinite(etaData?.finishAltDelta_m) ? etaData.finishAltDelta_m : null;
    }

    // Sim-time sampled XC Pace values (captured at configurable game-time intervals).
    // After a scrub, bypass interval checks briefly so values refresh each frame.
    if (positionTimeSeconds !== null) {
      const updateIntervalA = Math.max(1, Math.min(600, Number(config.overlayXcPerfAUpdateSec) || 1));
      const updateIntervalB = Math.max(1, Math.min(600, Number(config.overlayXcPerfBUpdateSec) || 1));

      const seekBypassActive = (() => {
        if (!lastSeekRealTime) return false;
        const speed = replayManager?.speed || 1;
        const maxIntervalSec = Math.max(updateIntervalA, updateIntervalB);
        const bypassDurationMs = (maxIntervalSec / Math.max(0.5, speed)) * 1000 + 1000;
        return (performance.now() - lastSeekRealTime) < bypassDurationMs;
      })();

      if (Number.isFinite(etfDeltaSecondsA)) {
        const sampledA = sampledEtfDeltasA.get(cookie);
        const shouldCaptureA =
          seekBypassActive ||
          !sampledA ||
          !Number.isFinite(sampledA.lastCaptureSimTimeSec) ||
          (positionTimeSeconds < sampledA.lastCaptureSimTimeSec - 1) ||
          (positionTimeSeconds - sampledA.lastCaptureSimTimeSec) >= updateIntervalA;
        if (shouldCaptureA) {
          sampledEtfDeltasA.set(cookie, { value: etfDeltaSecondsA, lastCaptureSimTimeSec: positionTimeSeconds });
        }
      }
      if (Number.isFinite(etfDeltaSecondsB)) {
        const sampledB = sampledEtfDeltasB.get(cookie);
        const shouldCaptureB =
          seekBypassActive ||
          !sampledB ||
          !Number.isFinite(sampledB.lastCaptureSimTimeSec) ||
          (positionTimeSeconds < sampledB.lastCaptureSimTimeSec - 1) ||
          (positionTimeSeconds - sampledB.lastCaptureSimTimeSec) >= updateIntervalB;
        if (shouldCaptureB) {
          sampledEtfDeltasB.set(cookie, { value: etfDeltaSecondsB, lastCaptureSimTimeSec: positionTimeSeconds });
        }
      }
    }

    if (task?.task_type === 'AAT' && !projectedAatMetrics) {
      projectedAatMetrics = calculateProjectedAatMetrics({
        distanceFlown_km: state.realDistance_km ?? state.officialDistance_km ?? 0,
        remainingDistance_km: raceStatus === 'finished' ? 0 : (realRemainingDistance_km ?? officialRemainingDistance_km ?? 0),
        elapsedMinutes: raceStatus === 'finished'
          ? (Number.isFinite(state.elapsedTime_s) ? state.elapsedTime_s / 60 : elapsedMinutesResolved)
          : elapsedMinutesResolved,
        etaRemainingMinutes: raceStatus === 'finished' ? 0 : etaData?.etfTotal_min,
        aatTimeHours: task.aat_time_hours
      });
    }

    const entry = {
      cookie,
      cn,
      pilotName,
      official_km: state.officialDistance_km || 0,
      real_km: state.realDistance_km || 0,
      status: raceStatus,
      gliderType,
      startAltitude_m: Number.isFinite(state.startAltitude_m) ? state.startAltitude_m : null,
      etaData,
      predictedFinishMinutes,
      predictedFinishMinutesHandicap,
      startTimeMs: state.startTimeMs ?? (state.startTime ? Date.parse(state.startTime) : null),
      deltaToLeaderMinutes: null,
      deltaToLeaderMinutesHandicap: null,
      deltaToLeaderStr: '--:--',
      finishOrder: raceStatus === 'finished' ? state.finishOrder ?? null : null,
      finishTimeMs: raceStatus === 'finished' ? state.finishTimeMs ?? null : null,
      finishElapsedMinutes: raceStatus === 'finished' && Number.isFinite(state.elapsedTime_s) ? state.elapsedTime_s / 60 : null,
      raceElapsedSec: Number.isFinite(state.elapsedTime_s) ? state.elapsedTime_s : null,
      finishDeltaMinutes: raceStatus === 'finished' && Number.isFinite(state.finishDeltaMinutes) ? state.finishDeltaMinutes : null,
      finishDeltaMinutesHandicap: raceStatus === 'finished' && Number.isFinite(state.finishDeltaMinutesHandicap)
        ? state.finishDeltaMinutesHandicap
        : null,
      xcSpeedKmh,
      etfDeltaSecondsA,
      etfDeltaSecondsB,
      hasFrozenDelta: false,
      isFinished: raceStatus === 'finished',
      isOffline: isOffline,  // Mark as offline based on position staleness
      isDnf: false
    };

    if (projectedAatMetrics) {
      Object.assign(entry, projectedAatMetrics);
    }

    if (debugEntry) {
      entry._debugEntry = debugEntry;
    }

    if (entry._debugEntry) {
      const entryStartTimeMs = getEntryStartTimeMs(entry);
      entry._debugEntry.entryStartTime = formatDebugDateTime(entryStartTimeMs);
      entry._debugEntry.regattaOffsetMin = isRegattaTask
        ? getRegattaStartOffsetMinutes(entry).toFixed(2)
        : '--';
      entry._debugEntry.finishElapsed = Number.isFinite(entry.finishElapsedMinutes)
        ? formatTimeMMSS(entry.finishElapsedMinutes)
        : '--';
      entry._debugEntry.raceElapsed = Number.isFinite(entry.raceElapsedSec)
        ? formatDebugClock(entry.raceElapsedSec)
        : '--';
    }

    if (Number.isFinite(xcSpeedKmh)) {
      state.xcSpeedKmh = xcSpeedKmh;
    }

    if (entry.isFinished) {
      if (Number.isFinite(state.xcSpeedKmh)) {
        entry.xcSpeedKmh = state.xcSpeedKmh;
      }
      // For Club/All class, ONLY use handicap-corrected delta (not regular delta)
      const useHandicapDelta = isHandicapTaskClass(task);
      let frozenDelta = null;

      if (useHandicapDelta) {
        // For handicap races, only use the handicap delta if it exists
        frozenDelta = Number.isFinite(state.finishDeltaMinutesHandicap)
          ? state.finishDeltaMinutesHandicap
          : null;
      } else {
        // For non-handicap races, use regular delta
        frozenDelta = Number.isFinite(state.finishDeltaMinutes)
          ? state.finishDeltaMinutes
          : null;
      }

      if (frozenDelta !== null) {
        entry.deltaToLeaderMinutes = frozenDelta;
        entry.deltaToLeaderStr = formatDeltaMinutes(frozenDelta);
        entry.hasFrozenDelta = true;
      }

      // Calculate handicap-corrected finish time for Club/All class (needed for delta calculation)
      if (useHandicapDelta && gliderType && entry.finishElapsedMinutes) {
          const handicap = getConfiguredHandicap(gliderType, task);
        if (handicap) {
          entry.predictedFinishMinutesHandicap = (entry.finishElapsedMinutes / 100) * handicap;
        }
      }
    } else {
      // Handle mid-race disconnects (DNF)
      const hasStarted = state.startTimeMs !== null || state.startTime !== null;
      const isMidRaceDisconnect = currentMode === 'replay' && isOffline && raceStatus === 'racing' && hasStarted;
      if (isMidRaceDisconnect) {
        entry.isDnf = true;
        entry.deltaToLeaderMinutes = null;
        entry.deltaToLeaderStr = 'DNF';
        entry.hasFrozenDelta = true;
        entry.predictedFinishMinutes = null;
        entry.predictedFinishMinutesHandicap = null;
      }
    }

    if (isRegattaTask && Number.isFinite(regattaReferenceStartMs) && Number.isFinite(entry.predictedFinishMinutesHandicap)) {
      entry.predictedFinishMinutesHandicap += getRegattaStartOffsetMinutes(entry);
    }

    aircraftWithDistance.push(entry);
  });

  const hasStartedLeaderboard = leaderboardParticipants.some(participant =>
    participant.status === 'racing' || participant.status === 'finished'
  );

  const preStartDisplayEntries = hasStartedLeaderboard
    ? []
    : leaderboardParticipants
      .map(participant => ({
        cookie: participant.cookie,
        cn: participant.cn,
        pilotName: participant.pilotName,
        official_km: null,
        real_km: null,
        status: 'pre_start',
        gliderType: participant.gliderType || null,
        etaData: null,
        predictedFinishMinutes: null,
        predictedFinishMinutesHandicap: null,
        startTimeMs: null,
        deltaToLeaderMinutes: null,
        deltaToLeaderStr: '',
        finishOrder: null,
        finishTimeMs: null,
        finishElapsedMinutes: null,
        raceElapsedSec: null,
        finishDeltaMinutes: null,
        finishDeltaMinutesHandicap: null,
        xcSpeedKmh: null,
        etfDeltaSecondsA: null,
        etfDeltaSecondsB: null,
        hasFrozenDelta: false,
        isFinished: false,
        isOffline: true,
        isDnf: false,
        isPreStart: true
      }))
      .sort((a, b) => a.cn.localeCompare(b.cn));

  let displayEntries = preStartDisplayEntries;

  // Delay cached leaderboard entries until we reach the first finish in replay mode
  const firstFinishTimeMs = aircraftStateTracker.finishLeader?.finishTimeMs ?? null;
  const isReplayBeforeFirstFinish =
    isReplayLikeMode &&
    firstFinishTimeMs !== null &&
    replayTimeMs !== null &&
    replayTimeMs < firstFinishTimeMs;

  if (hasStartedLeaderboard && !isReplayBeforeFirstFinish) {
    // Add finished pilots from cache who no longer have position data (left server during scrubbing)
    const finishedCookies = aircraftStateTracker.getAllFinishedPilotCookies();
    finishedCookies.forEach(cookie => {
      const finishInfo = aircraftStateTracker.getFinishedPilotInfo(cookie);

      if (finishInfo && finishInfo.cn) {
        // Check if this pilot is already in the list (has active position)
        const alreadyListed = aircraftWithDistance.some(e => e.cn === finishInfo.cn);

        if (!alreadyListed) {
          // Create entry for offline finished pilot
          // For Club/All class, try to get handicap delta from state or finish cache
          const { official_km: offlineOfficialKm, real_km: offlineRealKm, state } =
            resolveFinishedDistances(task, cookie, finishInfo);
          const useHandicapDelta = isHandicapTaskClass(task);

          let deltaMinutes = finishInfo.deltaMinutes;
          let finishDeltaMinutesHandicap = null;
          if (useHandicapDelta) {
            if (state && Number.isFinite(state.finishDeltaMinutesHandicap)) {
              deltaMinutes = state.finishDeltaMinutesHandicap;
              finishDeltaMinutesHandicap = state.finishDeltaMinutesHandicap;
            } else if (Number.isFinite(finishInfo.deltaMinutesHandicap)) {
              deltaMinutes = finishInfo.deltaMinutesHandicap;
              finishDeltaMinutesHandicap = finishInfo.deltaMinutesHandicap;
            } else {
              deltaMinutes = null;
            }
          }

          const gliderType = finishInfo.gliderType || null;

          let offlineXcSpeed = finishInfo.xcSpeedKmh ?? null;
          if (!Number.isFinite(offlineXcSpeed) && Number.isFinite(finishInfo.elapsedSeconds) && finishInfo.elapsedSeconds > 0) {
            const hours = finishInfo.elapsedSeconds / 3600;
            const distance_km =
              (Number.isFinite(offlineRealKm) && offlineRealKm > 0 ? offlineRealKm : null) ??
              (Number.isFinite(offlineOfficialKm) && offlineOfficialKm > 0 ? offlineOfficialKm : null);
            if (Number.isFinite(distance_km) && distance_km > 0 && hours > 0) {
              offlineXcSpeed = distance_km / hours;
            }
          }

          const replayPilotMeta = replayPilotMetaByCookie.get(cookie);
          const entry = {
            cookie,
            cn: finishInfo.cn,
            pilotName: replayPilotMeta?.pilotName || finishInfo?.pilotName || null,
            official_km: offlineOfficialKm,
            real_km: offlineRealKm,
            status: 'finished',
            gliderType,
            startAltitude_m: Number.isFinite(finishInfo.startAltitude_m) ? finishInfo.startAltitude_m : null,
            etaData: null,
            predictedFinishMinutes: null,
            predictedFinishMinutesHandicap: null,
            startTimeMs: finishInfo.startTimeMs,
            deltaToLeaderMinutes: Number.isFinite(deltaMinutes) ? deltaMinutes : null,
            deltaToLeaderMinutesHandicap: Number.isFinite(finishDeltaMinutesHandicap) ? finishDeltaMinutesHandicap : null,
            deltaToLeaderStr: Number.isFinite(deltaMinutes) ? formatDeltaMinutes(deltaMinutes) : '--:--',
            finishOrder: finishInfo.order,
            finishTimeMs: finishInfo.finishTimeMs,
            finishElapsedMinutes: Number.isFinite(finishInfo.elapsedSeconds) ? finishInfo.elapsedSeconds / 60 : null,
            finishDeltaMinutes: finishInfo.deltaMinutes,
            finishDeltaMinutesHandicap: finishDeltaMinutesHandicap,
            xcSpeedKmh: offlineXcSpeed ?? null,
            hasFrozenDelta: Number.isFinite(deltaMinutes),
            isFinished: true,
            isOffline: true  // Mark as offline so we can style differently
          };

          if (task?.task_type === 'AAT') {
            const projectedAatMetrics = calculateProjectedAatMetrics({
              distanceFlown_km: offlineRealKm,
              remainingDistance_km: 0,
              elapsedMinutes: entry.finishElapsedMinutes,
              etaRemainingMinutes: 0,
              aatTimeHours: task.aat_time_hours
            });

            if (projectedAatMetrics) {
              Object.assign(entry, projectedAatMetrics);
            }
          }

          // Calculate handicap-corrected finish time for offline pilots in Club/All class
          if (useHandicapDelta && finishInfo.gliderType && entry.finishElapsedMinutes) {
            const handicap = getConfiguredHandicap(finishInfo.gliderType, task);
            if (handicap) {
              entry.predictedFinishMinutesHandicap = (entry.finishElapsedMinutes / 100) * handicap;
              // If we didn't have a cached handicap delta, allow handicap override to compute it
              if (!Number.isFinite(deltaMinutes)) {
                entry.hasFrozenDelta = false;
              }
            }
          }

          if (isRegattaTask && Number.isFinite(regattaReferenceStartMs) && Number.isFinite(entry.predictedFinishMinutesHandicap)) {
            entry.predictedFinishMinutesHandicap += getRegattaStartOffsetMinutes(entry);
          }

          aircraftWithDistance.push(entry);
        }
      }
    });
  }

  if (hasStartedLeaderboard) {
    const finishedEntriesAll = aircraftWithDistance.filter(entry => entry.status === 'finished');
    let finishedLeaderEntry = null;
    maybeApplyWinnerMacCready(task, finishedEntriesAll);
    finishedEntriesAll.forEach(entry => {
      if (!finishedLeaderEntry) {
        finishedLeaderEntry = entry;
        return;
      }

      const currentOrder = finishedLeaderEntry.finishOrder ?? Infinity;
      const entryOrder = entry.finishOrder ?? Infinity;

      if (entryOrder < currentOrder) {
        finishedLeaderEntry = entry;
        return;
      }

      if (entryOrder === currentOrder) {
        const leaderTimeMs = finishedLeaderEntry.finishTimeMs ?? Infinity;
        const entryTimeMs = entry.finishTimeMs ?? Infinity;
        if (entryTimeMs < leaderTimeMs) {
          finishedLeaderEntry = entry;
        }
      }
    });

    const hasFinishedLeader = !!finishedLeaderEntry;

    if (hasFinishedLeader) {
      const leaderFinishTimeMs = finishedLeaderEntry.finishTimeMs ?? null;
      const leaderElapsedMinutes = Number.isFinite(finishedLeaderEntry.finishElapsedMinutes)
        ? finishedLeaderEntry.finishElapsedMinutes
        : null;
      const leaderAbsoluteFinishMinutes = (isRegattaTask && Number.isFinite(regattaReferenceStartMs))
        ? getAbsoluteFinishMinutes(finishedLeaderEntry)
        : leaderElapsedMinutes;

      // For Club/All class, skip regular delta for finished pilots - handicap override will handle it
      const useHandicapDelta = isHandicapTaskClass(task);

      if (!useHandicapDelta) {
        // Only set regular deltas for non-handicap races
        finishedEntriesAll.forEach(entry => {
          let deltaMinutes = null;

          if (entry === finishedLeaderEntry) {
            deltaMinutes = 0;
          } else if (isRegattaTask && Number.isFinite(leaderAbsoluteFinishMinutes)) {
            const entryAbsoluteFinish = getAbsoluteFinishMinutes(entry);
            if (Number.isFinite(entryAbsoluteFinish)) {
              deltaMinutes = entryAbsoluteFinish - leaderAbsoluteFinishMinutes;
            }
          } else if (isRegattaTask && Number.isFinite(entry.finishTimeMs) && Number.isFinite(leaderFinishTimeMs)) {
            deltaMinutes = (entry.finishTimeMs - leaderFinishTimeMs) / 60000;
          } else if (Number.isFinite(entry.finishDeltaMinutes)) {
            deltaMinutes = entry.finishDeltaMinutes;
          }

          if (Number.isFinite(deltaMinutes)) {
            deltaMinutes = Math.max(0, deltaMinutes);
            entry.deltaToLeaderMinutes = deltaMinutes;
            entry.deltaToLeaderStr = formatDeltaMinutes(deltaMinutes);
            entry.hasFrozenDelta = true;
          }
        });
      }

      aircraftWithDistance
        .filter(entry => entry.status !== 'finished')
        .forEach(entry => {
          if (entry.isDnf) {
            return;
          }
          let deltaMinutes = null;

          if (isRegattaTask && Number.isFinite(leaderAbsoluteFinishMinutes)) {
            const predictedAbsolute = getAbsolutePredictedFinishMinutes(entry);
            if (Number.isFinite(predictedAbsolute)) {
              deltaMinutes = predictedAbsolute - leaderAbsoluteFinishMinutes;
            }
          } else if (isRegattaTask && Number.isFinite(leaderFinishTimeMs)) {
            if (Number.isFinite(entry.predictedFinishMinutes) && Number.isFinite(entry.startTimeMs)) {
              const predictedAbsoluteMs = entry.startTimeMs + entry.predictedFinishMinutes * 60000;
              deltaMinutes = (predictedAbsoluteMs - leaderFinishTimeMs) / 60000;
            }
          } else if (Number.isFinite(leaderElapsedMinutes) && Number.isFinite(entry.predictedFinishMinutes)) {
            deltaMinutes = entry.predictedFinishMinutes - leaderElapsedMinutes;
          }

          if (Number.isFinite(deltaMinutes)) {
            deltaMinutes = Math.max(0, deltaMinutes);
            entry.deltaToLeaderMinutes = deltaMinutes;
            entry.deltaToLeaderStr = formatDeltaMinutes(deltaMinutes);
          }
        });
    }

    // Compute ETF delta to leader (predicted finish time differences)
    // For regatta: only consider racing pilots (hasFinishedLeader tracks first finisher)
    // For non-regatta: consider both finished and racing pilots for leader determination
    if (!hasFinishedLeader || !isRegattaTask) {
      // Get all entries with task times (finished use actual, racing use prediction)
      const entriesWithTime = aircraftWithDistance.filter(entry => {
        if (entry.isDnf) {
          return false;
        }
        if (isRegattaTask && entry.status === 'finished') return false; // Regatta: exclude finished
        const taskTime = getAbsolutePredictedFinishMinutes(entry);
        return Number.isFinite(taskTime);
      });

      if (entriesWithTime.length > 0) {
        // Find leader (best task time)
        let leaderEntry = null;
        entriesWithTime.forEach(entry => {
          const taskTime = getAbsolutePredictedFinishMinutes(entry);
          if (!Number.isFinite(taskTime)) {
            return;
          }

          const leaderTaskTime = leaderEntry ? getAbsolutePredictedFinishMinutes(leaderEntry) : Infinity;

          if (!leaderEntry || taskTime < leaderTaskTime - 1e-6) {
            leaderEntry = entry;
          }
        });

        if (leaderEntry) {
          const leaderTaskTime = getAbsolutePredictedFinishMinutes(leaderEntry);

          // Calculate deltas for all entries
          entriesWithTime.forEach(entry => {
            if (entry.hasFrozenDelta && isRegattaTask) {
              if (entry._debugEntry) {
                entry._debugEntry.deltaLeader = entry.deltaToLeaderStr;
              }
              return;
            }

            const entryTaskTime = getAbsolutePredictedFinishMinutes(entry);
            if (!Number.isFinite(entryTaskTime) || !Number.isFinite(leaderTaskTime)) {
              return;
            }

            // Delta = difference in task times (regatta adjustments baked into absolute times)
            let deltaMinutes = entryTaskTime - leaderTaskTime;

            entry.deltaToLeaderMinutes = deltaMinutes;
            entry.deltaToLeaderStr = deltaMinutes < 1e-6 ? 'Leader' : formatDeltaMinutes(deltaMinutes);

            if (entry._debugEntry) {
              entry._debugEntry.deltaLeader = entry.deltaToLeaderStr;
            }
          });
        }
      }
    }

    const isHandicapTask = isHandicapTaskClass(task);

    // For Club/All class: Override regular delta with handicap-corrected delta
    // Suppress this in replay regatta scenarios until the first finisher actually occurs
    const canApplyHandicapDeltas =
      isHandicapTask &&
      (!(currentMode === 'replay' && isRegattaTask && isReplayBeforeFirstFinish));

    if (canApplyHandicapDeltas) {
      // Get all entries with handicap-corrected times (including finished pilots)
      const entriesWithHandicapTime = aircraftWithDistance.filter(entry => {
        if (entry.isDnf) {
          return false;
        }
        // For Club/All class, include finished pilots (they need handicap deltas)
        // For regatta tasks, still exclude them from racing calculations
        // Note: Finished pilots will have predictedFinishMinutesHandicap calculated from finishElapsedMinutes
        return Number.isFinite(entry.predictedFinishMinutesHandicap);
      });

      if (entriesWithHandicapTime.length > 0) {
        // Find handicap-corrected leader (best handicap-corrected time)
        let handicapLeader = null;
        entriesWithHandicapTime.forEach(entry => {
          if (!handicapLeader || entry.predictedFinishMinutesHandicap < handicapLeader.predictedFinishMinutesHandicap - 1e-6) {
            handicapLeader = entry;
          }
        });

        if (handicapLeader) {
          const leaderHandicapTime = handicapLeader.predictedFinishMinutesHandicap;

          // Calculate handicap-corrected deltas and override regular delta
          entriesWithHandicapTime.forEach(entry => {
            const deltaMinutesHandicap = entry.predictedFinishMinutesHandicap - leaderHandicapTime;

            // Override the regular delta fields with handicap values
            entry.deltaToLeaderMinutes = deltaMinutesHandicap;
            entry.deltaToLeaderMinutesHandicap = deltaMinutesHandicap;
            entry.deltaToLeaderStr = deltaMinutesHandicap < 1e-6 ? 'Leader' : formatDeltaMinutes(deltaMinutesHandicap);

            if (entry.isFinished) {
              entry.hasFrozenDelta = true;
            }

            persistHandicapDelta(entry.cookie, deltaMinutesHandicap);

            if (shouldLogDebug) {
              console.log(`   ${entry.cn}: handicapTime=${entry.predictedFinishMinutesHandicap.toFixed(2)}, delta=${entry.deltaToLeaderStr}`);
            }
          });
        }
      }
    }

    if (task?.task_type === 'AAT') {
      applyAatProjectedSpeedDeltas(
        aircraftWithDistance,
        task,
        persistHandicapDelta,
        shouldLogDebug
      );
    }

    // For AAT and non-regatta tasks: sort all pilots by projected delta.
    // For racing regatta tasks: separate finished pilots and sort by finish order.
    if (!isRegattaTask || task?.task_type === 'AAT') {
      // Sort everyone by delta to leader (contains handicap delta for Club/All class).
      const sortByTaskTime = (a, b) => {
        if (a.isDnf && !b.isDnf) return 1;
        if (!a.isDnf && b.isDnf) return -1;

        const aDelta = a.deltaToLeaderMinutes;
        const bDelta = b.deltaToLeaderMinutes;
        const aHasDelta = Number.isFinite(aDelta);
        const bHasDelta = Number.isFinite(bDelta);

        // Sort by delta first
        if (aHasDelta && bHasDelta && Math.abs(aDelta - bDelta) > 1e-6) {
          return aDelta - bDelta;
        } else if (aHasDelta && !bHasDelta) {
          return -1;
        } else if (!aHasDelta && bHasDelta) {
          return 1;
        }

        // Fallback to task time if deltas are equal or missing
        const aTaskTime = task?.task_type === 'AAT'
          ? (a.projectedFinalElapsedMinutes ?? (a.status === 'finished'
            ? (a.finishElapsedMinutes ?? a.predictedFinishMinutes ?? Infinity)
            : (a.predictedFinishMinutes ?? Infinity)))
          : (a.status === 'finished'
            ? (a.finishElapsedMinutes ?? a.predictedFinishMinutes ?? Infinity)
            : (a.predictedFinishMinutes ?? Infinity));
        const bTaskTime = task?.task_type === 'AAT'
          ? (b.projectedFinalElapsedMinutes ?? (b.status === 'finished'
            ? (b.finishElapsedMinutes ?? b.predictedFinishMinutes ?? Infinity)
            : (b.predictedFinishMinutes ?? Infinity)))
          : (b.status === 'finished'
            ? (b.finishElapsedMinutes ?? b.predictedFinishMinutes ?? Infinity)
            : (b.predictedFinishMinutes ?? Infinity));

        if (Math.abs(aTaskTime - bTaskTime) > 1e-6) {
          return aTaskTime - bTaskTime;
        }

        // Final fallback: CN alphabetically
        return a.cn.localeCompare(b.cn);
      };

      displayEntries = [...aircraftWithDistance].sort(sortByTaskTime);

    } else {
      // Regatta: Separate finished and active, sort by finish order and ETF delta respectively
      const finishedEntries = [];
      const activeEntries = [];
      aircraftWithDistance.forEach(entry => {
        if (entry.status === 'finished') {
          finishedEntries.push(entry);
        } else {
          activeEntries.push(entry);
        }
      });

      // Finished pilots: maintain finish order and freeze leaderboard position
      finishedEntries.sort((a, b) => {
        const orderA = Number.isFinite(a.finishOrder) ? a.finishOrder : Number.POSITIVE_INFINITY;
        const orderB = Number.isFinite(b.finishOrder) ? b.finishOrder : Number.POSITIVE_INFINITY;
        if (orderA !== orderB) {
          return orderA - orderB;
        }

        const finishTimeA = Number.isFinite(a.finishTimeMs) ? a.finishTimeMs : Number.POSITIVE_INFINITY;
        const finishTimeB = Number.isFinite(b.finishTimeMs) ? b.finishTimeMs : Number.POSITIVE_INFINITY;
        if (finishTimeA !== finishTimeB) {
          return finishTimeA - finishTimeB;
        }

        const elapsedA = Number.isFinite(a.finishElapsedMinutes) ? a.finishElapsedMinutes : Number.POSITIVE_INFINITY;
        const elapsedB = Number.isFinite(b.finishElapsedMinutes) ? b.finishElapsedMinutes : Number.POSITIVE_INFINITY;
        if (Math.abs(elapsedA - elapsedB) > 1e-6) {
          return elapsedA - elapsedB;
        }

        return a.cn.localeCompare(b.cn);
      });

      // Active pilots: preserve existing ETF-based ordering
      const sortActiveEntries = (a, b) => {
        if (a.isDnf && !b.isDnf) return 1;
        if (!a.isDnf && b.isDnf) return -1;
        // Use handicap-corrected delta for Club/All class, regular delta otherwise
        const useHandicapDelta = isHandicapTaskClass(task);
        const aDelta = useHandicapDelta ? a.deltaToLeaderMinutesHandicap : a.deltaToLeaderMinutes;
        const bDelta = useHandicapDelta ? b.deltaToLeaderMinutesHandicap : b.deltaToLeaderMinutes;
        const aHasDelta = Number.isFinite(aDelta);
        const bHasDelta = Number.isFinite(bDelta);

        if (aHasDelta && bHasDelta && Math.abs(aDelta - bDelta) > 1e-6) {
          return aDelta - bDelta;
        } else if (aHasDelta && !bHasDelta) {
          return -1;
        } else if (!aHasDelta && bHasDelta) {
          return 1;
        }

        const aEta = a.etaData?.etfTotal_min;
        const bEta = b.etaData?.etfTotal_min;
        const aHasEta = Number.isFinite(aEta);
        const bHasEta = Number.isFinite(bEta);

        if (aHasEta && bHasEta) {
          return aEta - bEta;
        } else if (aHasEta) {
          return -1;
        } else if (bHasEta) {
          return 1;
        }

        // Neither has ETF - sort by official distance (descending) as fallback
        return b.official_km - a.official_km;
      };
      activeEntries.sort(sortActiveEntries);

      displayEntries = finishedEntries.concat(activeEntries);
    }
  }

  maybeApplyLiveLeaderMacCready(task, displayEntries);

  // Log debug data (throttled to every 10 seconds)
  if (shouldLogDebug && etaDebugData.length > 0) {
    console.table(etaDebugData);
  }

  // Clean up private debug references before rendering
  aircraftWithDistance.forEach(entry => {
    if (entry._debugEntry) {
      delete entry._debugEntry;
    }
  });

  // Keep ETF/color data fresh every replay sample, but throttle the heavy DOM and
  // localStorage/overlay work separately so aircraft colors and trails stay live.
  if (isReplayLikeMode && !shouldRenderReplayUi) {
    return;
  }

  // Update table using DOM recycling to preserve click targets during playback
  if (displayEntries.length === 0) {
    distanceStatsBody.innerHTML = '<tr><td colspan="16" class="no-data">No distance data yet</td></tr>';
  } else {
    // Build a map of existing rows by cookie for reuse
    const existingRows = new Map();
    Array.from(distanceStatsBody.children).forEach(row => {
      const cookie = row.dataset?.cookie;
      if (cookie) {
        existingRows.set(cookie, row);
      }
    });

    // Track which cookies are in the new display
    const currentCookies = new Set();

    // Update or create rows for each entry
    displayEntries.forEach((data, displayIndex) => {
      const cookieStr = String(data.cookie);
      currentCookies.add(cookieStr);

      let row = existingRows.get(cookieStr);
      const isPreStart = data.isPreStart === true;

      // Compute display values
      let etfTotalStr = isPreStart ? '' : '--:--';
      let etfCruiseStr = isPreStart ? '' : '--:--';
      let etfClimbStr = isPreStart ? '' : '--:--';
      let ddhStr = isPreStart ? '' : '--';
      let fgSpeedStr = isPreStart ? '' : '--';
      let etfDeltaStr = isPreStart ? '' : (data.deltaToLeaderStr || '--:--');
      let xcSpeedStr = isPreStart ? '' : '--';
      const liveXcPerfA = Number.isFinite(data.etfDeltaSecondsA) ? data.etfDeltaSecondsA : (etfDeltaByAircraft.get(data.cookie) ?? null);
      const liveXcPerfB = Number.isFinite(data.etfDeltaSecondsB) ? data.etfDeltaSecondsB : (etfDeltaByAircraftB.get(data.cookie) ?? null);
      const sampledXcPerfA = sampledEtfDeltasA.get(data.cookie)?.value ?? null;
      const sampledXcPerfB = sampledEtfDeltasB.get(data.cookie)?.value ?? null;
      const displayXcPerfA = isPreStart ? null : (Number.isFinite(sampledXcPerfA) ? sampledXcPerfA : liveXcPerfA);
      const displayXcPerfB = isPreStart ? null : (Number.isFinite(sampledXcPerfB) ? sampledXcPerfB : liveXcPerfB);

      if (!isPreStart && data.etaData) {
        etfTotalStr = formatTimeMMSS(data.etaData.etfTotal_min);
        etfCruiseStr = formatTimeMMSS(data.etaData.etfCruise_min);
        etfClimbStr = formatTimeMMSS(data.etaData.etfClimb_min);
        ddhStr = formatFinishAltDelta(data.etaData.finishAltDelta_m, config.units);
        fgSpeedStr = data.etaData.finalGlideSpeed_kmh.toFixed(0);
      }

      if (!isPreStart && Number.isFinite(data.xcSpeedKmh)) {
        xcSpeedStr = data.xcSpeedKmh.toFixed(1);
      }

      const isFinished = data.status === 'finished';
      const isOffline = data.isOffline === true || isPreStart;
      const rowClass = isOffline ? 'offline-pilot' : (isFinished ? 'finished-pilot' : '');
      const cnRaw = data.cn;
      const nameRaw = data.pilotName || null;
      const finishMark = isFinished ? ' ✓' : '';
      const cnDisplay = leaderboardShowNames && nameRaw
        ? nameRaw + finishMark
        : cnRaw + finishMark;
      const cnTooltip = leaderboardShowNames
        ? (cnRaw || '')
        : (nameRaw || '');
      const gliderDisplay = data.gliderType || '';
      const isVisible = !hiddenCookies.has(data.cookie);

      let isNewRow = false;
      if (!row) {
        // Create new row with stable structure
        isNewRow = true;
        row = document.createElement('tr');
        row.dataset.cookie = cookieStr;
        row.innerHTML = `
          <td class="visibility-cell" data-cookie="${cookieStr}">
            <div class="visibility-checkbox"></div>
          </td>
          <td class="col-rank"></td>
          <td class="col-cn"></td>
          <td class="col-glider minimal-handicap-col expanded-col"></td>
          <td class="col-delta"></td>
          <td class="aat-only-col expanded-col col-delta-nomin"></td>
          <td class="expanded-col col-window-a-xc"></td>
          <td class="expanded-col col-window-b-xc"></td>
          <td class="expanded-col col-official"></td>
          <td class="expanded-col col-real"></td>
          <td class="expanded-col col-xc"></td>
          <td class="expanded-col col-total"></td>
          <td class="expanded-col col-cruise"></td>
          <td class="expanded-col col-climb"></td>
          <td class="expanded-col col-ddh"></td>
          <td class="expanded-col col-fgs"></td>
        `;

        // Cache cell references to avoid repeated querySelector calls (major perf improvement)
        row._cellCache = {
          checkbox: row.querySelector('.visibility-checkbox'),
          rank: row.querySelector('.col-rank'),
          cn: row.querySelector('.col-cn'),
          glider: row.querySelector('.col-glider'),
          delta: row.querySelector('.col-delta'),
          deltaNoMin: row.querySelector('.col-delta-nomin'),
          windowAXc: row.querySelector('.col-window-a-xc'),
          windowBXc: row.querySelector('.col-window-b-xc'),
          official: row.querySelector('.col-official'),
          real: row.querySelector('.col-real'),
          xc: row.querySelector('.col-xc'),
          total: row.querySelector('.col-total'),
          cruise: row.querySelector('.col-cruise'),
          climb: row.querySelector('.col-climb'),
          ddh: row.querySelector('.col-ddh'),
          fgs: row.querySelector('.col-fgs')
        };
      }

      // Update row class if changed
      if (row.className !== rowClass) {
        row.className = rowClass;
      }

      // Use cached cell references for performance
      const cells = row._cellCache;

      // Update checkbox state
      if (cells.checkbox) {
        cells.checkbox.classList.toggle('checked', isVisible);
      }

      // Update cell text content (only if changed to minimize reflows)
      const updateCell = (cell, text) => {
        if (cell && cell.textContent !== text) {
          cell.textContent = text;
        }
      };

      // Display rank directly (no dampening)
      const displayRank = isPreStart ? '' : String(displayIndex + 1);
      updateCell(cells.rank, displayRank);

      // CN / Name toggle with tooltip
      if (cells.cn) {
        if (cells.cn.textContent !== cnDisplay || cells.cn.dataset.needsRefresh) {
          cells.cn.textContent = cnDisplay;
          delete cells.cn.dataset.needsRefresh;
        }
        if (cells.cn.title !== cnTooltip) {
          cells.cn.title = cnTooltip;
        }
      }

      updateCell(cells.glider, gliderDisplay);

      // Update ETA delta cell with color matching the aircraft's Recent XC Pace trail color
      const deltaCell = cells.delta;
      if (deltaCell) {
        if (deltaCell.textContent !== etfDeltaStr) {
          deltaCell.textContent = etfDeltaStr;
        }
        // Use the aircraft's actual etfDeltaSeconds (same data that colors trails/icons)
        const aircraftEtfDelta = isPreStart ? null : etfDeltaByAircraft.get(data.cookie);
        if (Number.isFinite(aircraftEtfDelta)) {
          const trendSeconds = config.trendWindowSec || 180;
          const etfColor = aircraftManager?.getETFDeltaColor?.(aircraftEtfDelta, trendSeconds);
          if (etfColor) {
            // Ensure minimum brightness for readability on dark background
            deltaCell.style.color = ensureMinBrightness(etfColor, 50);
          } else {
            deltaCell.style.color = '';
          }
        } else {
          deltaCell.style.color = '';
        }
      }

      // Update AAT unclamped delta cell
      const deltaNoMinStr = isPreStart ? '' : (data.deltaNoMinStr || '');
      updateCell(cells.deltaNoMin, deltaNoMinStr);

      updateCell(cells.official, Number.isFinite(data.official_km) ? data.official_km.toFixed(2) : '');

      // XC Pace A column (sim-time sampled by configured update interval)
      const windowAStr = isPreStart ? '' : formatSignedSeconds(displayXcPerfA);
      if (cells.windowAXc) {
        if (cells.windowAXc.textContent !== windowAStr) cells.windowAXc.textContent = windowAStr;
        if (Number.isFinite(displayXcPerfA)) {
          const trendSecA = config.trendWindowSec || 180;
          const colorA = aircraftManager?.getETFDeltaColor?.(displayXcPerfA, trendSecA);
          cells.windowAXc.style.color = colorA ? ensureMinBrightness(colorA, 50) : '';
        } else {
          cells.windowAXc.style.color = '';
        }
      }

      // XC Pace B column (sim-time sampled by configured update interval)
      const windowBStr = isPreStart ? '' : formatSignedSeconds(displayXcPerfB);
      if (cells.windowBXc) {
        if (cells.windowBXc.textContent !== windowBStr) cells.windowBXc.textContent = windowBStr;
        if (Number.isFinite(displayXcPerfB)) {
          const colorB = aircraftManager?.getETFDeltaColor?.(displayXcPerfB, trendWindowSecondsB);
          cells.windowBXc.style.color = colorB ? ensureMinBrightness(colorB, 50) : '';
        } else {
          cells.windowBXc.style.color = '';
        }
      }

      updateCell(cells.real, Number.isFinite(data.real_km) ? data.real_km.toFixed(2) : '');
      updateCell(cells.xc, xcSpeedStr);
      updateCell(cells.total, etfTotalStr);
      updateCell(cells.cruise, etfCruiseStr);
      updateCell(cells.climb, etfClimbStr);
      updateCell(cells.ddh, ddhStr);
      updateCell(cells.fgs, fgSpeedStr);

      // Mark row with its target position for DOM ordering
      row._targetIndex = displayIndex;

      // Add new rows to DOM
      if (isNewRow) {
        distanceStatsBody.appendChild(row);
      }
    });

    // Remove rows that are no longer in the display
    existingRows.forEach((row, cookie) => {
      if (!currentCookies.has(cookie)) {
        row.remove();
      }
    });

    // Remove any "no data" placeholder rows
    const noDataRow = distanceStatsBody.querySelector('.no-data');
    if (noDataRow) {
      noDataRow.closest('tr')?.remove();
    }

    // Check if rows need reordering (only reorder when sort order changes)
    const rows = Array.from(distanceStatsBody.children);
    let needsReorder = false;
    for (let i = 0; i < rows.length - 1; i++) {
      if ((rows[i]._targetIndex ?? i) > (rows[i + 1]._targetIndex ?? i + 1)) {
        needsReorder = true;
        break;
      }
    }

    if (needsReorder) {
      const sortedRows = rows
        .filter(r => r.dataset?.cookie)
        .sort((a, b) => (a._targetIndex ?? 0) - (b._targetIndex ?? 0));
      const shouldAnimateReorder =
        !isReplayLikeMode ||
        !replayManager?.isPlaying ||
        sortedRows.length <= 12;

      if (!shouldAnimateReorder) {
        sortedRows.forEach(row => {
          row.style.transform = '';
          row.style.transition = '';
          distanceStatsBody.appendChild(row);
        });
      } else {
        // FLIP animation: First, Last, Invert, Play
        const rowPositions = new Map();
        rows.forEach(row => {
          if (row.dataset?.cookie) {
            rowPositions.set(row.dataset.cookie, row.getBoundingClientRect().top);
          }
        });

        sortedRows.forEach(row => distanceStatsBody.appendChild(row));

        sortedRows.forEach(row => {
          const cookie = row.dataset.cookie;
          const oldTop = rowPositions.get(cookie);
          if (oldTop === undefined) {
            return;
          }

          const newTop = row.getBoundingClientRect().top;
          const deltaY = oldTop - newTop;

          if (Math.abs(deltaY) <= 1) {
            return;
          }

          row.style.transform = `translateY(${deltaY}px)`;
          row.style.transition = 'none';

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              row.style.transition = 'transform 0.6s ease-out';
              row.style.transform = 'translateY(0)';

              const cleanup = () => {
                row.style.transform = '';
                row.style.transition = '';
                row.removeEventListener('transitionend', cleanup);
              };
              row.addEventListener('transitionend', cleanup, { once: true });
              setTimeout(cleanup, 700);
            });
          });
        });
      }
    }
  }

  // Publish leaderboard data to overlay via localStorage (avoids background-tab throttling)
  // Use debounced write to reduce I/O frequency during replay
  const headerLabel = 'ETA gap to leader';
  scheduleLeaderboardWrite({
    headerLabel,
    isHandicappedRace: !!useHandicapDelta,
    units: config.units,
    trendColorScheme: config.trendColorScheme,
    colorSensitivity: config.colorSensitivity,
    overlayPermanentColumns: config.overlayPermanentColumns,
    overlayRotatingColumns: config.overlayRotatingColumns,
    overlayRotationSec: config.overlayRotationSec,
    overlayXcPerfAUpdateSec: config.overlayXcPerfAUpdateSec,
    overlayXcPerfBUpdateSec: config.overlayXcPerfBUpdateSec,
    overlayPositionWindowSec: config.overlayPositionWindowSec,
    overlayPositionRefreshSec: config.overlayPositionRefreshSec,
    simTimeSec: Number.isFinite(latestSimTimeSec) ? latestSimTimeSec : null,
    timestamp: Date.now(),
    entries: displayEntries.map((d, i) => ({
      cookie: d.cookie,
      rank: d.isPreStart ? null : (i + 1),
      cn: d.cn,
      glider: d.gliderType || null,
      pilotName: d.pilotName || null,
      deltaToLeaderStr: d.isPreStart ? '' : (d.deltaToLeaderStr || '--:--'),
      officialKm: d.isPreStart ? null : (d.official_km ?? null),
      realKm: d.isPreStart ? null : (d.real_km ?? null),
      xcSpeedKmh: d.isPreStart ? null : (d.xcSpeedKmh ?? null),
      etfTotalMin: d.isPreStart ? null : (d.etaData?.etfTotal_min ?? null),
      etfCruiseMin: d.isPreStart ? null : (d.etaData?.etfCruise_min ?? null),
      etfClimbMin: d.isPreStart ? null : (d.etaData?.etfClimb_min ?? null),
      finishAltDeltaM: d.isPreStart ? null : (d.etaData?.finishAltDelta_m ?? null),
      fgSpeedKmh: d.isPreStart ? null : (d.etaData?.finalGlideSpeed_kmh ?? null),
      isFinished: d.isFinished || false,
      isOffline: d.isOffline || false,
      isPreStart: d.isPreStart || false,
      isDnf: d.isDnf || false,
      raceElapsedSec: d.isPreStart ? null : (Number.isFinite(d.raceElapsedSec) ? d.raceElapsedSec : null),
      etfDeltaSecondsA: d.isPreStart ? null : (etfDeltaByAircraft.get(d.cookie) ?? null),
      etfDeltaSecondsB: d.isPreStart ? null : (etfDeltaByAircraftB.get(d.cookie) ?? null),
      trendWindowSecA: trendWindowSeconds,
      trendWindowSecB: trendWindowSecondsB,
      xcPerfASeconds: d.isPreStart ? null : (sampledEtfDeltasA.get(d.cookie)?.value ?? (etfDeltaByAircraft.get(d.cookie) ?? null)),
      xcPerfBSeconds: d.isPreStart ? null : (sampledEtfDeltasB.get(d.cookie)?.value ?? (etfDeltaByAircraftB.get(d.cookie) ?? null))
    }))
  });
}

/**
 * Apply visibility filter to map - hides/shows aircraft markers and trails
 * Works for both 2D (Leaflet) and 3D (Cesium) views
 */
function applyVisibilityToMap() {
  // Apply to 2D manager
  if (aircraftManager && typeof aircraftManager.setHiddenCookies === 'function') {
    aircraftManager.setHiddenCookies(hiddenCookies);
  }
  // Apply to 3D manager (handles both aircraft entities and trails)
  if (mapManager && typeof mapManager.setHiddenCookies === 'function') {
    mapManager.setHiddenCookies(hiddenCookies);
  }
}

/**
 * Toggle visibility for a single aircraft
 */
function toggleAircraftVisibility(cookie) {
  if (hiddenCookies.has(cookie)) {
    hiddenCookies.delete(cookie);
  } else {
    hiddenCookies.add(cookie);
  }

  // Update the checkbox UI immediately
  const cell = distanceStatsBody?.querySelector(`.visibility-cell[data-cookie="${cookie}"]`);
  if (cell) {
    const checkbox = cell.querySelector('.visibility-checkbox');
    if (checkbox) {
      checkbox.classList.toggle('checked', !hiddenCookies.has(cookie));
    }
  }

  // Apply to map
  applyVisibilityToMap();
}

/**
 * Show all aircraft on the map
 */
function showAllAircraft() {
  hiddenCookies.clear();

  // Update all checkboxes
  distanceStatsBody?.querySelectorAll('.visibility-checkbox').forEach(cb => {
    cb.classList.add('checked');
  });

  applyVisibilityToMap();
}

/**
 * Hide all aircraft on the map
 */
function hideAllAircraft() {
  // Get all cookies from the leaderboard rows
  distanceStatsBody?.querySelectorAll('tr[data-cookie]').forEach(row => {
    const cookie = row.dataset.cookie;
    if (cookie) {
      hiddenCookies.add(cookie);
    }
  });

  // Also add any tracked aircraft from state tracker
  aircraftStateTracker?.getAllStates().forEach((_, cookie) => {
    hiddenCookies.add(cookie);
  });

  // Update all checkboxes
  distanceStatsBody?.querySelectorAll('.visibility-checkbox').forEach(cb => {
    cb.classList.remove('checked');
  });

  applyVisibilityToMap();
}

// Setup visibility control event listeners
function setupVisibilityControls() {
  const showAllBtn = document.getElementById('show-all-btn');
  const hideAllBtn = document.getElementById('hide-all-btn');

  if (showAllBtn) {
    showAllBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      showAllAircraft();
    });
  }

  if (hideAllBtn) {
    hideAllBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      hideAllAircraft();
    });
  }

  // Delegate click events for individual row checkboxes
  // Use mousedown instead of click - fires earlier and more reliably during rapid DOM updates
  if (distanceStatsBody) {
    distanceStatsBody.addEventListener('mousedown', (e) => {
      const cell = e.target.closest('.visibility-cell');
      if (cell) {
        e.preventDefault(); // Prevent text selection
        e.stopPropagation();
        const cookie = cell.dataset.cookie;
        if (cookie) {
          toggleAircraftVisibility(cookie);
        }
      }
    });
  }
}

// Initialize visibility controls
setupVisibilityControls();

// Handle follow button clicks in popups (event delegation)
document.addEventListener('click', (e) => {
  if (e.target.classList.contains('popup-follow-btn')) {
    const cookie = parseInt(e.target.getAttribute('data-cookie'));
    if (e.target.classList.contains('following')) {
      aircraftManager.unfollowAircraft();
    } else {
      aircraftManager.followAircraft(cookie);
    }
  }
});

// Stop following if user manually pans the map
let userPanning = false;
map.on('movestart', () => {
  if (!userPanning && aircraftManager.followingCookie) {
    // Check if this move was initiated by user (not by follow)
    setTimeout(() => {
      userPanning = false;
    }, 100);
  }
});

map.on('dragstart', () => {
  if (aircraftManager.followingCookie) {
    aircraftManager.unfollowAircraft();
  }
});

// UI Elements (with fallback to left panel versions)
const statusIndicator =
  document.getElementById('status-indicator') ||
  document.getElementById('left-status-indicator');

const statusText =
  document.getElementById('status-text') ||
  document.getElementById('left-status-text');
const aircraftCountEl = document.getElementById('aircraft-count');
const serverCountEl = document.getElementById('server-count');
const viewerCountEl = document.getElementById('viewer-count');
const serversContentEl = document.getElementById('servers-content');
const leftServersContentEl = document.getElementById('left-servers-content'); // Left panel servers
const taskListEl = document.getElementById('task-list');
const leftTaskListEl = document.getElementById('left-task-list'); // Left panel tasks

/**
 * Updates the connection status indicator
 */
function updateStatus(connected) {
  if (connected) {
    statusIndicator.classList.remove('disconnected');
    statusIndicator.classList.add('connected');
    statusText.textContent = 'Connected';
    updateLeftPanelStatus('connected', 'Connected');
  } else {
    statusIndicator.classList.remove('connected');
    statusIndicator.classList.add('disconnected');
    statusText.textContent = 'Disconnected';
    updateLeftPanelStatus('disconnected', 'Disconnected');
  }
}

/**
 * Updates the aircraft count display
 */
function updateAircraftCount() {
  const count = aircraftManager.getAircraftCount();
  aircraftCountEl.textContent = count;

  // Update left panel stat
  const leftAircraftStat = document.getElementById('stat-aircraft');
  if (leftAircraftStat) {
    leftAircraftStat.textContent = count;
  }
}

/**
 * Updates the server count display
 */
function updateServerCount() {
  serverCountEl.textContent = activeServers.size;

  // Update left panel stat
  const leftServerStat = document.getElementById('stat-servers');
  if (leftServerStat) {
    leftServerStat.textContent = activeServers.size;
  }
}
/**
 * Updates the servers panel with detailed server information
 */
function updateServersPanel() {
  // Update both old panel and left panel
  const panels = [serversContentEl, leftServersContentEl].filter(el => el);

  panels.forEach(panel => {
    // Clear existing content
    panel.innerHTML = '';

    // Calculate total aircraft across all servers
    const totalAircraft = activeServers.size === 0 ? 0 : Array.from(activeServers.values()).reduce((sum, server) => sum + server.count, 0);

    // Always show "All Servers" option
    const allItem = document.createElement('div');
    allItem.className = 'stat-item server-item' + (selectedServers.has('all') ? ' active' : '');
    allItem.innerHTML = `
      <span class="stat-label">All Servers</span>
      <span class="stat-value">${totalAircraft}</span>
    `;
    allItem.addEventListener('click', () => handleServerSelection('all'));
    panel.appendChild(allItem);

    if (activeServers.size === 0) {
      return;
    }

    // Show individual servers
    activeServers.forEach((server, serverKey) => {
      const serverItem = document.createElement('div');
      serverItem.className = 'stat-item server-item' + (selectedServers.has(serverKey) ? ' active' : '');
      serverItem.innerHTML = `
        <span class="stat-label">${server.name}</span>
        <span class="stat-value">${server.count}</span>
      `;
      serverItem.addEventListener('click', () => handleServerSelection(serverKey));
      panel.appendChild(serverItem);
    });
  });
}

/**
 * Gets time range for "Live Now" filter (UTC now - 1 hour to UTC now + 4 hours)
 * Shows tasks that started within the last hour or will start in the next 4 hours
 */
function getLiveNowTimeRange() {
  const now = new Date();
  const startTime = new Date(now.getTime() - 1 * 60 * 60 * 1000); // -1 hour
  const endTime = new Date(now.getTime() + 4 * 60 * 60 * 1000); // +4 hours

  return {
    startTime: startTime.toISOString(),
    endTime: endTime.toISOString()
  };
}

function getTaskAssociatedServerNames(task) {
  const names = new Set();

  if (Array.isArray(task?.associated_servers)) {
    task.associated_servers.forEach(name => {
      const normalized = String(name || '').trim();
      if (normalized) names.add(normalized);
    });
  }

  const serverGroup = String(task?.server_group || '').trim();
  if (serverGroup) {
    names.add(serverGroup);
  }

  return names;
}

function getTaskRoutePoints(task) {
  if (!task || !Array.isArray(task.turnpoints) || task.turnpoints.length === 0) {
    return [];
  }

  const points = [];
  task.turnpoints.forEach((tp, index) => {
    if (index === 0 && task.startScoredPoint) {
      points.push({ lat: task.startScoredPoint.lat, lon: task.startScoredPoint.lon });
      return;
    }

    if (index === task.turnpoints.length - 1 && task.finishScoredPoint) {
      points.push({ lat: task.finishScoredPoint.lat, lon: task.finishScoredPoint.lon });
      return;
    }

    points.push({ lat: tp.Lat, lon: tp.Lon });
  });

  return points;
}

function distancePointToSegmentKm(lat, lon, startPoint, endPoint) {
  if (!startPoint || !endPoint) return Infinity;

  const refLatRad = ((startPoint.lat + endPoint.lat + lat) / 3) * Math.PI / 180;
  const metersPerDegLat = 111_320;
  const metersPerDegLon = Math.cos(refLatRad) * 111_320;

  const px = (lon - startPoint.lon) * metersPerDegLon;
  const py = (lat - startPoint.lat) * metersPerDegLat;
  const sx = (endPoint.lon - startPoint.lon) * metersPerDegLon;
  const sy = (endPoint.lat - startPoint.lat) * metersPerDegLat;
  const segLenSq = sx * sx + sy * sy;

  if (segLenSq <= 1e-6) {
    return haversineDistance(lat, lon, startPoint.lat, startPoint.lon) / 1000;
  }

  const t = Math.max(0, Math.min(1, ((px * sx) + (py * sy)) / segLenSq));
  const closestX = sx * t;
  const closestY = sy * t;
  const dx = px - closestX;
  const dy = py - closestY;
  return Math.sqrt(dx * dx + dy * dy) / 1000;
}

function calculateTaskProximityKm(task, position) {
  if (!task || !Array.isArray(task.turnpoints) || task.turnpoints.length === 0 || !position) {
    return Infinity;
  }

  let minDistanceKm = Infinity;

  task.turnpoints.forEach(tp => {
    const radiusKm = Number(tp?.RadiusMeters) > 0 ? Number(tp.RadiusMeters) / 1000 : 0;
    const centerDistanceKm = haversineDistance(position.lat, position.lon, tp.Lat, tp.Lon) / 1000;
    minDistanceKm = Math.min(minDistanceKm, Math.max(0, centerDistanceKm - radiusKm));
  });

  const routePoints = getTaskRoutePoints(task);
  for (let i = 0; i < routePoints.length - 1; i++) {
    minDistanceKm = Math.min(
      minDistanceKm,
      distancePointToSegmentKm(position.lat, position.lon, routePoints[i], routePoints[i + 1])
    );
  }

  return minDistanceKm;
}

function buildLiveTaskContexts() {
  const nowMs = Date.now();
  const livePositions = Array.from(latestLivePositions.values()).filter(position =>
    Number.isFinite(position?.lat) &&
    Number.isFinite(position?.lon)
  );

  return activeTasks
    .map(task => {
      const startMs = Date.parse(task?.ds_helper_start_time || task?.start_time || '');
      const hasStartTime = Number.isFinite(startMs);
      const timeDeltaMs = Number.isFinite(startMs) ? startMs - nowMs : null;
      const isTimeLive = Number.isFinite(timeDeltaMs) && timeDeltaMs <= 0 && Math.abs(timeDeltaMs) <= (4 * 60 * 60 * 1000);
      const isUpcoming = Number.isFinite(timeDeltaMs) && timeDeltaMs > 0 && timeDeltaMs <= (4 * 60 * 60 * 1000);
      const taskServers = getTaskAssociatedServerNames(task);

      let proximityCount = 0;
      let proximitySumKm = 0;
      let serverMatchCount = 0;

      livePositions.forEach(position => {
        const serverName = String(position.serverName || '').trim();
        if (taskServers.size > 0) {
          if (!serverName || !taskServers.has(serverName)) {
            return;
          }
          serverMatchCount++;
        }

        const proximityKm = calculateTaskProximityKm(task, position);
        if (proximityKm <= LIVE_TASK_PROXIMITY_THRESHOLD_KM) {
          proximityCount++;
          proximitySumKm += proximityKm;
        }
      });

      return {
        task,
        proximityCount,
        proximityAvgKm: proximityCount > 0 ? (proximitySumKm / proximityCount) : Infinity,
        serverMatchCount,
        hasStartTime,
        isTimeLive,
        isUpcoming,
        startMs
      };
    })
    .filter(context => {
      const passesTimeGate = !context.hasStartTime || context.isTimeLive || context.isUpcoming;
      return passesTimeGate && (context.proximityCount > 0 || context.isTimeLive || context.isUpcoming);
    })
    .sort((a, b) => {
      if (b.proximityCount !== a.proximityCount) return b.proximityCount - a.proximityCount;
      if (b.serverMatchCount !== a.serverMatchCount) return b.serverMatchCount - a.serverMatchCount;
      if (a.proximityAvgKm !== b.proximityAvgKm) return a.proximityAvgKm - b.proximityAvgKm;
      if (a.isTimeLive !== b.isTimeLive) return a.isTimeLive ? -1 : 1;
      if (a.isUpcoming !== b.isUpcoming) return a.isUpcoming ? -1 : 1;
      if (Number.isFinite(a.startMs) && Number.isFinite(b.startMs)) {
        return Math.abs(a.startMs - nowMs) - Math.abs(b.startMs - nowMs);
      }
      return 0;
    });
}

function getVisibleTasksForCurrentFilter() {
  if (selectedTaskFilter === 'liveNow') {
    return buildLiveTaskContexts();
  }

  return activeTasks.map(task => ({
    task,
    proximityCount: 0,
    proximityAvgKm: Infinity,
    serverMatchCount: 0,
    isTimeLive: false,
    isUpcoming: false,
    startMs: Date.parse(task?.ds_helper_start_time || task?.start_time || '')
  }));
}

function hasExplicitUrlTaskState() {
  const params = new URLSearchParams(window.location.search);
  return params.has('taskid') || params.has('replayTaskId') || params.has('ssUrl') || params.has('wgUrl');
}

function maybeAutoSelectLiveTask() {
  if (!autoLiveTaskSelectionPending || currentMode !== 'live' || selectedTask !== null || replaySelectedTask !== null) {
    return false;
  }
  if (hasExplicitUrlTaskState()) {
    return false;
  }

  const liveContexts = buildLiveTaskContexts();
  const bestContext = liveContexts.find(context => context.proximityCount > 0);
  if (!bestContext?.task?.id) {
    return false;
  }

  autoLiveTaskSelectionPending = false;
  handleTaskSelection(bestContext.task.id);
  return true;
}

function scheduleLiveTaskRefresh() {
  if (liveTaskRefreshTimer) {
    return;
  }

  liveTaskRefreshTimer = setTimeout(() => {
    liveTaskRefreshTimer = null;
    if (selectedTaskFilter === 'liveNow') {
      updateTasksPanel();
    }
    maybeAutoSelectLiveTask();
  }, 250);
}

function storeLiveTaskPosition(cookieKey, position) {
  const key = String(cookieKey || '');
  if (!key || !Number.isFinite(position?.lat) || !Number.isFinite(position?.lon)) {
    return;
  }

  latestLivePositions.set(key, {
    lat: position.lat,
    lon: position.lon,
    serverName: position.serverName || '',
    portNumber: position.portNumber || '',
    timestamp: position.timestamp instanceof Date ? position.timestamp : new Date(position.timestamp)
  });
}

function setMacCreadyValue(value, reason = '') {
  if (!Number.isFinite(value) || value <= 0) {
    console.log('MacCready auto-set skipped (invalid value):', value, reason);
    return;
  }
  console.log('MacCready auto-set requested:', { value, reason });
  const mcRounded = Math.round(value * 10) / 10;
  config.maccready = mcRounded;
  const macInput = document.getElementById('maccready-value');
  if (macInput) {
    macInput.value = mcRounded;
  }
  saveSettings();
  notifyEtfInputChange();
  console.log(`MacCready auto-set to ${mcRounded} m/s${reason ? ` (${reason})` : ''}`);
}

function setNettoValue(value, reason = '') {
  if (!Number.isFinite(value)) {
    return;
  }
  const rounded = Math.round(value * 10) / 10;
  config.netto = rounded;
  const nettoInput = document.getElementById('netto-value');
  if (nettoInput) {
    nettoInput.value = rounded;
  }
  saveSettings();
  notifyEtfInputChange();
  console.log(`Netto auto-set to ${rounded} m/s${reason ? ` (${reason})` : ''}`);
}

function setWingLoadingValue(value, reason = '') {
  if (!Number.isFinite(value) || value <= 0) {
    return;
  }
  const rounded = Math.round(value * 10) / 10;
  config.wingLoading = rounded;
  const wlInput = document.getElementById('wing-loading-value');
  if (wlInput) {
    wlInput.value = rounded;
  }
  saveSettings();
  notifyEtfInputChange();
  console.log(`Wing loading auto-set to ${rounded} kg/m²${reason ? ` (${reason})` : ''}`);
}

function resetMacCreadyAutoState() {
  macCreadyManualOverride = false;
  macCreadyWinnerTaskId = null;
  macCreadyWinnerPromise = null;
  macCreadyLastCheckMs = 0;
  macCreadyLiveLeaderTaskId = null;
  macCreadyLiveLeaderCookie = null;
  macCreadyLiveLeaderLastCheckMs = 0;
}

function getBestCruiseForMacCready(coeffs, mcSetting_ms) {
  if (!coeffs || coeffs.length < 3 || !Number.isFinite(mcSetting_ms) || mcSetting_ms <= 0) {
    return null;
  }

  const [a, b, c] = coeffs;
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) {
    return null;
  }

  let bestSpeedKmh = null;
  let bestXcSpeedKmh = -Infinity;

  for (let speedKmh = 80; speedKmh <= 280; speedKmh += 2) {
    const v_ms = speedKmh / 3.6;
    const sink_ms = a * v_ms * v_ms + b * v_ms + c; // typically negative
    const sinkAbs = Math.max(0.05, Math.abs(sink_ms));
    const xcSpeedMs = v_ms / (1 + sinkAbs / mcSetting_ms);
    const xcSpeedKmh = xcSpeedMs * 3.6;

    if (xcSpeedKmh > bestXcSpeedKmh) {
      bestXcSpeedKmh = xcSpeedKmh;
      bestSpeedKmh = speedKmh;
    }
  }

  if (!Number.isFinite(bestSpeedKmh) || bestXcSpeedKmh <= 0) {
    return null;
  }

  return { cruiseSpeedKmh: bestSpeedKmh, xcSpeedKmh: bestXcSpeedKmh };
}

/**
 * Like getBestCruiseForMacCready but with netto shifting the polar.
 * Netto > 0 means rising air (less effective sink in cruise).
 */
function getBestCruiseForMcAndNetto(coeffs, mcSetting_ms, netto_ms) {
  if (!coeffs || coeffs.length < 3 || !Number.isFinite(mcSetting_ms) || mcSetting_ms <= 0) {
    return null;
  }

  const [a, b, c] = coeffs;
  let bestSpeedKmh = null;
  let bestXcSpeedKmh = -Infinity;

  for (let speedKmh = 80; speedKmh <= 280; speedKmh += 2) {
    const v_ms = speedKmh / 3.6;
    const sink_ms = a * v_ms * v_ms + b * v_ms + c; // negative
    const effectiveSinkAbs = Math.max(0.05, Math.abs(sink_ms) - netto_ms);
    const xcSpeedMs = v_ms / (1 + effectiveSinkAbs / mcSetting_ms);
    const xcSpeedKmh = xcSpeedMs * 3.6;

    if (xcSpeedKmh > bestXcSpeedKmh) {
      bestXcSpeedKmh = xcSpeedKmh;
      bestSpeedKmh = speedKmh;
    }
  }

  if (!Number.isFinite(bestSpeedKmh) || bestXcSpeedKmh <= 0) {
    return null;
  }

  return { cruiseSpeedKmh: bestSpeedKmh, xcSpeedKmh: bestXcSpeedKmh };
}

/**
 * Find the netto value that, combined with a known MC, produces the target XC speed.
 * Uses binary search since XC speed is monotonically increasing with netto.
 */
function findNettoForSpeed(coeffs, mc_ms, targetSpeedKmh) {
  if (!Number.isFinite(targetSpeedKmh) || targetSpeedKmh <= 0 || !Number.isFinite(mc_ms) || mc_ms <= 0) {
    return null;
  }

  let lo = -3.0;
  let hi = 3.0;

  // Verify target is within reachable range
  const loResult = getBestCruiseForMcAndNetto(coeffs, mc_ms, lo);
  const hiResult = getBestCruiseForMcAndNetto(coeffs, mc_ms, hi);
  if (!loResult || !hiResult) return null;
  if (targetSpeedKmh < loResult.xcSpeedKmh || targetSpeedKmh > hiResult.xcSpeedKmh) {
    // Target out of range, clamp to nearest
    console.warn('findNettoForSpeed: target out of range', {
      targetSpeedKmh: targetSpeedKmh.toFixed(1),
      rangeMin: loResult.xcSpeedKmh.toFixed(1),
      rangeMax: hiResult.xcSpeedKmh.toFixed(1)
    });
    return null;
  }

  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    const result = getBestCruiseForMcAndNetto(coeffs, mc_ms, mid);
    if (!result) return null;

    if (result.xcSpeedKmh < targetSpeedKmh) {
      lo = mid;
    } else {
      hi = mid;
    }

    if (hi - lo < 0.001) break; // converged to ~0.001 m/s
  }

  return Math.round(((lo + hi) / 2) * 100) / 100;
}

function findMacCreadyForSpeed(coeffs, targetSpeedKmh) {
  if (!Number.isFinite(targetSpeedKmh) || targetSpeedKmh <= 0) {
    return null;
  }

  const minSearchMc = 0.5;
  const maxSearchMc = 10.0;
  let bestMc = null;
  let bestDiff = Infinity;

  for (let mc = minSearchMc; mc <= maxSearchMc; mc += 0.01) {
    const bestCruise = getBestCruiseForMacCready(coeffs, mc);
    if (!bestCruise) continue;
    const diff = Math.abs(bestCruise.xcSpeedKmh - targetSpeedKmh);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestMc = mc;
    }
  }

  return Number.isFinite(bestMc) ? Math.round(bestMc * 100) / 100 : null;
}

/**
 * Calculate winner's XC speed using altitude compensation at a specific MC baseline
 * This is used for iteratively solving for the winner's effective MC setting.
 */
function calculateWinnerXcSpeedAtMc(entry, task, coeffs, baselineMc = 2.0, gliderType = null) {
  const elapsedMinutes = entry?.finishElapsedMinutes;
  if (!Number.isFinite(elapsedMinutes) || elapsedMinutes <= 0) {
    return null;
  }

  const distanceSources = [
    entry?.real_km,
    entry?.official_km,
    task?.netTaskLength,
    task?.scoringTaskLength
  ];

  const distance_km = distanceSources.find(dist => Number.isFinite(dist) && dist > 0) || null;
  if (!distance_km) {
    return null;
  }

  // Only use the pilot's actual start altitude. Falling back to the task
  // ceiling (often 10000 m for "unlimited") produces nonsense XC speeds.
  const elapsedSeconds = elapsedMinutes * 60;
  const simpleSpeed = distance_km / (elapsedSeconds / 3600);
  const startHeight_m = Number.isFinite(entry?.startAltitude_m) ? entry.startAltitude_m : null;

  // Get finish altitude from task
  const finishAlt_m = getFinishAltitudeMeters(task);

  if (!Number.isFinite(startHeight_m) || startHeight_m <= 0.1 || !Number.isFinite(finishAlt_m) || finishAlt_m <= 0.1) {
    console.log('Winner MC: no usable start/finish altitude data, using simple dist/time', {
      cn: entry?.cn,
      simpleSpeed: simpleSpeed.toFixed(1)
    });
    return simpleSpeed;
  }

  if (!coeffs || coeffs.length < 3) {
    console.warn('Winner MC: missing polar coefficients for XC speed calculation');
    return simpleSpeed;
  }

  const bestCruise = getBestCruiseForMacCready(coeffs, baselineMc);
  if (!bestCruise) {
    console.warn('Winner MC: unable to resolve cruise speed for MC baseline', baselineMc);
    return simpleSpeed;
  }

  const [a, b, c] = coeffs;

  const mcSpeedKmh = bestCruise.cruiseSpeedKmh;
  const vOpt_ms = mcSpeedKmh / 3.6;
  const mcSinkMs = a * vOpt_ms * vOpt_ms + b * vOpt_ms + c;

  // Calculate XC speed using XCSoar altitude-compensated formula
  const elapsedTime_hours = elapsedSeconds / 3600;
  const deltaAlt_km = (finishAlt_m - startHeight_m) / 1000.0;

  const mcSinkKmh = mcSinkMs * 3.6;
  const mcGlideRatio = Math.abs(mcSinkKmh) > 0 ? mcSpeedKmh / Math.abs(mcSinkKmh) : 0;
  const equivalentTime = Math.abs(mcSinkKmh) > 0 ? deltaAlt_km / Math.abs(mcSinkKmh) : 0;
  const mcSettingKmh = baselineMc * 3.6;
  const mcAvgSpeed = mcSettingKmh !== 0 ? mcSpeedKmh / (1 + (Math.abs(mcSinkKmh) / mcSettingKmh)) : 0;

  const altitudeCredit = deltaAlt_km * mcGlideRatio;
  const timePenalty = equivalentTime * mcAvgSpeed;
  const numerator = altitudeCredit + distance_km - timePenalty;
  const xcSpeed = elapsedTime_hours > 0 ? numerator / elapsedTime_hours : 0;

  console.log('Winner MC: XC speed with altitude compensation', {
    cn: entry?.cn,
    glider: gliderType ?? entry?.gliderType ?? null,
    mcBaseline: baselineMc.toFixed(2),
    distance_km: distance_km.toFixed(2),
    elapsedSec: elapsedSeconds.toFixed(0),
    startHeight_m: startHeight_m.toFixed(0),
    finishAlt_m: finishAlt_m.toFixed(0),
    deltaAlt_km: deltaAlt_km.toFixed(3),
    xcSpeed: xcSpeed.toFixed(1)
  });

  return xcSpeed;
}

function compareFinishOrder(entryA, entryB) {
  const orderA = Number.isFinite(entryA?.finishOrder) ? entryA.finishOrder : Infinity;
  const orderB = Number.isFinite(entryB?.finishOrder) ? entryB.finishOrder : Infinity;

  if (orderA !== orderB) {
    return orderA - orderB;
  }

  const timeA = Number.isFinite(entryA?.finishTimeMs) ? entryA.finishTimeMs : Infinity;
  const timeB = Number.isFinite(entryB?.finishTimeMs) ? entryB.finishTimeMs : Infinity;

  return timeA - timeB;
}

function getHandicapFinishMinutes(entry, task = null) {
  if (!entry) {
    return null;
  }
  if (Number.isFinite(entry.predictedFinishMinutesHandicap)) {
    return entry.predictedFinishMinutesHandicap;
  }
  if (Number.isFinite(entry.finishElapsedMinutes) && entry.gliderType) {
    const handicap = getConfiguredHandicap(entry.gliderType, task);
    if (handicap) {
      return (entry.finishElapsedMinutes / 100) * handicap;
    }
  }
  return null;
}

function getFinishedAatMetrics(entry, task) {
  if (
    Number.isFinite(entry?.projectedFinalDistance_km) &&
    entry.projectedFinalDistance_km > 0 &&
    Number.isFinite(entry?.projectedFinalElapsedMinutes) &&
    entry.projectedFinalElapsedMinutes > 0
  ) {
    return {
      projectedFinalDistance_km: entry.projectedFinalDistance_km,
      projectedFinalElapsedMinutes: entry.projectedFinalElapsedMinutes
    };
  }

  return calculateProjectedAatMetrics({
    distanceFlown_km:
      (Number.isFinite(entry?.real_km) && entry.real_km > 0 ? entry.real_km : null) ??
      (Number.isFinite(entry?.official_km) && entry.official_km > 0 ? entry.official_km : null) ??
      0,
    remainingDistance_km: 0,
    elapsedMinutes: entry?.finishElapsedMinutes,
    etaRemainingMinutes: 0,
    aatTimeHours: task?.aat_time_hours
  });
}

function getAatWinnerComparisonSpeed(entry, task) {
  const metrics = getFinishedAatMetrics(entry, task);
  const projectedDistance_km = metrics?.projectedFinalDistance_km;
  const projectedElapsedMinutes = metrics?.projectedFinalElapsedMinutes;

  if (
    !Number.isFinite(projectedDistance_km) ||
    projectedDistance_km <= 0 ||
    !Number.isFinite(projectedElapsedMinutes) ||
    projectedElapsedMinutes <= 0
  ) {
    return null;
  }

  let comparisonDistance_km = projectedDistance_km;
  const useHandicapDistance = isHandicapTaskClass(task);

  if (useHandicapDistance) {
    const handicap = getConfiguredHandicap(entry?.gliderType, task);
    if (!Number.isFinite(handicap) || handicap <= 0) {
      return null;
    }
    comparisonDistance_km *= 100 / handicap;
  }

  return comparisonDistance_km / (projectedElapsedMinutes / 60);
}

function pickWinnerEntry(finishedEntries, task = null) {
  if (!finishedEntries || finishedEntries.length === 0) {
    return null;
  }

  if (task?.task_type === 'AAT') {
    let winner = null;

    finishedEntries.forEach(entry => {
      const entrySpeed = getAatWinnerComparisonSpeed(entry, task);
      if (!Number.isFinite(entrySpeed) || entrySpeed <= 0) {
        return;
      }

      if (!winner) {
        winner = entry;
        return;
      }

      const winnerSpeed = getAatWinnerComparisonSpeed(winner, task);
      if (!Number.isFinite(winnerSpeed) || entrySpeed > winnerSpeed + 1e-6) {
        winner = entry;
        return;
      }

      if (Math.abs(entrySpeed - winnerSpeed) <= 1e-6 && compareFinishOrder(entry, winner) < 0) {
        winner = entry;
      }
    });

    return winner || finishedEntries[0];
  }

  const isHandicapRace =
    isHandicapTaskClass(task) ||
    finishedEntries.some(entry =>
      Number.isFinite(entry?.predictedFinishMinutesHandicap) ||
      Number.isFinite(entry?.finishDeltaMinutesHandicap)
    );
  let winner = null;
  finishedEntries.forEach(entry => {
    if (!winner) {
      winner = entry;
      return;
    }

    if (isHandicapRace) {
      const winnerHandicap = getHandicapFinishMinutes(winner, task);
      const entryHandicap = getHandicapFinishMinutes(entry, task);

      const winnerHasHandicap = Number.isFinite(winnerHandicap);
      const entryHasHandicap = Number.isFinite(entryHandicap);

      if (entryHasHandicap && (!winnerHasHandicap || entryHandicap < winnerHandicap - 1e-6)) {
        winner = entry;
        return;
      }

      if (entryHasHandicap && winnerHasHandicap) {
        if (Math.abs(entryHandicap - winnerHandicap) <= 1e-6) {
          if (compareFinishOrder(entry, winner) < 0) {
            winner = entry;
          }
        }
        return;
      }

      if (!entryHasHandicap && !winnerHasHandicap) {
        if (compareFinishOrder(entry, winner) < 0) {
          winner = entry;
        }
        return;
      }

      // If we reach here, winner has handicap time and entry does not; keep winner
      return;
    }

    if (compareFinishOrder(entry, winner) < 0) {
      winner = entry;
    }
  });

  return winner;
}

function solveIterativeMacCready(coeffs, calculateSpeedAtMc, logPrefix = 'MacCready') {
  const toleranceMc = 0.01;
  const maxIterations = 12;
  let baselineMc = 2.0;
  let derivedMc = null;
  let converged = false;
  let lastBaselineUsed = null;
  let lastXcSpeed = null;

  for (let iteration = 1; iteration <= maxIterations; iteration++) {
    lastBaselineUsed = baselineMc;
    const loopSpeed = calculateSpeedAtMc(baselineMc);
    if (!Number.isFinite(loopSpeed) || loopSpeed <= 0) {
      return null;
    }

    lastXcSpeed = loopSpeed;
    derivedMc = findMacCreadyForSpeed(coeffs, loopSpeed);
    if (!Number.isFinite(derivedMc) || derivedMc <= 0) {
      return null;
    }

    console.log(`${logPrefix}: refinement iteration`, {
      iteration,
      baselineMc: baselineMc.toFixed(2),
      xcSpeed: loopSpeed.toFixed(1),
      derivedMc: derivedMc.toFixed(2)
    });

    if (Math.abs(derivedMc - baselineMc) < toleranceMc) {
      converged = true;
      baselineMc = derivedMc;
      break;
    }

    baselineMc = derivedMc;
  }

  if (!converged) {
    console.warn(`${logPrefix}: refinement loop hit limit before convergence`, {
      finalBaselineMc: baselineMc.toFixed(2),
      lastDerivedMc: derivedMc?.toFixed(2)
    });
  }

  let resolvedSpeedKmh = lastXcSpeed;
  const needsFinalPass = !Number.isFinite(resolvedSpeedKmh) ||
    lastBaselineUsed === null ||
    Math.abs(baselineMc - lastBaselineUsed) >= toleranceMc;

  if (needsFinalPass) {
    resolvedSpeedKmh = calculateSpeedAtMc(baselineMc);
  }

  if (!Number.isFinite(resolvedSpeedKmh) || resolvedSpeedKmh <= 0) {
    return null;
  }

  return {
    mc: baselineMc,
    xcSpeedKmh: resolvedSpeedKmh,
    converged
  };
}

function calculateLiveLeaderXcSpeedAtMc(entry, task, coeffs, baselineMc = 2.0, gliderType = null) {
  if (entry?.isFinished || entry?.status === 'finished') {
    return calculateWinnerXcSpeedAtMc(entry, task, coeffs, baselineMc, gliderType);
  }

  const elapsedSeconds =
    (Number.isFinite(entry?.raceElapsedSec) && entry.raceElapsedSec > 0 ? entry.raceElapsedSec : null) ??
    (Number.isFinite(entry?.finishElapsedMinutes) && entry.finishElapsedMinutes > 0 ? entry.finishElapsedMinutes * 60 : null);
  if (!Number.isFinite(elapsedSeconds) || elapsedSeconds <= 0) {
    return null;
  }

  const distance_km =
    (Number.isFinite(entry?.real_km) && entry.real_km > 0 ? entry.real_km : null) ??
    (Number.isFinite(entry?.official_km) && entry.official_km > 0 ? entry.official_km : null);
  if (!Number.isFinite(distance_km) || distance_km <= 0) {
    return null;
  }

  const simpleSpeed = distance_km / (elapsedSeconds / 3600);
  const state = aircraftStateTracker.getState(entry?.cookie);
  const position = state?.lastPosition;
  if (!position) {
    return simpleSpeed;
  }

  const startHeight_m =
    (Number.isFinite(state?.startAltitude_m) ? state.startAltitude_m : null) ??
    (Number.isFinite(entry?.startAltitude_m) ? entry.startAltitude_m : null);
  if (!Number.isFinite(startHeight_m)) {
    return simpleSpeed;
  }

  if (!coeffs || coeffs.length < 3) {
    return simpleSpeed;
  }

  const bestCruise = getBestCruiseForMacCready(coeffs, baselineMc);
  if (!bestCruise) {
    return simpleSpeed;
  }

  const [a, b, c] = coeffs;
  const mcSpeedKmh = bestCruise.cruiseSpeedKmh;
  const vOpt_ms = mcSpeedKmh / 3.6;
  const mcSinkMs = a * vOpt_ms * vOpt_ms + b * vOpt_ms + c;
  const currentAlt_m =
    (Number.isFinite(position?.tec_m) && position.tec_m !== 0 ? position.tec_m : null) ??
    (Number.isFinite(position?.altitude_m) ? position.altitude_m : null);

  if (!Number.isFinite(currentAlt_m)) {
    return simpleSpeed;
  }

  const elapsedTime_hours = elapsedSeconds / 3600;
  if (elapsedTime_hours <= 0) {
    return simpleSpeed;
  }

  const deltaAlt_km = (currentAlt_m - startHeight_m) / 1000.0;
  const mcSinkKmh = mcSinkMs * 3.6;
  const mcGlideRatio = Math.abs(mcSinkKmh) > 0 ? mcSpeedKmh / Math.abs(mcSinkKmh) : 0;
  const equivalentTime = Math.abs(mcSinkKmh) > 0 ? deltaAlt_km / Math.abs(mcSinkKmh) : 0;
  const mcSettingKmh = baselineMc * 3.6;
  const mcAvgSpeed = mcSettingKmh !== 0 ? mcSpeedKmh / (1 + (Math.abs(mcSinkKmh) / mcSettingKmh)) : 0;

  const altitudeCredit = deltaAlt_km * mcGlideRatio;
  const timePenalty = equivalentTime * mcAvgSpeed;
  const numerator = altitudeCredit + distance_km - timePenalty;
  const xcSpeed = elapsedTime_hours > 0 ? numerator / elapsedTime_hours : 0;

  console.log('Live leader MC: XC speed with altitude compensation', {
    cn: entry?.cn,
    glider: gliderType ?? entry?.gliderType ?? null,
    mcBaseline: baselineMc.toFixed(2),
    distance_km: distance_km.toFixed(2),
    elapsedSec: elapsedSeconds.toFixed(0),
    startHeight_m: startHeight_m.toFixed(0),
    currentAlt_m: currentAlt_m.toFixed(0),
    deltaAlt_km: deltaAlt_km.toFixed(3),
    xcSpeed: xcSpeed.toFixed(1)
  });

  return xcSpeed;
}

async function maybeApplyLiveLeaderMacCready(task, displayEntries) {
  if (currentMode !== 'live' || replayManager?.isLiveMode !== true) {
    return;
  }
  if (!task || !task.id || macCreadyManualOverride) {
    return;
  }

  const leaderEntry = (displayEntries || []).find(entry =>
    entry &&
    entry.isPreStart !== true &&
    entry.isDnf !== true &&
    entry.isOffline !== true &&
    (
      (Number.isFinite(entry?.raceElapsedSec) && entry.raceElapsedSec > 0) ||
      (Number.isFinite(entry?.finishElapsedMinutes) && entry.finishElapsedMinutes > 0)
    ) &&
    (
      (Number.isFinite(entry?.real_km) && entry.real_km > 0) ||
      (Number.isFinite(entry?.official_km) && entry.official_km > 0)
    ) &&
    entry.gliderType
  );

  if (!leaderEntry) {
    return;
  }

  const now = Date.now();
  if (
    macCreadyLiveLeaderTaskId === task.id &&
    macCreadyLiveLeaderCookie === leaderEntry.cookie &&
    (now - macCreadyLiveLeaderLastCheckMs) < 30_000
  ) {
    return;
  }
  macCreadyLiveLeaderLastCheckMs = now;

  try {
    const effectiveWingLoading = getEffectiveWingLoading(leaderEntry.gliderType, task, { cookie: leaderEntry.cookie });
    const coeffs = await globalPolarManager.getPolarCoefficientsAsync(leaderEntry.gliderType, effectiveWingLoading);
    if (!coeffs || coeffs.length < 3) {
      return;
    }

    const solution = solveIterativeMacCready(
      coeffs,
      (baselineMc) => calculateLiveLeaderXcSpeedAtMc(leaderEntry, task, coeffs, baselineMc, leaderEntry.gliderType),
      'Live leader MC'
    );
    if (!solution?.mc || !solution?.xcSpeedKmh) {
      return;
    }

    console.log('Live leader MC: resolved XC speed', {
      cn: leaderEntry.cn,
      xcSpeed: solution.xcSpeedKmh.toFixed(1),
      glider: leaderEntry.gliderType,
      mc: solution.mc.toFixed(2),
      converged: solution.converged,
      displayedXcSpeedKmh: Number.isFinite(leaderEntry.xcSpeedKmh)
        ? leaderEntry.xcSpeedKmh.toFixed(1)
        : null
    });

    setMacCreadyValue(solution.mc, `leader ${leaderEntry.cn} XC`);
    macCreadyLiveLeaderTaskId = task.id;
    macCreadyLiveLeaderCookie = leaderEntry.cookie;
  } catch (error) {
    console.warn('Failed to auto-set MacCready from live leader:', error);
  }
}

function maybeApplyWinnerMacCready(task, finishedEntries) {
  if (currentMode !== 'replay' && currentMode !== 'igc') {
    console.log('Winner MC: skipped (not in replay or igc mode)');
    return;
  }
  if (!task || !task.id) {
    console.log('Winner MC: skipped (task missing or no id)');
    return;
  }
  if (macCreadyManualOverride) {
    console.log('Winner MC: skipped (manual override active)');
    return;
  }
  if (macCreadyWinnerTaskId === task.id) {
    return;
  }
  // Throttle: only re-check every 2 minutes of wall-clock time
  const now = Date.now();
  if (now - macCreadyLastCheckMs < 120_000) {
    return;
  }
  macCreadyLastCheckMs = now;
  if (!finishedEntries || finishedEntries.length === 0) {
    finishedEntries = buildFinishedEntriesFromCache(task);
    if (!finishedEntries || finishedEntries.length === 0) {
      return;
    }
  }
  if (macCreadyWinnerPromise) {
    console.log('Winner MC: skipped (calculation already running)');
    return;
  }

  const winnerEntry = pickWinnerEntry(finishedEntries, task);
  if (!winnerEntry || !winnerEntry.gliderType) {
    console.log('Winner MC: no winner entry or glider type available');
    return;
  }

  const resolvedPolar = globalPolarManager.resolvePolarName(winnerEntry.gliderType);
  console.log('Winner MC: calculating from entry', {
    taskId: task.id,
    cn: winnerEntry.cn,
    glider: winnerEntry.gliderType,
    polar: resolvedPolar
  });

  macCreadyWinnerPromise = (async () => {
    try {
      const gliderType = winnerEntry.gliderType;
      const effectiveWingLoading = getEffectiveWingLoading(gliderType, task, { cookie: winnerEntry.cookie });
      const coeffs = await globalPolarManager.getPolarCoefficientsAsync(gliderType, effectiveWingLoading);
      if (!coeffs || coeffs.length < 3) {
        console.warn('Winner MC: no polar coefficients for', gliderType);
        return;
      }

      const solution = solveIterativeMacCready(
        coeffs,
        (baselineMc) => calculateWinnerXcSpeedAtMc(winnerEntry, task, coeffs, baselineMc, gliderType),
        'Winner MC'
      );
      if (!solution?.mc || !solution?.xcSpeedKmh) {
        console.log('Winner MC: unable to resolve winner XC speed', winnerEntry.cn);
        return;
      }

      const baselineMc = solution.mc;
      const winnerSpeed = solution.xcSpeedKmh;
      const converged = solution.converged;

      console.log('Winner MC: resolved XC speed', {
        cn: winnerEntry.cn,
        xcSpeed: winnerSpeed.toFixed(1),
        glider: gliderType,
        mc: baselineMc.toFixed(2),
        converged
      });

      const bestCruise = getBestCruiseForMacCready(coeffs, baselineMc);
      console.log('Winner MC: derived MC for speed', {
        winnerXcSpeed: winnerSpeed.toFixed(1),
        mcValue: baselineMc.toFixed(2),
        cruiseSpeed: bestCruise?.cruiseSpeedKmh?.toFixed(1) ?? null,
        xcSpeedAtMc: bestCruise?.xcSpeedKmh?.toFixed(1) ?? null,
        converged
      });

      const theoreticalMc = baselineMc;
      setMacCreadyValue(theoreticalMc, `winner ${winnerEntry.cn}`);

      // Analyze winner's actual flight track (both replay and IGC modes)
      // Override MC with measured avg climb rate, derive netto as residual
      // DISABLED: climb rate + netto adjustment produces inconsistent results;
      // keeping only the XC-speed-derived MC above.
      const ENABLE_CLIMB_RATE_NETTO_OVERRIDE = false;
      if (ENABLE_CLIMB_RATE_NETTO_OVERRIDE && replayManager?.flights) {
        const winnerFlight = replayManager.flights.find(f =>
          f.positions?.[0]?.cookie === winnerEntry.cookie
        );

        if (winnerFlight?.positions?.length > 0) {
          const { analyzeFlightTrack } = await import('./igc/igcFlightAnalyzer.js');
          const analysis = analyzeFlightTrack(winnerFlight.positions);

          console.log('Winner flight analysis:', {
            cn: winnerEntry.cn,
            glider: gliderType,
            avgClimbRate_ms: analysis.avgClimbRate_ms,
            thermalCount: analysis.thermalCount,
            thermalPercent: analysis.thermalPercent,
            sampleIntervalSec: analysis.sampleIntervalSec,
            theoreticalMc: theoreticalMc.toFixed(2)
          });

          // Only trust the measured-climb override if it also yields a
          // physically consistent netto solution for the winner's XC speed.
          if (Number.isFinite(analysis.avgClimbRate_ms) && analysis.avgClimbRate_ms > 0) {
            const measuredMc = analysis.avgClimbRate_ms;
            const xcSpeedAtMeasuredMc = calculateWinnerXcSpeedAtMc(
              winnerEntry, task, coeffs, measuredMc, gliderType
            );
            if (Number.isFinite(xcSpeedAtMeasuredMc) && xcSpeedAtMeasuredMc > 0) {
              const netto = findNettoForSpeed(coeffs, measuredMc, xcSpeedAtMeasuredMc);
              if (Number.isFinite(netto)) {
                setMacCreadyValue(measuredMc, `winner ${winnerEntry.cn} avg climb`);
                setNettoValue(netto, `winner ${winnerEntry.cn} solved netto`);
                console.log('Winner netto solved:', {
                  cn: winnerEntry.cn,
                  measuredMc: measuredMc.toFixed(2),
                  xcSpeed: xcSpeedAtMeasuredMc.toFixed(1),
                  netto: netto.toFixed(2)
                });
              } else {
                console.warn('Winner MC: skipping measured climb override (no valid netto)', {
                  cn: winnerEntry.cn,
                  measuredMc: measuredMc.toFixed(2),
                  theoreticalMc: theoreticalMc.toFixed(2),
                  sampleIntervalSec: analysis.sampleIntervalSec
                });
              }
            } else {
              console.warn('Winner MC: skipping measured climb override (invalid XC speed)', {
                cn: winnerEntry.cn,
                measuredMc: measuredMc.toFixed(2),
                theoreticalMc: theoreticalMc.toFixed(2),
                sampleIntervalSec: analysis.sampleIntervalSec
              });
            }
          }

          // Reflect the winner's effective replay wing loading in the UI when we know it.
          if (currentMode === 'igc') {
            const winnerWingLoading = getEffectiveWingLoading(gliderType, task, {
              cookie: winnerEntry.cookie,
              flightMetadata: winnerFlight.metadata || null
            });
            if (Number.isFinite(winnerWingLoading) && winnerWingLoading > 0) {
              setWingLoadingValue(winnerWingLoading, `winner ${winnerEntry.cn} replay WL`);
            }
          }
        }
      }

      macCreadyWinnerTaskId = task.id;
    } catch (error) {
      console.warn('Failed to calculate MacCready from winner:', error);
    }
  })().finally(() => {
    console.log('Winner MC: promise complete');
    macCreadyWinnerPromise = null;
  });
}

/**
 * Fetches a single task by ID from the API
 */
async function fetchTaskById(taskId) {
  try {
    const url = `${HTTP_URL}/api/tasks/${taskId}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.task) {
      // Reset MacCready automation for the new task (replay mode will recalc from winner)
      resetMacCreadyAutoState();

      config.taskMaxWingLoading = getTaskMaxWingLoadingKgM2(data.task);

      // Extract max wing loading from task_additional_details if available
      if (config.taskMaxWingLoading) {
        // Update the wing loading input field if it exists
        const wingLoadingInput = document.getElementById('wing-loading-value');
        if (wingLoadingInput) {
          wingLoadingInput.value = config.taskMaxWingLoading;
          config.wingLoading = config.taskMaxWingLoading;
          saveSettings();
          notifyEtfInputChange();
        }
        wingLoadingPromptedThisSession = true;
      } else if (!wingLoadingPromptedThisSession) {
        // No wing loading in task data — prompt the user (once per session)
        wingLoadingPromptedThisSession = true;
        const { showWingLoadingDialog } = await import('./igc/igcSetupDialogs.js');
        const wl = await showWingLoadingDialog(config.wingLoading);
        if (Number.isFinite(wl)) {
          config.wingLoading = wl;
          const wingLoadingInput = document.getElementById('wing-loading-value');
          if (wingLoadingInput) wingLoadingInput.value = wl;
          saveSettings();
          notifyEtfInputChange();
        }
      }

      // Process task for distance calculations
      const processedTask = taskManager.processTask(data.task);

      return processedTask || data.task;
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('❌ Error fetching task by ID:', error);
    return null;
  }
}

/**
 * Fetches tasks from the API based on current filter
 */
async function fetchTasks() {
  try {
    let url = `${HTTP_URL}/api/tasks`;
    const params = [];

    if (selectedTaskFilter === 'byServer') {
      // Get selected server(s)
      if (!selectedServers.has('all')) {
        // Get first selected server
        const serverKey = Array.from(selectedServers)[0];
        if (serverKey) {
          const server = activeServers.get(serverKey);
          if (server) {
            params.push(`serverName=${encodeURIComponent(server.name)}`);
          }
        }
      }
      // If 'all' is selected, fetch all tasks (no filters)
    }

    if (params.length > 0) {
      url += '?' + params.join('&');
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.tasks) {
      // Process all tasks for distance calculations
      activeTasks = data.tasks.map(task => {
        const processedTask = taskManager.processTask(task);
        return processedTask || task;
      });
      updateTasksPanel();
      scheduleLiveTaskRefresh();
    } else {
      throw new Error('Invalid response format');
    }
  } catch (error) {
    console.error('❌ Error fetching tasks:', error);
    activeTasks = [];
    updateTasksPanel();
  }
}

/**
 * Formats time difference from now
 */
function formatTimeFromNow(isoTime) {
  if (!isoTime) {
    return '';
  }

  const now = new Date();
  const taskTime = new Date(isoTime);
  if (!Number.isFinite(taskTime.getTime())) {
    return '';
  }

  const diffMs = taskTime - now;
  const diffMinutes = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  const isPast = diffMs < 0;
  const absDiffMinutes = Math.abs(diffMinutes);
  const absDiffHours = Math.abs(diffHours);
  const absDiffDays = Math.abs(diffDays);

  // Check if we're in the "Live" window (0 to 4 hours after event time)
  const fourHoursMs = 4 * 60 * 60 * 1000;
  if (isPast && Math.abs(diffMs) <= fourHoursMs) {
    return 'LIVE';
  }

  // If more than 4 hours past, show "ago"
  if (isPast) {
    if (absDiffMinutes < 60) {
      return `${absDiffMinutes} min${absDiffMinutes !== 1 ? 's' : ''} ago`;
    } else if (absDiffHours < 24) {
      return `${absDiffHours} hour${absDiffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${absDiffDays} day${absDiffDays !== 1 ? 's' : ''} ago`;
    }
  }

  // Future events - countdown
  if (absDiffMinutes < 60) {
    return `${absDiffMinutes} min${absDiffMinutes !== 1 ? 's' : ''} from now`;
  } else if (absDiffHours < 24) {
    return `${absDiffHours} hour${absDiffHours !== 1 ? 's' : ''} from now`;
  } else {
    return `${absDiffDays} day${absDiffDays !== 1 ? 's' : ''} from now`;
  }
}

/**
 * Formats AAT time in hours to "Xh Ym" format
 */
function formatAATTime(hours) {
  if (!hours) return '';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) {
    return `${h}h`;
  }
  return `${h}h ${m}m`;
}

/**
 * Updates the tasks panel with fetched tasks
 */
function updateTasksPanel() {
  const taskContexts = getVisibleTasksForCurrentFilter();

  // Update both old panel and left panel
  const panels = [taskListEl, leftTaskListEl].filter(el => el);

  panels.forEach(panel => {
    panel.innerHTML = '';

    if (taskContexts.length === 0) {
      const message = selectedTaskFilter === 'liveNow'
        ? 'No tasks are live right now'
        : 'No tasks found';
      panel.innerHTML = `<div class="empty-state">${message}</div>`;
      return;
    }
  });

  taskContexts.forEach(({ task, proximityCount }) => {
    const taskItem = document.createElement('div');
    taskItem.className = 'task-list-item' + (selectedTask === task.id ? ' selected' : '');
    taskItem.dataset.taskId = task.id;

    // Calculate time from now
    const timeFromNow = formatTimeFromNow(task.ds_helper_start_time);

    // Determine third row content based on task type
    let thirdRowLeft = task.task_type || '';
    let thirdRowRight = '';
    let accentClass = '';

    // Add glider class if available (prepend to task type)
    if (task.gliderClass && task.gliderClass !== 'All') {
      thirdRowLeft = `${task.gliderClass} - ${thirdRowLeft}`;
    }

    if (task.task_type === 'Racing') {
      if (task.regatta_start === true) {
        thirdRowRight = 'Regatta';
        accentClass = 'accent-purple';
      } else if (task.start_window_minutes !== undefined && task.start_window_minutes !== null) {
        thirdRowRight = `${task.start_window_minutes} min start window`;
        accentClass = 'accent-blue';
      }
    } else if (task.task_type === 'AAT') {
      if (task.aat_time_hours) {
        thirdRowRight = formatAATTime(task.aat_time_hours);
        accentClass = 'accent-green';
      }
    }

    if (selectedTaskFilter === 'liveNow' && proximityCount > 0) {
      thirdRowRight = `${proximityCount} nearby`;
      accentClass = 'accent-green';
    }

    // Check if the time is "LIVE" to apply special styling
    const isLive = timeFromNow === 'LIVE';
    const timeClass = isLive ? 'task-start-time task-live' : 'task-start-time';

    taskItem.innerHTML = `
      <span class="task-name">${task.task_name}</span>
      <div class="task-info-row">
        <span class="task-landscape">${task.landscape}</span>
        <span class="${timeClass}">${timeFromNow}</span>
      </div>
      <div class="task-info-row">
        <span class="task-type ${accentClass}">${thirdRowLeft}</span>
        <span class="task-extra ${accentClass}">${thirdRowRight}</span>
      </div>
    `;
    taskItem.addEventListener('click', () => handleTaskSelection(task.id));

    // Append to all panels
    panels.forEach(panel => {
      panel.appendChild(taskItem.cloneNode(true));
    });
  });

  // Re-attach event listeners to cloned elements
  panels.forEach(panel => {
    panel.querySelectorAll('.task-list-item').forEach(item => {
      item.addEventListener('click', () => handleTaskSelection(parseInt(item.dataset.taskId)));
    });
  });

  // Task metadata (like glider class) may have changed, so refresh wing loading control state
  updateWingLoadingInputState();
}

/**
 * Clears the current task overlay from the map
 */
function clearTaskOverlay() {
  taskOverlayLayers.forEach(layer => {
    map.removeLayer(layer);
  });
  taskOverlayLayers = [];

  // Also clear from 3D view if active
  mapManager.clearTask();

  // Update wing loading input state (re-enable when no task selected)
  updateWingLoadingInputState();
}

/**
 * Calculates the bearing between two points in degrees
 */
function calculateBearing(lat1, lon1, lat2, lon2) {
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const lat1Rad = lat1 * Math.PI / 180;
  const lat2Rad = lat2 * Math.PI / 180;

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  const bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360; // Normalize to 0-360
}

/**
 * Creates a sector (partial circle) for turnpoints
 */
function createSector(latLng, radius, angle, bearing) {
  const angleNum = parseFloat(angle);

  // If 360 degrees, return a full circle
  if (angleNum === 360) {
    return L.circle(latLng, {
      radius: radius,
      fillColor: '#94a3b8',
      color: '#94a3b8',
      weight: 2,
      opacity: 0.9,
      fillOpacity: 0.5
    });
  }

  // For sectors (180 or 90), we need to create a polygon
  const center = L.latLng(latLng);
  const points = [center];

  // Determine start and end angles based on sector type
  let startAngle, endAngle;

  if (angleNum === 180) {
    // Semicircle on the "back" side (opposite to the bearing direction)
    // Note: bearing is the direction TO the next point, so we add 90/270 for the back side
    startAngle = bearing + 90;
    endAngle = bearing + 270;
  } else if (angleNum === 90) {
    // Quarter circle on the "outside corner"
    startAngle = bearing - 45;
    endAngle = bearing + 45;
  } else {
    // Default to full circle if unknown angle
    return L.circle(latLng, {
      radius: radius,
      fillColor: '#94a3b8',
      color: '#94a3b8',
      weight: 2,
      opacity: 0.9,
      fillOpacity: 0.5
    });
  }

  // Generate points along the arc
  const numPoints = 50;
  for (let i = 0; i <= numPoints; i++) {
    const angle = startAngle + (endAngle - startAngle) * (i / numPoints);
    const angleRad = angle * Math.PI / 180;

    // Calculate point on circle using destination point formula
    const lat1 = center.lat * Math.PI / 180;
    const lon1 = center.lng * Math.PI / 180;
    const angularDistance = radius / 6371000; // Earth radius in meters

    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(angularDistance) +
      Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(angleRad)
    );

    const lon2 = lon1 + Math.atan2(
      Math.sin(angleRad) * Math.sin(angularDistance) * Math.cos(lat1),
      Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2)
    );

    points.push([lat2 * 180 / Math.PI, lon2 * 180 / Math.PI]);
  }

  points.push(center); // Close the sector

  return L.polygon(points, {
    fillColor: '#94a3b8',
    color: '#94a3b8',
    weight: 2,
    opacity: 0.9,
    fillOpacity: 0.5
  });
}

/**
 * Draws a task on the map
 */
function drawTaskOnMap(taskId) {
  // Clear any existing task overlay
  clearTaskOverlay();

  // Find the task in activeTasks (handle both string and number IDs)
  const task = activeTasks.find(t => t.id == taskId);  // Use == for loose comparison
  if (!task || !task.turnpoints || task.turnpoints.length === 0) {
    console.warn('Task not found or has no turnpoints:', taskId);
    return;
  }


  // Draw in 3D view if active
  mapManager.drawTask(task);

  const turnpoints = task.turnpoints;
  const coordinates = [];

  // Draw each turnpoint
  turnpoints.forEach((tp, index) => {
    const latLng = [tp.Lat, tp.Lon];
    coordinates.push(latLng);

    // Draw center point (red circle)
    const centerMarker = L.circleMarker(latLng, {
      radius: 6,
      fillColor: '#ef4444',
      color: '#fff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.9
    });
    centerMarker.addTo(map);
    taskOverlayLayers.push(centerMarker);

    // Draw radius circle or sector based on angle
    if (tp.RadiusMeters) {
      const radius = parseFloat(tp.RadiusMeters);

      // Calculate bearing to next turnpoint (or from previous if last)
      let bearing = Number.isFinite(Number(tp.bearing)) ? Number(tp.bearing) : 0;
      const isLastTurnpoint = index === turnpoints.length - 1;

      if (!Number.isFinite(Number(tp.bearing)) && index < turnpoints.length - 1) {
        // Bearing to next turnpoint
        const nextTp = turnpoints[index + 1];
        bearing = calculateBearing(tp.Lat, tp.Lon, nextTp.Lat, nextTp.Lon);
      } else if (!Number.isFinite(Number(tp.bearing)) && index > 0) {
        // For last turnpoint (finish), use bearing from previous
        // But reverse it by adding 180 degrees so the semicircle faces forward
        const prevTp = turnpoints[index - 1];
        bearing = calculateBearing(prevTp.Lat, prevTp.Lon, tp.Lat, tp.Lon);

        // Reverse bearing for finish line (add 180 degrees)
        if (parseFloat(tp.Angle) === 180) {
          bearing = (bearing + 180) % 360;
        }
      }

      const sector = createSector(latLng, radius, tp.Angle, bearing);
      sector.addTo(map);
      taskOverlayLayers.push(sector);
    }

    // Add label (dynamic width, centered)
    const label = L.marker(latLng, {
      icon: L.divIcon({
        className: 'turnpoint-label',
        html: `<div class="turnpoint-label-content">${tp.Name}</div>`
      })
    });
    label.addTo(map);
    taskOverlayLayers.push(label);
  });

  // Draw connecting lines between turnpoints
  if (coordinates.length > 1) {
    const taskLine = L.polyline(coordinates, {
      color: '#3b82f6',
      weight: 3,
      opacity: 0.7,
      dashArray: '10, 5'
    });
    taskLine.addTo(map);
    taskOverlayLayers.push(taskLine);
  }

  // Fit map to show all turnpoints (only in 2D mode)
  if (coordinates.length > 0 && mapManager.getMode() === '2d') {
    const bounds = L.latLngBounds(coordinates);
    map.fitBounds(bounds, { padding: [50, 50] });
  }

  // Update wing loading input state based on task class
  updateWingLoadingInputState();
}

/**
 * Handles task selection
 */
function handleTaskSelection(taskId) {
  if (selectedTask == taskId) {  // Use == for loose comparison
    // Deselect if clicking the same task
    selectedTask = null;
    replaySelectedTask = null;
    autoLiveTaskSelectionPending = false;

    // Exit PVR mode if active so "no task" always means plain live
    if (currentMode === 'live' && replayManager?.isLiveMode) {
      replayManager.destroy();
      replayManager = null;
      replayControls.setLiveMode(false);
      replayControls.hide();
      if (replayUpdateInterval) {
        clearInterval(replayUpdateInterval);
        replayUpdateInterval = null;
      }
    }

    resetEtfTracking();
    clearTaskOverlay();
    if (currentMode === 'live') {
      updateURLParameter(null);
    }
  } else {
    resetEtfTracking();
    resetMacCreadyAutoState();
    selectedTask = taskId;
    autoLiveTaskSelectionPending = false;
    if (currentMode === 'replay' || currentMode === 'igc') {
      replaySelectedTask = activeTasks.find((t) => String(t?.id) === String(taskId)) || null;
      if (replayManager && replaySelectedTask) {
        replayManager.setSelectedTask(replaySelectedTask);
        updateAlignStartsVisibility();
      }
    }
    drawTaskOnMap(taskId);
    if (currentMode === 'live') {
      updateURLParameter(taskId);
    }

    // In live mode, start PVR: load history into ReplayManager + append live data
    if (currentMode === 'live') {
      const task = activeTasks.find(t => t.id === taskId);
      if (task) {
        initLiveReplayForTask(taskId, task);
      }
    }
  }

  updateTasksPanel();
  console.log('Selected task:', selectedTask);
}

/**
 * Initialize PVR (live-replay) mode for a task.
 * Loads historical data into ReplayManager, precomputes events,
 * then enters live-append mode so WebSocket positions feed into the replay engine.
 * @param {number} taskId
 * @param {object} task - full task object with turnpoints
 */
async function initLiveReplayForTask(taskId, task) {
  try {
    console.log(`🔴 PVR: Loading history for task ${taskId}...`);

    // Clean up any existing replay
    if (replayManager) {
      replayManager.destroy();
      replayManager = null;
    }

    // Create fresh ReplayManager with all calculators
    replayManager = new ReplayManager(
      aircraftManager, mapManager, HTTP_URL,
      aircraftStateTracker, globalPolarManager, globalEnergyCalc
    );
    replayManager.setPositionUpdateCallback(updateDistanceStatsWidget);
    aircraftManager.labelRefreshMs = 1000 / Math.max(0.1, config.replayRefreshHz);

    // Clear existing aircraft and state
    aircraftManager.clearAll();
    aircraftStateTracker.clearAll();

    // Load historical data (same API as replay mode)
    replayControls.resetAlignStarts();
    const replayInfo = await replayManager.load(taskId);

    // Clear again after async load — during the await, WebSocket positions may have
    // gone through the direct live path and created entries with numeric cookies.
    // ReplayManager.load() already calls aircraftManager.clearAll() internally,
    // but we also need to wipe the state tracker so only canonical-cookie entries remain.
    aircraftStateTracker.clearAll();

    // Set the task for event precomputation
    replayManager.setSelectedTask(task);
    updateAlignStartsVisibility();

    // Enter live-append mode
    replayManager.initLiveReplay();

    // Show replay controls with live mode indicator
    replayControls.setShareEnabled(false);
    replayControls.setLiveMode(true);
    replayControls.show();
    replayControls.setDuration(replayInfo.duration);
    replayControls.setTimeRange(replayInfo.timeRange.start, replayInfo.timeRange.end);

    // Seek to live edge and start playback at the selected replay speed
    markReplaySeekInProgress();
    replayManager.jumpToLiveEdge();
    replayControls.setCurrentTime(replayManager.getCurrentTimeSeconds());
    replayControls.setAtLiveEdge(replayManager.isAtLiveEdge());
    replayManager.setSpeed(replayControls.getEffectivePlaybackSpeed?.() ?? replayControls.playbackSpeed);
    replayManager.updatePositions();
    updateDistanceStatsWidget._lastRefresh = 0;
    updateDistanceStatsWidget();

    // Start the timeline update interval
    if (!replayUpdateInterval) {
      replayUpdateInterval = setInterval(() => {
        if (replayManager && replayManager.isPlaying) {
          replayManager.ensurePlaybackLoop?.();
          const currentTime = replayManager.getCurrentTimeSeconds();
          replayControls.setCurrentTime(currentTime);

          // In live mode, grow the scrub bar and update live-edge indicator
          if (replayManager.isLiveMode) {
            const newDuration = replayManager.getDurationSeconds();
            replayControls.setDuration(newDuration);
            replayControls.setAtLiveEdge(replayManager.isAtLiveEdge());
          }
        }
      }, 100);
    }

    // Trigger play via controls so button state stays in sync
    if (!replayControls.isPlaying) {
      replayControls.togglePlayPause();
    }

    console.log(`🔴 PVR: Ready — ${replayInfo.totalFlights} flights loaded, live edge at ${replayManager.liveEdge?.toISOString()}`);

  } catch (error) {
    console.error('❌ PVR: Failed to initialize live replay:', error);
    // Fall back to direct live mode (no PVR)
    if (replayManager) {
      replayManager.destroy();
      replayManager = null;
    }
    replayControls.hide();
  }
}

/**
 * Updates the URL parameter for task sharing
 */
function updateURLParameter(taskId, options = {}) {
  const { replace = false } = options;
  const url = new URL(window.location);
  url.searchParams.delete('replayTaskId');
  url.searchParams.delete('replayFormat');
  url.searchParams.delete('replayTime');

  if (taskId) {
    url.searchParams.set('taskid', taskId);
  } else {
    url.searchParams.delete('taskid');
  }

  // Update URL without reloading the page
  window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
}

function updateReplayURLParameters(replayConfig, options = {}) {
  const { replace = false } = options;
  const url = new URL(window.location);
  const taskId = replayConfig?.taskId;
  const format = replayConfig?.format || 'json';
  const replayTime = Number.isFinite(replayConfig?.startSeconds)
    ? Math.max(0, Math.floor(replayConfig.startSeconds))
    : null;

  url.searchParams.delete('taskid');

  if (taskId) {
    url.searchParams.set('replayTaskId', String(taskId));
  } else {
    url.searchParams.delete('replayTaskId');
  }

  if (format && format !== 'json') {
    url.searchParams.set('replayFormat', format);
  } else {
    url.searchParams.delete('replayFormat');
  }

  if (replayTime !== null) {
    url.searchParams.set('replayTime', String(replayTime));
  } else {
    url.searchParams.delete('replayTime');
  }

  window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
}

/**
 * Gets the task ID from URL parameters
 */
function getTaskIdFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const taskId = urlParams.get('taskid');
  return taskId ? parseInt(taskId) : null;
}

function getReplayConfigFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const replayTaskIdRaw = urlParams.get('replayTaskId');
  if (!replayTaskIdRaw) {
    return null;
  }

  const taskId = Number.parseInt(replayTaskIdRaw, 10);
  if (!Number.isFinite(taskId)) {
    return null;
  }

  const formatParam = urlParams.get('replayFormat');
  const format = formatParam === 'polyline' ? 'polyline' : 'json';
  const replayTimeRaw = urlParams.get('replayTime');
  let startSeconds = null;
  if (replayTimeRaw !== null) {
    const parsedSeconds = Number.parseFloat(replayTimeRaw);
    if (Number.isFinite(parsedSeconds) && parsedSeconds >= 0) {
      startSeconds = parsedSeconds;
    }
  }

  return { taskId, format, startSeconds };
}

function applySharedReplayView({ startSeconds = null, alignStarts = false } = {}) {
  if (alignStarts && replayControls && !replayControls.alignStartsEnabled) {
    replayControls.toggleAlignStarts();
  }

  if (Number.isFinite(startSeconds) && replayManager) {
    seekReplayToSeconds(startSeconds);
  }
}

/**
 * Loads a task from URL parameter if present
 */
async function loadTaskFromURL() {
  const taskId = getTaskIdFromURL();

  if (taskId) {
    autoLiveTaskSelectionPending = false;
    console.log(`🔗 Loading task from URL: ${taskId}`);

    // Fetch the task by ID
    const task = await fetchTaskById(taskId);

    if (task) {
      // Add task to activeTasks if not already present
      const existingTask = activeTasks.find(t => t.id === taskId);
      if (!existingTask) {
        activeTasks.unshift(task); // Add to beginning of list
        updateTasksPanel();
      }

      // Select and display the task
      resetEtfTracking();
      selectedTask = taskId;
      drawTaskOnMap(taskId);
      updateTasksPanel();

      // In live mode, start PVR: load history into ReplayManager + append live data
      if (currentMode === 'live') {
        initLiveReplayForTask(taskId, task);
      }

      // Disable auto-snap to gliders since we're showing a task
      hasSnappedToGliders = true;
    } else {
      console.warn(`⚠️  Task ${taskId} not found`);
      // Remove invalid taskid from URL
      updateURLParameter(null, { replace: true });
    }
  } else {
    // No taskid in URL - clear any selected task
    if (currentMode === 'live' && replayManager?.isLiveMode) {
      replayManager.destroy();
      replayManager = null;
      replayControls.setLiveMode(false);
      replayControls.hide();
      if (replayUpdateInterval) {
        clearInterval(replayUpdateInterval);
        replayUpdateInterval = null;
      }
    }

    if (selectedTask !== null) {
      resetEtfTracking();
      selectedTask = null;
      replaySelectedTask = null;
      clearTaskOverlay();
      updateTasksPanel();
    }
  }
}

async function loadSoaringSpotFromURL(ssUrl, urlParams) {
  let sharedConfig = null;
  const ssConfigB64 = urlParams.get('ssConfig');
  if (ssConfigB64) {
    try {
      sharedConfig = JSON.parse(atob(ssConfigB64));
    } catch (e) {
      console.warn('[SoaringSpot] Failed to decode shared config:', e);
    }
  }

  const replayTimeRaw = urlParams.get('replayTime');
  let startSeconds = null;
  if (replayTimeRaw !== null) {
    const parsed = Number.parseFloat(replayTimeRaw);
    if (Number.isFinite(parsed) && parsed >= 0) startSeconds = parsed;
  }

  currentSoaringSpotUrl = ssUrl;
  currentIgcSourceType = 'soaringspot';
  replayLoadingModal.show();
  replayLoadingModal.setMessage('Fetching SoaringSpot results...');

  try {
    const { importFromSoaringSpot } = await import('./igc/soaringSpotImporter.js');
    const result = await importFromSoaringSpot(ssUrl, (current, total, name) => {
      if (total > 0) {
        replayLoadingModal.setMessage(`Downloading IGC ${current}/${total}: ${name}`);
      } else {
        replayLoadingModal.setMessage(name);
      }
    });
    replayLoadingModal.hide();

    await processIgcFiles(result.files, result.metadata, result.task, sharedConfig);
    applySharedReplayView({
      startSeconds,
      alignStarts: urlParams.get('alignStarts') === '1'
    });
  } catch (err) {
    replayLoadingModal.hide();
    console.error('[SoaringSpot] Share URL import failed:', err);
    alert('Failed to load shared SoaringSpot replay: ' + err.message);
  }
}

async function loadWeGlideFromURL(wgUrl, urlParams) {
  let sharedConfig = null;
  const ssConfigB64 = urlParams.get('ssConfig');
  if (ssConfigB64) {
    try {
      sharedConfig = JSON.parse(atob(ssConfigB64));
    } catch (e) {
      console.warn('[WeGlide] Failed to decode shared config:', e);
    }
  }

  const replayTimeRaw = urlParams.get('replayTime');
  let startSeconds = null;
  if (replayTimeRaw !== null) {
    const parsed = Number.parseFloat(replayTimeRaw);
    if (Number.isFinite(parsed) && parsed >= 0) startSeconds = parsed;
  }

  currentSoaringSpotUrl = wgUrl;
  currentIgcSourceType = 'weglide';
  replayLoadingModal.show();
  replayLoadingModal.setMessage('Fetching WeGlide data...');

  try {
    const { importFromWeGlide } = await import('./igc/weGlideImporter.js');
    const result = await importFromWeGlide(wgUrl, (current, total, name) => {
      if (total > 0) {
        replayLoadingModal.setMessage(`Downloading IGC ${current}/${total}: ${name}`);
      } else {
        replayLoadingModal.setMessage(name);
      }
    });
    replayLoadingModal.hide();

    await processIgcFiles(result.files, result.metadata, result.task, sharedConfig);
    applySharedReplayView({
      startSeconds,
      alignStarts: urlParams.get('alignStarts') === '1'
    });
  } catch (err) {
    replayLoadingModal.hide();
    console.error('[WeGlide] Share URL import failed:', err);
    alert('Failed to load shared WeGlide replay: ' + err.message);
  }
}

async function loadStateFromURL() {
  // Check for SoaringSpot or WeGlide share URL first
  const urlParams = new URLSearchParams(window.location.search);
  const ssUrl = urlParams.get('ssUrl');
  if (ssUrl) {
    console.log(`[SoaringSpot] Loading from share URL: ${ssUrl}`);
    await loadSoaringSpotFromURL(ssUrl, urlParams);
    return;
  }
  const wgUrl = urlParams.get('wgUrl');
  if (wgUrl) {
    console.log(`[WeGlide] Loading from share URL: ${wgUrl}`);
    await loadWeGlideFromURL(wgUrl, urlParams);
    return;
  }

  const replayConfig = getReplayConfigFromURL();
  if (replayConfig) {
    console.log(`🔗 Loading replay from URL: ${replayConfig.taskId}`);
    const loaded = await loadServerReplay(replayConfig.taskId, {
      format: replayConfig.format,
      startSeconds: replayConfig.startSeconds,
      alignStarts: urlParams.get('alignStarts') === '1',
      syncUrl: false
    });
    if (!loaded) {
      updateURLParameter(null, { replace: true });
    }
    return;
  }

  if (currentMode === 'replay' || currentMode === 'igc') {
    setLiveModeState({ syncUrl: false });
  }

  await loadTaskFromURL();
}

/**
 * Handles task filter change (Live Now / By Server)
 */
function handleTaskFilterChange(filter) {
  if (selectedTaskFilter === filter) return;

  selectedTaskFilter = filter;

  // Update UI - old panel
  document.querySelectorAll('.task-filter-item').forEach(item => {
    if (item.dataset.filter === filter) {
      item.classList.add('selected');
    } else {
      item.classList.remove('selected');
    }
  });

  // Update UI - left panel
  document.querySelectorAll('.task-filter-btn').forEach(btn => {
    if (btn.dataset.filter === filter) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  // Fetch tasks with new filter
  fetchTasks();

  console.log('Task filter changed to:', filter);
}

/**
 * Handles server selection/deselection
 */
function handleServerSelection(serverKey) {
  if (serverKey === 'all') {
    // Select "All Servers" and deselect all others
    selectedServers.clear();
    selectedServers.add('all');
  } else {
    // Deselect "All Servers"
    selectedServers.delete('all');

    // Toggle individual server
    if (selectedServers.has(serverKey)) {
      selectedServers.delete(serverKey);
      // If no servers selected, default back to "All"
      if (selectedServers.size === 0) {
        selectedServers.add('all');
      }
    } else {
      selectedServers.add(serverKey);
    }
  }

  // Update UI
  updateServersPanel();

  // Apply filtering
  filterAircraftByServer();

  // Re-fetch tasks if "By Server" filter is active
  if (selectedTaskFilter === 'byServer') {
    fetchTasks();
  }

  console.log('Selected servers:', Array.from(selectedServers));
}

/**
 * Filters aircraft visibility based on selected servers
 */
function filterAircraftByServer() {
  if (selectedServers.has('all')) {
    // Show all aircraft
    aircraftManager.showAllAircraft();
  } else {
    // Show only aircraft from selected servers
    const visibleCookies = new Set();
    selectedServers.forEach(serverKey => {
      const cookies = serverAircraft.get(serverKey);
      if (cookies) {
        cookies.forEach(cookie => visibleCookies.add(cookie));
      }
    });
    aircraftManager.filterAircraft(visibleCookies);
  }
}

/**
 * Fetches the viewer count from the backend
 */
async function fetchViewerCount() {
  try {
    const response = await fetch(`${HTTP_URL}/health`);
    if (response.ok) {
      const data = await response.json();
      if (data.connectedClients !== undefined) {
        viewerCountEl.textContent = data.connectedClients;
        const leftViewerStat = document.getElementById('stat-viewers');
        if (leftViewerStat) leftViewerStat.textContent = data.connectedClients;
      }
    }
  } catch (error) {
    // Silently fail - endpoint might not exist yet
    console.log('Could not fetch viewer count:', error.message);
    viewerCountEl.textContent = '-';
    const leftViewerStat = document.getElementById('stat-viewers');
    if (leftViewerStat) leftViewerStat.textContent = '-';
  }
}

/**
 * Handles incoming WebSocket messages
 */
function handleMessage(event) {
  try {
    const data = JSON.parse(event.data);

    if (data.type === 'connection') {
    } else if (data.type === 'position-update') {
      // Validate new protocol fields
      if (!data.positions || !Array.isArray(data.positions)) {
        console.error('❌ Invalid position-update: missing or invalid positions array');
        return;
      }

      // Ignore late live updates while replay/IGC is active. This prevents
      // WebSocket frames from bleeding live gliders into replay visuals/state.
      if (currentMode !== 'live' && !replayManager?.isLiveMode) {
        return;
      }

      const positions = data.positions;

      // Track unique servers from individual positions (multi-server format)
      const serverCounts = new Map(); // serverKey -> count

      positions.forEach(pos => {
        if (pos.serverName && pos.portNumber) {
          const serverKey = `${pos.serverName}:${pos.portNumber}`;

          // Count positions per server
          serverCounts.set(serverKey, (serverCounts.get(serverKey) || 0) + 1);

          // Track which aircraft belong to this server
          if (!serverAircraft.has(serverKey)) {
            serverAircraft.set(serverKey, new Set());
          }
          if (pos.cookie) {
            serverAircraft.get(serverKey).add(pos.cookie);
          }
        }
      });

      // Update activeServers with counts
      serverCounts.forEach((count, serverKey) => {
        const [serverName, portNumber] = serverKey.split(':');

        if (!activeServers.has(serverKey)) {
          activeServers.set(serverKey, {
            name: serverName,
            port: portNumber,
            count: count
          });
          console.log(`📡 New server detected: ${serverName} (Port: ${portNumber})`);
        } else {
          // Update position count for existing server
          activeServers.get(serverKey).count = count;
        }
      });

      // Update UI if servers changed
      if (serverCounts.size > 0) {
        updateServerCount();
        updateServersPanel();
      }

      // ── PVR mode: delegate to ReplayManager when in live-replay ──
      if (replayManager?.isLiveMode) {
        const now = Date.now();
        positions.forEach(position => {
          // Register live cookie and normalize to replay format
          const normalized = normalizePosition(position);
          if (!positionNoiseFilter.acceptLivePosition(normalized, normalized.cookie, 'live_pvr')) {
            return;
          }
          storeLiveTaskPosition(normalized.cookie, normalized);

          // Track timestamps for stale-aircraft cleanup
          if (normalized.cookie) {
            aircraftTimestamps.set(normalized.cookie, now);
          }

          // Append to ReplayManager — its animate loop handles rendering/stats
          replayManager.appendLivePosition(normalized);
        });
        // Server tracking already done above; skip direct rendering
        return;
      }

      // ── Direct live mode (no task selected / no replay) ──
      // Update each aircraft and track timestamps (state/telemetry pass)
      const now = Date.now();
      const positionsToRender = [];
      positions.forEach(position => {
        try {
          const lat = parseFloat(position.lat ?? position.latitude);
          const lon = parseFloat(position.lon ?? position.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lon) || !position.cookie) {
            return;
          }

          position.lat = lat;
          position.lon = lon;
          storeLiveTaskPosition(position.cookie, position);

          // Register live cookie -> callsign mapping in CookieRegistry
          const callsign = position.id_cn || position.callsign;
          if (callsign) {
            cookieRegistry.registerLive(position.cookie, callsign);
          }

          const continuityKey = callsign
            ? cookieRegistry.getCanonical(callsign)
            : position.cookie;
          if (!positionNoiseFilter.acceptLivePosition(position, continuityKey, 'live_direct')) {
            return;
          }

          aircraftTimestamps.set(position.cookie, now);

          // Phase 4: Calculate TEC altitude and vario for each aircraft (with lazy loading)
          const gliderType = position.aircraft_type || position.aircraft || position.id_model || null;

          // DEBUG: Log first position to see what glider type we're getting
          if (!window._tecDebugLogged) {
            console.log('🔍 First glider type detected:', {
              aircraft_type: position.aircraft_type,
              aircraft: position.aircraft,
              id_model: position.id_model,
              gliderType: gliderType,
              hasPolar: gliderType ? globalPolarManager.hasPolar(gliderType) : false,
              availablePolars: globalPolarManager.getLoadedPolars()
            });
            window._tecDebugLogged = true;
          }

          if (gliderType) {
            // Try to load polar on-demand if we don't have it yet (non-blocking)
            if (!globalPolarManager.hasPolar(gliderType)) {
              globalPolarManager.loadPolarOnDemand(gliderType).catch(err => {
                // Silently fail - polar not available
              });
            } else {
              // We have the polar, calculate TEC
              const altitude_m = parseFloat(position.alt_m || position.altitude_m);
              const speed_kmh = parseFloat(position.speed_mps) * 3.6; // Convert to km/h

              if (!isNaN(altitude_m) && !isNaN(speed_kmh)) {
                globalEnergyCalc.updateTEC(position.cookie, altitude_m, speed_kmh, now);
              }
            }
          }

          // Phase 2 & 3: Start/Finish/TP detection and distance calculation if task is selected
          if (selectedTask !== null) {
            // Handle both cases: selectedTask might be an ID or a task object (replay mode)
            const task = typeof selectedTask === 'object'
              ? selectedTask
              : activeTasks.find(t => t.id === selectedTask);

            if (task && task.turnpoints && task.turnpoints.length > 0) {
              const startSector = task.turnpoints[0]; // First TP is start
              const finishSector = task.turnpoints[task.turnpoints.length - 1]; // Last TP is finish

              // Get max start altitude from task (use 999999m if not specified)
              const maxStartAltitude_m = task.max_start_altitude_m || 999999;
              const rawStartGateTime = task.start_time_override_iso || task.startTimeOverrideIso || null;
              const startGateTimeMs = rawStartGateTime ? new Date(rawStartGateTime).getTime() : null;

              // Check for start crossing (leaving start sector)
              const startCrossed = aircraftStateTracker.checkStartCrossing(
                position,
                startSector,
                maxStartAltitude_m,
                startGateTimeMs,
                task,
                { requireDirectionTowardNextTp: true }
              );
              if (startCrossed) {
                // Clear ETF/XC Pace tracking for this pilot — stale data from a
                // previous (false) start attempt must not persist into the real start.
                etfHistory.delete(position.cookie);
                etfDeltaByAircraft.delete(position.cookie);
                etfDeltaByAircraftB.delete(position.cookie);
                sampledEtfDeltasA.delete(position.cookie);
                sampledEtfDeltasB.delete(position.cookie);
              }

              // Phase 3: Check for turnpoint touches
              aircraftStateTracker.checkTurnpointTouch(position, task);

              // Phase 3: Calculate task distance
              aircraftStateTracker.calculateDistance(position, task);

              // Check for finish crossing (entering finish sector) - Phase 3 update: requires all TPs
              aircraftStateTracker.checkFinishCrossing(position, finishSector, task);

              // Update elapsed time for racing aircraft
              aircraftStateTracker.updateElapsedTime(position.cookie, position.timestamp);
            }
          }

          // Stash current trend windows on the position for labels/colors
          position.etfTrendSec = config.trendWindowSec;
          position.etfTrendSecB = config.trendWindowBSec;

          positionsToRender.push(position);
        } catch (error) {
          console.error('Live position processing failed:', {
            cookie: position?.cookie ?? null,
            cn: position?.id_cn || position?.callsign || null,
            lat: position?.lat ?? position?.latitude ?? null,
            lon: position?.lon ?? position?.longitude ?? null
          }, error);
        }
      });

      // Keep aircraft rendering alive even if the leaderboard/widget update throws.
      try {
        updateDistanceStatsWidget();
      } catch (error) {
        console.error('Distance stats update failed:', error);
      }

      // Render aircraft after ETF deltas have been attached to positions
      positionsToRender.forEach(position => {
        try {
          aircraftManager.updateAircraft(position);
          mapManager.updateAircraft(position);
        } catch (error) {
          console.error('Live aircraft render failed:', {
            cookie: position?.cookie ?? null,
            cn: position?.id_cn || position?.callsign || null
          }, error);
        }
      });

      // Apply current filter after updates
      filterAircraftByServer();

      // Update stats
      updateAircraftCount();

      if (selectedTaskFilter === 'liveNow' || autoLiveTaskSelectionPending) {
        scheduleLiveTaskRefresh();
      }

      // Auto-fit bounds ONCE on first update with max zoom of 10
      if (!hasSnappedToGliders && aircraftManager.getAircraftCount() === positions.length && positions.length > 0) {
        setTimeout(() => {
          aircraftManager.fitBounds(10); // Pass max zoom of 10
          hasSnappedToGliders = true; // Mark as snapped, never auto-snap again
        }, 500);
      }
    } else if (data.type === 'error') {
      console.error('❌ Backend error:', data.message);
    }
  } catch (error) {
    console.error('❌ Error parsing WebSocket message:', error);
  }
}

/**
 * Connects to the WebSocket server
 */
function connect() {

  try {
    ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      updateStatus(true);

      // Clear reconnect timer
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    ws.onmessage = handleMessage;

    ws.onerror = (error) => {
      console.error('❌ WebSocket connection error');
    };

    ws.onclose = (event) => {
      console.warn('⚠️  WebSocket connection lost');
      updateStatus(false);

      // Auto-reconnect only if not in replay mode
      if (shouldAutoReconnect && !reconnectTimer) {
        console.log(`🔄 Reconnecting in ${RECONNECT_INTERVAL / 1000} seconds...`);
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          connect();
        }, RECONNECT_INTERVAL);
      } else if (!shouldAutoReconnect) {
      }
    };
  } catch (error) {
    console.error('❌ Failed to create WebSocket connection');
    updateStatus(false);

    // Retry connection only if not in replay mode
    if (shouldAutoReconnect && !reconnectTimer) {
      console.log(`🔄 Retrying connection in ${RECONNECT_INTERVAL / 1000} seconds...`);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, RECONNECT_INTERVAL);
    }
  }
}

/**
 * Cleanup on page unload
 */
window.addEventListener('beforeunload', () => {
  if (ws) {
    ws.close();
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
  }
  if (timeoutCheckInterval) {
    clearInterval(timeoutCheckInterval);
  }
});

/**
 * Handle visibility change to pause/resume updates
 */
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
  } else {
    // Reconnect if disconnected while hidden
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.log('🔄 Reconnecting after page became visible...');
      connect();
    }
  }
});

/**
 * Checks for aircraft that haven't been updated and removes them
 */
function checkAircraftTimeouts() {
  const now = Date.now();
  const cookiesToRemove = [];

  aircraftTimestamps.forEach((lastUpdate, cookie) => {
    if (now - lastUpdate > AIRCRAFT_TIMEOUT) {
      cookiesToRemove.push(cookie);
    }
  });

  if (cookiesToRemove.length > 0) {
    cookiesToRemove.forEach(cookie => {
      // Remove from aircraft manager
      aircraftManager.removeAircraft(cookie);

      // Remove from map manager (handles 3D)
      mapManager.removeAircraft(cookie);
      clearEtfTrackingForCookie(cookie);
      etfStartActivationByAircraft.delete(cookie);

      // Remove from timestamp tracking
      aircraftTimestamps.delete(cookie);
      latestLivePositions.delete(String(cookie));

      // Remove from server tracking
      serverAircraft.forEach((cookies, serverKey) => {
        cookies.delete(cookie);
      });
    });

    // Clean up servers with no aircraft
    const serversToRemove = [];
    serverAircraft.forEach((cookies, serverKey) => {
      if (cookies.size === 0) {
        serversToRemove.push(serverKey);
      }
    });

    serversToRemove.forEach(serverKey => {
      serverAircraft.delete(serverKey);
      activeServers.delete(serverKey);
      console.log(`📡 Removed inactive server: ${serverKey}`);
    });

    // Update server counts for remaining servers
    activeServers.forEach((server, serverKey) => {
      const aircraftSet = serverAircraft.get(serverKey);
      if (aircraftSet) {
        server.count = aircraftSet.size;
      }
    });

    // Update UI
    updateAircraftCount();
    updateServerCount();
    updateServersPanel();
    filterAircraftByServer();
    if (selectedTaskFilter === 'liveNow' || autoLiveTaskSelectionPending) {
      scheduleLiveTaskRefresh();
    }
  }
}

// Initialize connection
const buildTimestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
console.log('═════════════════════════════════════════════════════');
console.log('🚀 CondorWorldMap - PHASE 3: Turnpoint Detection');
console.log('   Branch: feature/phase-3-turnpoint-detection');
console.log('   Features: Start/Finish/TP Detection + Distance Calc');
console.log(`   Build: ${buildTimestamp} UTC`);
console.log('═════════════════════════════════════════════════════');
console.log(`📡 Connecting to ${WS_URL}`);
connect();

// Start timeout checker (runs every 2 seconds)
timeoutCheckInterval = setInterval(checkAircraftTimeouts, 2000);

// Fetch viewer count every minute
fetchViewerCount(); // Initial fetch
setInterval(fetchViewerCount, 60000); // Every 60 seconds

// Fetch tasks initially (with "Live Now" filter by default)
fetchTasks();

// Load task/replay from URL if present (after initial tasks are fetched)
setTimeout(() => {
  loadStateFromURL();
}, 500);

// Update task times every minute (to keep "X mins from now" current)
setInterval(() => {
  if (activeTasks.length > 0) {
    updateTasksPanel();
  }
}, 60000); // Every 60 seconds

// Stats panel toggle functionality
const statsPanel = document.querySelector('.stats-panel');
const statsHeader = document.getElementById('stats-header');

// Only toggle when clicking the header, not the dropdowns
statsHeader.addEventListener('click', (e) => {
  statsPanel.classList.toggle('expanded');
});

// Servers panel toggle functionality
const serversPanel = document.querySelector('.servers-panel');
const serversHeader = document.getElementById('servers-header');

serversHeader.addEventListener('click', (e) => {
  serversPanel.classList.toggle('expanded');
});

// Tasks panel toggle functionality
const tasksPanel = document.querySelector('.tasks-panel');
const tasksHeader = document.getElementById('tasks-header');

tasksHeader.addEventListener('click', (e) => {
  tasksPanel.classList.toggle('expanded');
});

// Task filter button handlers (old panel)
document.querySelectorAll('.task-filter-item').forEach(filterItem => {
  filterItem.addEventListener('click', (e) => {
    const filter = filterItem.dataset.filter;
    console.log('🎯 OLD PANEL Task filter clicked:', filter);
    handleTaskFilterChange(filter);
  });
});

// Click-away functionality for all panels
document.addEventListener('click', (e) => {
  // Close stats panel if clicking outside of it
  if (statsPanel.classList.contains('expanded') &&
    !statsPanel.contains(e.target)) {
    statsPanel.classList.remove('expanded');
  }

  // Close servers panel if clicking outside of it
  if (serversPanel.classList.contains('expanded') &&
    !serversPanel.contains(e.target)) {
    serversPanel.classList.remove('expanded');
  }

  // Close tasks panel if clicking outside of it
  if (tasksPanel.classList.contains('expanded') &&
    !tasksPanel.contains(e.target)) {
    tasksPanel.classList.remove('expanded');
  }
});

// Configuration change handlers (old panel)
const trailLengthSelect = document.getElementById('trail-length');
const infoLabelSelect = document.getElementById('info-label');
const colorsSelect = document.getElementById('colors');
const colorSensitivitySlider = document.getElementById('color-sensitivity');
const colorSensitivityRow = document.getElementById('color-sensitivity-row');
const unitsSelect = document.getElementById('units');
const trendWindowInput = document.getElementById('trend-window');

function applySensitivitySliderGradient() {
  // Sensitivity indicates intensity only, so keep a neutral intensity scale: white -> green.
  const gradient = 'linear-gradient(to right, rgba(255, 255, 255, 0.75) 0%, rgba(34, 197, 94, 0.85) 100%)';
  document.documentElement.style.setProperty('--sensitivity-slider-gradient', gradient);
}

function applyTrendColorScheme(schemeId, persist = true) {
  const resolved = resolveTrendColorSchemeId(schemeId);
  config.trendColorScheme = resolved;
  aircraftManager.setTrendColorScheme(resolved);
  // Force 2D redraw with current color mode so markers/trails immediately adopt the new palette.
  aircraftManager.setColorScheme(config.colors);
  mapManager.updateConfig({ trendColorScheme: resolved });
  applySensitivitySliderGradient();
  if (persist) {
    saveSettings();
  }
}

// Dev/operator helper: quickly swap palette from console or scripts
window.setTrendColorScheme = (schemeId) => {
  applyTrendColorScheme(schemeId, true);
  return config.trendColorScheme;
};

// Update color sensitivity row visibility based on color scheme
function updateColorSensitivityVisibility() {
  const isAircraftScheme = config.colors === 'aircraft';
  applySensitivitySliderGradient();
  // Old right panel
  if (colorSensitivityRow) {
    colorSensitivityRow.classList.toggle('hidden', isAircraftScheme);
  }
  // New left panel
  const sensitivitySlider = document.getElementById('dropdown-color-sensitivity');
  if (sensitivitySlider) {
    const rangeItem = sensitivitySlider.closest('.dropdown-item');
    if (rangeItem) {
      rangeItem.style.display = isAircraftScheme ? 'none' : 'flex';
    }
  }
}
const etfHelpInline = document.getElementById('etf-help-inline');
const etfHelpRow = document.getElementById('etf-help-row');

// Ensure UI controls reflect persisted config on initial load
syncDropdowns();
applySensitivitySliderGradient();
updateColorSensitivityVisibility();
setTimeout(updateEtfHelpText, 0);

if (trailLengthSelect) {
  trailLengthSelect.addEventListener('change', (e) => {
    config.trailLength = parseInt(e.target.value);
    aircraftManager.setMaxTrailPoints(config.trailLength);
    mapManager.updateConfig({ trailLength: config.trailLength });
    console.log(`Trail length changed to: ${config.trailLength}`);
    syncDropdowns();
    saveSettings();
  });
}

if (infoLabelSelect) {
  infoLabelSelect.addEventListener('change', (e) => {
    config.infoLabel = e.target.value;
    aircraftManager.setInfoLabel(config.infoLabel);
    mapManager.updateConfig({ infoLabel: config.infoLabel });
    console.log(`Info label changed to: ${config.infoLabel}`);
    updateEtfHelpText();
    syncDropdowns();
    setTimeout(updateEtfHelpText, 0);
  });
}

if (colorsSelect) {
  colorsSelect.addEventListener('change', (e) => {
    config.colors = e.target.value;
    aircraftManager.setColorScheme(config.colors);
    mapManager.updateConfig({ colorScheme: config.colors });
    console.log(`Colors changed to: ${config.colors}`);
    updateEtfHelpText();
    updateColorSensitivityVisibility();
    syncDropdowns();
    setTimeout(updateEtfHelpText, 0);
  });
}

// Color sensitivity slider (left = less sensitive, right = more sensitive)
if (colorSensitivitySlider) {
  colorSensitivitySlider.value = config.colorSensitivity;
  colorSensitivitySlider.addEventListener('input', (e) => {
    config.colorSensitivity = parseInt(e.target.value, 10);
    aircraftManager.setColorSensitivity(config.colorSensitivity);
    mapManager.updateConfig({ colorSensitivity: config.colorSensitivity });
    console.log(`Color sensitivity changed to: ${config.colorSensitivity}`);
  });
  colorSensitivitySlider.addEventListener('change', () => {
    saveSettings();
  });
}

// Replay Refresh Rate slider (0.1Hz – 20Hz)
const replayRefreshSlider = document.getElementById('replay-refresh-rate');
const replayRefreshLabel = document.getElementById('replay-refresh-label');

function updateReplayRefreshLabel(hz) {
  if (replayRefreshLabel) {
    replayRefreshLabel.textContent = hz >= 1 ? `${Math.round(hz)} Hz` : `${hz.toFixed(1)} Hz`;
  }
}

if (replayRefreshSlider) {
  // Slider value 1-200, divide by 10 => 0.1Hz - 20Hz
  replayRefreshSlider.value = Math.round(config.replayRefreshHz * 10);
  updateReplayRefreshLabel(config.replayRefreshHz);

  replayRefreshSlider.addEventListener('input', (e) => {
    const hz = parseInt(e.target.value) / 10;
    config.replayRefreshHz = hz;
    updateReplayRefreshLabel(hz);
  });
  replayRefreshSlider.addEventListener('change', () => {
    saveSettings();
  });
}

// Initial visibility update for color sensitivity row
updateColorSensitivityVisibility();

if (unitsSelect) {
  unitsSelect.addEventListener('change', (e) => {
    config.units = e.target.value;
    aircraftManager.setUnits(config.units);
    mapManager.updateConfig({ units: config.units });
    console.log(`Units changed to: ${config.units}`);
    syncDropdowns();
  });
}

if (trendWindowInput) {
  trendWindowInput.value = config.trendWindowSec;
  trendWindowInput.addEventListener('change', (e) => {
    const rawValue = parseInt(e.target.value, 10);
    const clamped = Math.max(30, Math.min(600, Number.isFinite(rawValue) ? rawValue : config.trendWindowSec));
    config.trendWindowSec = clamped;
    e.target.value = clamped;
    console.log(`XC Pace window A set to: ${config.trendWindowSec}s`);
    saveSettings();
    updateEtfHelpText();
    updateDistanceStatsWidget();
  });
}

// MacCready value input
const maccreadyInput = document.getElementById('maccready-value');
if (maccreadyInput) {
  maccreadyInput.value = config.maccready;
  maccreadyInput.addEventListener('change', (e) => {
    const newValue = parseFloat(e.target.value);
    if (Number.isFinite(newValue)) {
      config.maccready = newValue;
      macCreadyManualOverride = true;
      console.log(`MacCready changed to: ${config.maccready} m/s`);
      saveSettings();
      notifyEtfInputChange();
    }
  });
}

// Netto value input
const nettoInput = document.getElementById('netto-value');
if (nettoInput) {
  nettoInput.value = config.netto;
  nettoInput.addEventListener('change', (e) => {
    config.netto = parseFloat(e.target.value);
    console.log(`Netto changed to: ${config.netto} m/s`);
    saveSettings();
    notifyEtfInputChange();
  });
}

// Wing loading input (numeric, pre-populated from task)
const wingLoadingInput = document.getElementById('wing-loading-value');
if (wingLoadingInput) {
  // Set initial value from config or task data
  wingLoadingInput.value = config.taskMaxWingLoading || config.wingLoading;

  wingLoadingInput.addEventListener('change', (e) => {
    config.wingLoading = parseFloat(e.target.value);
    saveSettings();
    notifyEtfInputChange();
  });
}

/**
 * Update wing loading input state based on task class
 * Disable for Club class (uses fixed default values)
 */
function updateWingLoadingInputState() {
  if (!wingLoadingInput) {
    return;
  }

  let taskData = null;
  if (typeof selectedTask === 'object' && selectedTask !== null) {
    taskData = selectedTask;
  } else if (selectedTask !== null) {
    taskData =
      taskManager.getTask(selectedTask) ||
      activeTasks.find(t => t.id == selectedTask) ||
      null;
  }

  const taskClass = taskData?.gliderClass || taskData?.task_additional_details?.Class || null;
  const isClubClass = typeof taskClass === 'string' && taskClass.toLowerCase().includes('club');

  if (isClubClass) {
    wingLoadingInput.disabled = true;
    wingLoadingInput.style.opacity = '0.5';
    wingLoadingInput.style.cursor = 'not-allowed';
    wingLoadingInput.title = 'Using Club-class default wing loading per glider.';
  } else {
    wingLoadingInput.disabled = false;
    wingLoadingInput.style.opacity = '1';
    wingLoadingInput.style.cursor = 'text';
    wingLoadingInput.title = '';
  }
}

// ========================================
// POLAR TESTER
// ========================================

// Populate glider list with ALL available gliders (not just loaded ones)
const polarGliderSelect = document.getElementById('polar-glider-select');
const allGliders = [
  'Antares18S', 'Antares18S-FXB', 'Arcus', 'Arcus-FXB', 'AS33Me-15', 'ASG29-18', 'ASG29-18-FXB',
  'ASG29Es-18', 'ASG32Mi', 'ASG32Mi-FXB', 'ASK13', 'ASK13-FXB', 'ASK21', 'ASK21-FXB', 'ASW15',
  'ASW15-FXB', 'ASW19', 'ASW19-FXB', 'ASW20', 'ASW20-FXB', 'ASW28-15', 'Blanik', 'Blanik-FXB',
  'DG1000S', 'DG1000S-FXB', 'DG101G', 'DG101G-FXB', 'DG808C-15', 'DG808C-18', 'Diana2', 'Diana2-FXB',
  'Discus2a', 'DuoDiscus', 'DuoDiscusFES', 'DuoDiscusFES-FXB', 'DuoDiscus-FXB', 'EB29R', 'Genesis2',
  'GrunauBaby', 'GrunauBaby-FXB', 'JS1-18', 'JS1-18-FXB', 'JS1-21', 'JS1-21-FXB', 'JS3-15', 'JS3-15-FXB',
  'JS3-18', 'JS3-18-FXB', 'K8', 'K8-FXB', 'Ka6CR', 'Ka6CR-FXB', 'Libelle', 'Libelle-FXB', 'LS3a',
  'LS4a', 'LS4a-FXB', 'LS8neo', 'MiniLAKFES', 'Pegase', 'Pegase-FXB', 'PilatusB4', 'PilatusB4-FXB',
  'SF28', 'SF28-FXB', 'SG38', 'SG38-FXB', 'SGS1-26', 'SGS1-26-FXB', 'StdCirrus', 'StdCirrus-FXB',
  'StemmeS12', 'StemmeS12-FXB', 'SwiftS1', 'SwiftS1-FXB', 'Ventus3-15', 'Ventus3-18', 'Ventus3-18-FXB'
];

if (polarGliderSelect) {
  allGliders.forEach(glider => {
    const option = document.createElement('option');
    option.value = glider;
    option.textContent = glider;
    polarGliderSelect.appendChild(option);
  });
}

// Function to update polar display
async function updatePolarDisplay() {
  const glider = document.getElementById('polar-glider-select')?.value;
  const wingLoading = parseFloat(document.getElementById('polar-wing-loading')?.value || 45);
  const mc = parseFloat(document.getElementById('polar-maccready')?.value || 0);
  const netto = parseFloat(document.getElementById('polar-netto')?.value || 0);
  const display = document.getElementById('polar-data-display');

  if (!glider || !display) {
    if (display) display.innerHTML = '<div style="color: rgba(255,255,255,0.5); font-style: italic;">Select a glider to see polar data</div>';
    return;
  }

  // Load polar if not already loaded (lazy loading)
  if (!globalPolarManager.hasPolar(glider)) {
    display.innerHTML = '<div style="color: rgba(255,255,255,0.7);">Loading polar data...</div>';
    try {
      await globalPolarManager.loadPolarOnDemand(glider);
    } catch (error) {
      display.innerHTML = `<div style="color: #ef4444;">Error loading polar for ${glider}: ${error.message}</div>`;
      return;
    }
  }

  // Get polar coefficients with wing loading adjustment
  const coeffs = globalPolarManager.getPolarCoefficients(glider, wingLoading);
  if (!coeffs) {
    display.innerHTML = `<div style="color: #ef4444;">Failed to get polar coefficients for ${glider}</div>`;
    return;
  }
  let [a, b, c] = coeffs;

  // Apply netto to coefficients (shifts sink rates)
  c = c + netto;

  // Calculate best L/D (minimum sink)
  const minSinkSpeed_ms = -b / (2 * a);
  const minSinkSpeed_kmh = minSinkSpeed_ms * 3.6;
  const minSink_ms = a * minSinkSpeed_ms * minSinkSpeed_ms + b * minSinkSpeed_ms + c;

  // Calculate MacCready speed if MC > 0
  let mcSpeed_kmh = minSinkSpeed_kmh;
  let mcSink_ms = minSink_ms;
  let mcLD = minSinkSpeed_kmh / Math.abs(minSink_ms) / 3.6;
  let mcXC_kmh = (mc > 0) ? (mcSpeed_kmh / (1 + (Math.max(0, -mcSink_ms) / mc))) : null;

  if (mc > 0 && a !== 0 && a < 0) {
    const term = (c - mc) / a;
    if (term > 0) {
      const v_mc_ms = Math.sqrt(term);
      mcSpeed_kmh = v_mc_ms * 3.6;
      mcSink_ms = a * v_mc_ms * v_mc_ms + b * v_mc_ms + c;
      mcLD = mcSpeed_kmh / Math.abs(mcSink_ms) / 3.6;
      mcXC_kmh = (mc > 0) ? (mcSpeed_kmh / (1 + (Math.max(0, -mcSink_ms) / mc))) : null;
    }
  }

  // Calculate speeds at various points
  const speeds = [80, 100, 120, 140, 160, 180, 200];
  const speedData = speeds.map(speed_kmh => {
    const speed_ms = speed_kmh / 3.6;
    const sink_ms = a * speed_ms * speed_ms + b * speed_ms + c;
    const ld = speed_kmh / Math.abs(sink_ms) / 3.6;
    // Cross-country (cycle) speed with MC: v / (1 + sink_pos/MC)
    const sink_pos = Math.max(0, -sink_ms);
    const xcSpeed = mc > 0 ? (speed_kmh / (1 + (sink_pos / mc))) : null;
    return {
      speed: speed_kmh,
      sink: sink_ms.toFixed(2),
      ld: ld.toFixed(1),
      xcSpeed: (mc > 0 ? xcSpeed.toFixed(1) : "--")
    };
  });

  const html = `
    <div style="background: rgba(0,0,0,0.3); padding: 8px; border-radius: 4px; margin-bottom: 8px;">
      <div style="color: #3b82f6; font-weight: 600; margin-bottom: 4px;">Polar Coefficients</div>
      <div style="color: rgba(255,255,255,0.7);">
        a: ${a.toFixed(6)}<br>
        b: ${b.toFixed(6)}<br>
        c: ${c.toFixed(6)}
      </div>
    </div>

    <div style="background: rgba(0,0,0,0.3); padding: 8px; border-radius: 4px; margin-bottom: 8px;">
      <div style="color: #3b82f6; font-weight: 600; margin-bottom: 4px;">Min Sink</div>
      <div style="color: rgba(255,255,255,0.7);">
        Speed: ${minSinkSpeed_kmh.toFixed(1)} km/h<br>
        Sink: ${minSink_ms.toFixed(2)} m/s<br>
        L/D: ${(minSinkSpeed_kmh / Math.abs(minSink_ms) / 3.6).toFixed(1)}
      </div>
    </div>

    ${mc > 0 ? `
    <div style="background: rgba(59,130,246,0.2); padding: 8px; border-radius: 4px; margin-bottom: 8px;">
      <div style="color: #3b82f6; font-weight: 600; margin-bottom: 4px;">MacCready Speed</div>
      <div style="color: rgba(255,255,255,0.7);">
        Speed: ${mcSpeed_kmh.toFixed(1)} km/h<br>
        Sink: ${mcSink_ms.toFixed(2)} m/s<br>
        L/D: ${mcLD.toFixed(1)}<br>\n        XC (cycle): ${mcXC_kmh !== null ? mcXC_kmh.toFixed(1) : "--"} km/h
      </div>
    </div>
    ` : ''}

    <div style="background: rgba(0,0,0,0.3); padding: 8px; border-radius: 4px;">
      <div style="color: #3b82f6; font-weight: 600; margin-bottom: 4px;">Speed Polar</div>
      <table style="width: 100%; font-size: 10px; color: rgba(255,255,255,0.7);">
        <thead>
          <tr style="color: #94a3b8;">
            <th style="text-align: left;">Speed</th>
            <th style="text-align: right;">Sink</th>
            <th style="text-align: right;">L/D</th>
            <th style="text-align: right;">XC (cycle)</th>
          </tr>
        </thead>
        <tbody>
          ${speedData.map(d => `
            <tr>
              <td>${d.speed} km/h</td>
              <td style="text-align: right;">${d.sink} m/s</td>
              <td style="text-align: right;">${d.ld}</td>
              <td style="text-align: right;">${d.xcSpeed} km/h</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  `;

  display.innerHTML = html;
}

// Polar tester event listeners
if (polarGliderSelect) {
  polarGliderSelect.addEventListener('change', updatePolarDisplay);
}

const polarWingLoading = document.getElementById('polar-wing-loading');
if (polarWingLoading) {
  polarWingLoading.addEventListener('input', updatePolarDisplay);
}

const polarMC = document.getElementById('polar-maccready');
if (polarMC) {
  polarMC.addEventListener('input', updatePolarDisplay);
}

const polarNetto = document.getElementById('polar-netto');
if (polarNetto) {
  polarNetto.addEventListener('input', updatePolarDisplay);
}

// ========================================
// END POLAR TESTER
// ========================================

// Function to show/hide CN Label Size dropdown based on 2D/3D mode
function updateCNLabelSizeVisibility() {
  const cnLabelSizeItem = document.getElementById('dropdown-cn-label-size');
  if (cnLabelSizeItem) {
    const dropdownItem = cnLabelSizeItem.closest('.dropdown-item');
    if (dropdownItem) {
      if (mapManager.getMode() === '3d') {
        dropdownItem.style.display = 'flex';
      } else {
        dropdownItem.style.display = 'none';
      }
    }
  }
}

function getOverlayColumnLabel(key) {
  const option = OVERLAY_COLUMN_OPTIONS.find(col => col.key === key);
  return option?.label || key;
}

function normalizeOverlayColumns() {
  const permanent = sanitizeOverlayColumnListAllowEmpty(config.overlayPermanentColumns);
  const rotating = sanitizeOverlayColumnListAllowEmpty(config.overlayRotatingColumns)
    .filter(key => !permanent.includes(key));

  config.overlayPermanentColumns = permanent;
  config.overlayRotatingColumns = rotating;
}

function setSelectOptions(selectElement, keys) {
  if (!selectElement) return;
  selectElement.innerHTML = keys
    .map(key => `<option value="${key}">${getOverlayColumnLabel(key)}</option>`)
    .join('');
}

function getSelectedValues(selectElement) {
  if (!selectElement) return [];
  return Array.from(selectElement.selectedOptions).map(opt => opt.value);
}

function setupOverlayLeaderboardControls() {
  const toggleBtn = document.getElementById('show-leaderboard-settings-btn');
  const settingsPanel = document.getElementById('leaderboard-settings-container');
  const windowBInput = document.getElementById('dropdown-trend-window-b');
  const xcAUpdateInput = document.getElementById('dropdown-overlay-xc-a-update-sec');
  const xcBUpdateInput = document.getElementById('dropdown-overlay-xc-b-update-sec');
  const positionWindowInput = document.getElementById('dropdown-overlay-position-window-sec');
  const positionRefreshInput = document.getElementById('dropdown-overlay-position-refresh-sec');
  const rotationInput = document.getElementById('dropdown-overlay-rotation-sec');
  const availableSelect = document.getElementById('overlay-available-columns');
  const permanentSelect = document.getElementById('overlay-permanent-columns');
  const rotatingSelect = document.getElementById('overlay-rotating-columns');

  if (!settingsPanel) {
    return;
  }

  normalizeOverlayColumns();

  if (windowBInput) {
    windowBInput.value = String(config.trendWindowBSec);
    windowBInput.addEventListener('change', (e) => {
      const rawValue = Number.parseInt(e.target.value, 10);
      const clamped = Math.max(30, Math.min(3600, Number.isFinite(rawValue) ? rawValue : config.trendWindowBSec));
      config.trendWindowBSec = clamped;
      e.target.value = clamped;
      syncDropdowns();
      saveSettings();
      updateDistanceStatsWidget();
      updateEtfHelpText();
    });
  }

  if (xcAUpdateInput) {
    xcAUpdateInput.value = String(config.overlayXcPerfAUpdateSec);
    xcAUpdateInput.addEventListener('change', (e) => {
      const rawValue = Number.parseInt(e.target.value, 10);
      const clamped = Math.max(1, Math.min(600, Number.isFinite(rawValue) ? rawValue : config.overlayXcPerfAUpdateSec));
      config.overlayXcPerfAUpdateSec = clamped;
      e.target.value = clamped;
      syncDropdowns();
      saveSettings();
      updateDistanceStatsWidget();
    });
  }

  if (xcBUpdateInput) {
    xcBUpdateInput.value = String(config.overlayXcPerfBUpdateSec);
    xcBUpdateInput.addEventListener('change', (e) => {
      const rawValue = Number.parseInt(e.target.value, 10);
      const clamped = Math.max(1, Math.min(600, Number.isFinite(rawValue) ? rawValue : config.overlayXcPerfBUpdateSec));
      config.overlayXcPerfBUpdateSec = clamped;
      e.target.value = clamped;
      syncDropdowns();
      saveSettings();
      updateDistanceStatsWidget();
    });
  }

  if (positionWindowInput) {
    positionWindowInput.value = String(config.overlayPositionWindowSec);
    positionWindowInput.addEventListener('change', (e) => {
      const rawValue = Number.parseInt(e.target.value, 10);
      const clamped = Math.max(30, Math.min(3600, Number.isFinite(rawValue) ? rawValue : config.overlayPositionWindowSec));
      config.overlayPositionWindowSec = clamped;
      e.target.value = clamped;
      syncDropdowns();
      saveSettings();
      updateDistanceStatsWidget();
    });
  }

  if (positionRefreshInput) {
    positionRefreshInput.value = String(config.overlayPositionRefreshSec);
    positionRefreshInput.addEventListener('change', (e) => {
      const rawValue = Number.parseInt(e.target.value, 10);
      const clamped = Math.max(1, Math.min(600, Number.isFinite(rawValue) ? rawValue : config.overlayPositionRefreshSec));
      config.overlayPositionRefreshSec = clamped;
      e.target.value = clamped;
      syncDropdowns();
      saveSettings();
      updateDistanceStatsWidget();
    });
  }

  if (rotationInput) {
    rotationInput.value = String(config.overlayRotationSec);
    rotationInput.addEventListener('change', (e) => {
      const rawValue = Number.parseInt(e.target.value, 10);
      const clamped = Math.max(1, Math.min(120, Number.isFinite(rawValue) ? rawValue : config.overlayRotationSec));
      config.overlayRotationSec = clamped;
      e.target.value = clamped;
      saveSettings();
      updateDistanceStatsWidget();
    });
  }

  const renderOverlayColumnLists = () => {
    normalizeOverlayColumns();
    setSelectOptions(permanentSelect, config.overlayPermanentColumns);
    setSelectOptions(rotatingSelect, config.overlayRotatingColumns);

    const usedKeys = new Set([...config.overlayPermanentColumns, ...config.overlayRotatingColumns]);
    const availableKeys = OVERLAY_COLUMN_OPTIONS
      .map(col => col.key)
      .filter(key => !usedKeys.has(key));
    setSelectOptions(availableSelect, availableKeys);
  };

  const commitOverlayColumnSettings = () => {
    normalizeOverlayColumns();
    saveSettings();
    updateDistanceStatsWidget();
  };

  const moveColumn = (listKey, selectElement, direction) => {
    if (!selectElement) return;
    const selectedValue = selectElement.value;
    if (!selectedValue) return;

    const list = listKey === 'permanent' ? config.overlayPermanentColumns : config.overlayRotatingColumns;
    const index = list.indexOf(selectedValue);
    if (index < 0) return;

    const nextIndex = direction === 'up' ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= list.length) return;

    [list[index], list[nextIndex]] = [list[nextIndex], list[index]];
    renderOverlayColumnLists();
    if (selectElement) {
      selectElement.value = selectedValue;
    }
    commitOverlayColumnSettings();
  };

  const removeColumns = (listKey, selectElement) => {
    const selectedValues = new Set(getSelectedValues(selectElement));
    if (selectedValues.size === 0) return;

    if (listKey === 'permanent') {
      config.overlayPermanentColumns = config.overlayPermanentColumns.filter(key => !selectedValues.has(key));
    } else {
      config.overlayRotatingColumns = config.overlayRotatingColumns.filter(key => !selectedValues.has(key));
    }

    renderOverlayColumnLists();
    commitOverlayColumnSettings();
  };

  const addColumn = (targetListKey) => {
    const key = availableSelect?.value;
    if (!key) return;

    if (targetListKey === 'permanent') {
      if (!config.overlayPermanentColumns.includes(key)) {
        config.overlayPermanentColumns.push(key);
      }
      config.overlayRotatingColumns = config.overlayRotatingColumns.filter(existing => existing !== key);
    } else {
      if (!config.overlayRotatingColumns.includes(key) && !config.overlayPermanentColumns.includes(key)) {
        config.overlayRotatingColumns.push(key);
      }
    }

    renderOverlayColumnLists();
    commitOverlayColumnSettings();
  };

  document.getElementById('overlay-add-permanent-btn')?.addEventListener('click', () => addColumn('permanent'));
  document.getElementById('overlay-add-rotating-btn')?.addEventListener('click', () => addColumn('rotating'));
  document.getElementById('overlay-permanent-up-btn')?.addEventListener('click', () => moveColumn('permanent', permanentSelect, 'up'));
  document.getElementById('overlay-permanent-down-btn')?.addEventListener('click', () => moveColumn('permanent', permanentSelect, 'down'));
  document.getElementById('overlay-rotating-up-btn')?.addEventListener('click', () => moveColumn('rotating', rotatingSelect, 'up'));
  document.getElementById('overlay-rotating-down-btn')?.addEventListener('click', () => moveColumn('rotating', rotatingSelect, 'down'));
  document.getElementById('overlay-remove-permanent-btn')?.addEventListener('click', () => removeColumns('permanent', permanentSelect));
  document.getElementById('overlay-remove-rotating-btn')?.addEventListener('click', () => removeColumns('rotating', rotatingSelect));

  if (toggleBtn) {
    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const isOpen = settingsPanel.style.display !== 'none';
      settingsPanel.style.display = isOpen ? 'none' : 'block';
      if (!isOpen) {
        setTimeout(() => settingsPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
      }
    });
  }

  renderOverlayColumnLists();
}

// 3D View Toggle Button
const viewToggleBtn = document.getElementById('view-toggle-btn');
if (viewToggleBtn) {
  const handleViewToggle = async () => {
    console.log('🌍 Toggling 3D view...');
    try {
      await mapManager.toggleView();
      // Keep styling consistent; set label to target mode
      const labelEl = viewToggleBtn.querySelector('.view-text');
      if (labelEl) labelEl.textContent = (mapManager.getMode() === '3d') ? '2D' : '3D';

      // Update CN Label Size visibility
      updateCNLabelSizeVisibility();

      // If switched to 3D and a task is selected, sync task overlay
      if (mapManager.getMode() === '3d') {
        if (selectedTask !== null) {
          const task = activeTasks.find(t => t.id === selectedTask);
          if (task) {
            mapManager.syncTask(task);
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to toggle view:', error);
      alert('Failed to initialize 3D view. Please refresh the page and try again.');
    }
  };

  // Click handler (desktop and mobile fallback)
  viewToggleBtn.addEventListener('click', handleViewToggle);
  // Touch handler (mobile) to ensure reliable taps
  viewToggleBtn.addEventListener('touchend', (e) => {
    try { e.preventDefault(); e.stopPropagation(); } catch (_) { }
    handleViewToggle();
  }, { passive: false });
}

// Link toggle state
let isLinkActive = true;

// Configuration change handlers (left panel) - set up after panel is initialized
setTimeout(() => {
  const leftTrailLength = document.getElementById('dropdown-trail-length');
  const leftInfoLabel = document.getElementById('dropdown-info-label');
  const leftColors = document.getElementById('dropdown-colors');
  const leftUnits = document.getElementById('dropdown-units');
  const leftTrendWindow = document.getElementById('dropdown-trend-window');
  const linkToggleBtn = document.getElementById('link-toggle-btn');

  setupOverlayLeaderboardControls();

  // Link toggle button handler
  if (linkToggleBtn) {
    linkToggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      isLinkActive = !isLinkActive;
      linkToggleBtn.classList.toggle('active', isLinkActive);
      console.log(`Link toggle: ${isLinkActive ? 'ON' : 'OFF'}`);
    });
  }

  if (leftTrailLength) {
    leftTrailLength.addEventListener('change', (e) => {
      config.trailLength = parseInt(e.target.value);
      aircraftManager.setMaxTrailPoints(config.trailLength);
      mapManager.updateConfig({ trailLength: config.trailLength });
      console.log(`Trail length changed to: ${config.trailLength}`);
      syncDropdowns();
      saveSettings();
    });
  }

  if (leftInfoLabel) {
    leftInfoLabel.addEventListener('change', (e) => {
      config.infoLabel = e.target.value;
      aircraftManager.setInfoLabel(config.infoLabel);
      mapManager.updateConfig({ infoLabel: config.infoLabel });
      console.log(`Info label changed to: ${config.infoLabel}`);

      // Show help when XC Pace selected
      updateEtfHelpText();

      // If link is active, sync colors
      if (isLinkActive) {
        config.colors = getLinkedColorSchemeForInfoLabel(e.target.value);
        aircraftManager.setColorScheme(config.colors);
        mapManager.updateConfig({ colorScheme: config.colors });
        console.log(`Colors auto-synced to: ${config.colors}`);
      }

      syncDropdowns();
      setTimeout(updateEtfHelpText, 0);
      saveSettings();
    });
  }

  if (leftColors) {
    leftColors.addEventListener('change', (e) => {
      config.colors = e.target.value;
      aircraftManager.setColorScheme(config.colors);
      mapManager.updateConfig({ colorScheme: config.colors });
      console.log(`Colors changed to: ${config.colors}`);
      updateEtfHelpText();

      // If link is active, sync info label
      if (isLinkActive) {
        config.infoLabel = getLinkedInfoLabelForColorScheme(e.target.value);
        aircraftManager.setInfoLabel(config.infoLabel);
        mapManager.updateConfig({ infoLabel: config.infoLabel });
        console.log(`Info label auto-synced to: ${config.infoLabel}`);
      }

      syncDropdowns();
      setTimeout(updateEtfHelpText, 0);
      saveSettings();
      updateColorSensitivityVisibility();
    });
  }

  // Color Sensitivity slider
  const leftColorSensitivity = document.getElementById('dropdown-color-sensitivity');
  if (leftColorSensitivity) {
    // Set initial value from config
    leftColorSensitivity.value = config.colorSensitivity || 0;

    leftColorSensitivity.addEventListener('input', (e) => {
      config.colorSensitivity = parseInt(e.target.value);
      aircraftManager.setColorSensitivity(config.colorSensitivity);
      mapManager.updateConfig({ colorSensitivity: config.colorSensitivity });
      console.log(`Color sensitivity changed to: ${config.colorSensitivity}`);
      saveSettings();
    });
  }

  // Trend Color Scheme selector (left panel)
  const leftTrendColorScheme = document.getElementById('dropdown-trend-color-scheme');
  if (leftTrendColorScheme) {
    leftTrendColorScheme.value = config.trendColorScheme;
    leftTrendColorScheme.addEventListener('change', (e) => {
      applyTrendColorScheme(e.target.value, true);
      console.log(`Trend color scheme changed to: ${config.trendColorScheme}`);
      syncDropdowns();
    });
  }

  // Replay Refresh Rate slider (left panel)
  const leftReplayRefresh = document.getElementById('dropdown-replay-refresh-rate');
  const leftReplayRefreshLabel = document.getElementById('dropdown-replay-refresh-rate-label');
  if (leftReplayRefresh) {
    leftReplayRefresh.value = Math.round(config.replayRefreshHz * 10);
    const updateLabel = (hz) => {
      if (leftReplayRefreshLabel) {
        leftReplayRefreshLabel.textContent = hz >= 1 ? `${Math.round(hz)} Hz` : `${hz.toFixed(1)} Hz`;
      }
    };
    updateLabel(config.replayRefreshHz);

    leftReplayRefresh.addEventListener('input', (e) => {
      const hz = parseInt(e.target.value) / 10;
      config.replayRefreshHz = hz;
      updateLabel(hz);
      // Propagate to aircraft manager for label throttling
      aircraftManager.labelRefreshMs = 1000 / Math.max(0.1, hz);
      syncDropdowns();
    });
    leftReplayRefresh.addEventListener('change', () => {
      saveSettings();
    });
  }

  // Initial visibility update for color sensitivity slider
  updateColorSensitivityVisibility();

  if (leftUnits) {
    leftUnits.addEventListener('change', (e) => {
      config.units = e.target.value;
      aircraftManager.setUnits(config.units);
      mapManager.updateConfig({ units: config.units });
      console.log(`Units changed to: ${config.units}`);
      syncDropdowns();
      saveSettings();
      updateEtfHelpText();
    });
  }

  if (leftTrendWindow) {
    leftTrendWindow.addEventListener('change', (e) => {
      const rawValue = parseInt(e.target.value, 10);
      const clamped = Math.max(30, Math.min(600, Number.isFinite(rawValue) ? rawValue : config.trendWindowSec));
      config.trendWindowSec = clamped;
      e.target.value = clamped;
      console.log(`XC Pace window A set to: ${config.trendWindowSec}s (left panel)`);
      syncDropdowns();
      saveSettings();
      updateEtfHelpText();
      updateDistanceStatsWidget();
    });
  }

  // 3D Label Size Control
  const cnLabelSize = document.getElementById('dropdown-cn-label-size');

  if (cnLabelSize) {
    cnLabelSize.addEventListener('change', (e) => {
      const size = parseInt(e.target.value);
      config.cnLabelSize = size;
      mapManager.setCNLabelSize(size);
      console.log(`CN Label Size changed to: ${size}px (3D only)`);
      saveSettings();
    });
  }

  // Hide CN Label Size initially (starts in 2D mode)
  updateCNLabelSizeVisibility();

  // TE tuning UI removed
}, 100);

// Sync dropdown values between old and left panels
function syncDropdowns() {
  // Sync trail length
  if (trailLengthSelect) trailLengthSelect.value = config.trailLength;
  const leftTrailLength = document.getElementById('dropdown-trail-length');
  if (leftTrailLength) leftTrailLength.value = config.trailLength;

  // Sync info label
  if (infoLabelSelect) infoLabelSelect.value = config.infoLabel;
  const leftInfoLabel = document.getElementById('dropdown-info-label');
  if (leftInfoLabel) {
    leftInfoLabel.value = config.infoLabel;
  }

  // Sync TE tuning dropdowns
  const syncTeGain = document.getElementById('dropdown-te-gain');
  if (syncTeGain) {
    // Ensure formatting matches options (e.g., '1.0')
    const val = (Math.round(config.teGain * 10) / 10).toFixed(1);
    syncTeGain.value = val;
  }
  const syncTeAccel = document.getElementById('dropdown-te-accel');
  if (syncTeAccel) {
    const val = (Math.round(config.teAccelLimit * 10) / 10).toFixed(1);
    syncTeAccel.value = val;
  }

  // Sync colors
  if (colorsSelect) colorsSelect.value = config.colors;
  const leftColors = document.getElementById('dropdown-colors');
  if (leftColors) {
    leftColors.value = config.colors;
    console.log(`🎨 Synced Colors dropdown to: ${config.colors}`);
  }

  // Sync units
  if (unitsSelect) unitsSelect.value = config.units;
  const leftUnits = document.getElementById('dropdown-units');
  if (leftUnits) leftUnits.value = config.units;
  // Sync trend window
  if (trendWindowInput) trendWindowInput.value = config.trendWindowSec;
  const leftTrendWindow = document.getElementById('dropdown-trend-window');
  if (leftTrendWindow) leftTrendWindow.value = config.trendWindowSec;
  const leftTrendWindowB = document.getElementById('dropdown-trend-window-b');
  if (leftTrendWindowB) leftTrendWindowB.value = config.trendWindowBSec;
  const xcAUpdateInput = document.getElementById('dropdown-overlay-xc-a-update-sec');
  if (xcAUpdateInput) xcAUpdateInput.value = String(config.overlayXcPerfAUpdateSec);
  const xcBUpdateInput = document.getElementById('dropdown-overlay-xc-b-update-sec');
  if (xcBUpdateInput) xcBUpdateInput.value = String(config.overlayXcPerfBUpdateSec);
  const positionWindowInput = document.getElementById('dropdown-overlay-position-window-sec');
  if (positionWindowInput) positionWindowInput.value = String(config.overlayPositionWindowSec);
  const positionRefreshInput = document.getElementById('dropdown-overlay-position-refresh-sec');
  if (positionRefreshInput) positionRefreshInput.value = String(config.overlayPositionRefreshSec);
  const overlayRotationInput = document.getElementById('dropdown-overlay-rotation-sec');
  if (overlayRotationInput) overlayRotationInput.value = config.overlayRotationSec;

  // Sync label size (3D only)
  const cnLabelSizeDropdown = document.getElementById('dropdown-cn-label-size');
  if (cnLabelSizeDropdown) cnLabelSizeDropdown.value = config.cnLabelSize;

  // Sync trend color scheme
  const leftTrendColorScheme = document.getElementById('dropdown-trend-color-scheme');
  if (leftTrendColorScheme) leftTrendColorScheme.value = config.trendColorScheme;

  // Sync color sensitivity slider
  const leftColorSensitivity = document.getElementById('dropdown-color-sensitivity');
  if (leftColorSensitivity) leftColorSensitivity.value = config.colorSensitivity;
  const oldColorSensitivity = document.getElementById('color-sensitivity');
  if (oldColorSensitivity) oldColorSensitivity.value = config.colorSensitivity;

  // Sync replay refresh rate slider
  const syncReplayRefresh = document.getElementById('dropdown-replay-refresh-rate');
  if (syncReplayRefresh) syncReplayRefresh.value = Math.round(config.replayRefreshHz * 10);
  const syncReplayRefreshOld = document.getElementById('replay-refresh-rate');
  if (syncReplayRefreshOld) syncReplayRefreshOld.value = Math.round(config.replayRefreshHz * 10);
}

// Handle browser back/forward buttons
window.addEventListener('popstate', () => {
  console.log('🔙 Browser navigation detected');
  loadStateFromURL();
});

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
      })
      .catch((error) => {
        console.log('❌ Service Worker registration failed:', error);
      });
  });
}
function updateEtfHelpText() {
  const usesWindowA = (config.infoLabel === 'etf_delta' || config.colors === 'etf_delta');
  const usesWindowB = (config.infoLabel === 'etf_delta_b' || config.colors === 'etf_delta_b');
  const usesDdh = config.infoLabel === 'ddh';
  const show = usesWindowA || usesWindowB || usesDdh;
  let message = '';

  if (usesDdh) {
    message = 'Estimated altitude above/below finish altitude (DDH) at current MC. Negative means climb still needed; positive means excess height at MC speed.';
  } else {
    const mc = Number.isFinite(config.maccready) ? config.maccready.toFixed(1) : '--';
    const netto = Number.isFinite(config.netto) ? config.netto.toFixed(1) : '--';
    const trendA = Number.isFinite(config.trendWindowSec) ? Math.round(config.trendWindowSec) : '--';
    const trendB = Number.isFinite(config.trendWindowBSec) ? formatWindowMinutesLabel(config.trendWindowBSec) : '--';
    const activeWindowText = usesWindowB
      ? `window B (${trendB})`
      : `window A (${trendA}s)`;
    message = `Seconds improved (-) or worsened (+) vs. predicted XC speed polar at ${mc} m/s MC and ${netto} m/s Netto over ${activeWindowText}.`;
  }

  if (etfHelpInline && etfHelpRow) {
    etfHelpRow.style.display = show ? 'flex' : 'none';
    etfHelpInline.style.display = show ? 'block' : 'none';
    if (show) {
      etfHelpInline.textContent = message;
      etfHelpInline.style.fontSize = '10px';
      etfHelpInline.style.color = 'rgba(255,255,255,0.7)';
      etfHelpInline.style.marginTop = '6px';
      etfHelpInline.style.lineHeight = '1.4';
      etfHelpInline.style.maxWidth = '340px';
      etfHelpInline.style.width = '100%';
      etfHelpInline.style.flexBasis = '100%';
      etfHelpInline.style.alignSelf = 'stretch';
      etfHelpInline.style.order = '10';
      etfHelpInline.style.padding = '6px 8px';
      etfHelpInline.style.background = 'rgba(255,255,255,0.04)';
      etfHelpInline.style.borderRadius = '6px';
    }
  }

  const leftHelp = document.getElementById('left-etf-help');
  if (leftHelp) {
    leftHelp.style.display = show ? 'block' : 'none';
    if (show) {
      leftHelp.textContent = message;
      leftHelp.style.fontSize = '10px';
      leftHelp.style.color = 'rgba(255,255,255,0.7)';
      leftHelp.style.marginTop = '6px';
      leftHelp.style.lineHeight = '1.4';
      leftHelp.style.maxWidth = '340px';
      leftHelp.style.width = '100%';
      leftHelp.style.flexBasis = '100%';
      leftHelp.style.alignSelf = 'stretch';
      leftHelp.style.order = '10';
      leftHelp.style.padding = '6px 8px';
      leftHelp.style.background = 'rgba(255,255,255,0.04)';
      leftHelp.style.borderRadius = '6px';
    }
  }
}
